import {
  ActionIcon, Alert, Anchor, Badge, Button, Container, Divider, Fieldset, Grid, Group,
  LoadingOverlay, Menu, Modal, NumberInput, Paper, SegmentedControl, Select, Stack, Table, Tabs, Text, TextInput,
  Title, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconAlertTriangle, IconArrowBackUp, IconArrowsExchange, IconBriefcase, IconBuildingBank,
  IconChartHistogram, IconChecklist, IconCircleX, IconClipboardList, IconEdit,
  IconFileText, IconIdBadge2, IconPlus, IconReceipt2, IconRotate, IconShieldCheck, IconTrash, IconUnlink,
  IconHistory, IconMail, IconUsers, IconWand, IconX,
} from '@tabler/icons-react';
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApiError } from '../api/client';
import { agreementsApi } from '../api/agreements';
import { personsApi } from '../api/persons';
import { useAlertModal } from '../components/AlertModalProvider';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import { PersonPicker } from '../components/EntityPicker';
import { usePerson } from '../hooks/usePersons';
import { AccountRef } from '../components/EntityRef';
import type {
  AccountInsurance, AgreementCreateInput, AgreementDocument, AgreementDocumentKind,
  AgreementPartyCreateInput, AgreementUpdateInput, ClientType, InsurancePolicyPartyRole,
  LifecycleStatus, ServiceType, UUID,
} from '../api/types';
import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { StickyDetailHeader } from '../components/StickyHeader';
import { EmailDraftModal } from '../components/EmailDraftModal';
import { canEmailDrafts } from '../api/emailDrafts';
import { useCurrentUser } from '../hooks/useAuth';
import { SummaryItem, SummaryBadge } from '../components/SummaryBox';
import { useAccount, useAccounts, accountsKeys } from '../hooks/useAccounts';
import { accountsApi } from '../api/accounts';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { RowSeverIcon } from '../components/RowSeverIcon';
import { AddAccountModal } from '../components/AddAccountModal';
import { AgreementDocumentsTab } from '../components/AgreementDocumentsTab';
import { AqSection, ESG_LABELS, type AqDraft } from '../components/AqSection';
import { useAqResponses, useCreateAqResponse, useSuitabilityConversion } from '../hooks/useAq';
import { allocationExceeds, aqApi } from '../api/aq';
import {
  useAccountInsuranceList, useAttachPolicy, useDetachPolicy,
  useLapsePolicy, useMaturePolicy, useMovePolicy, useRedeemPolicy,
} from '../hooks/useAccountInsurance';
import { useAgreementParties, apKeys } from '../hooks/useAgreementParties';
import { agreementPartiesApi } from '../api/agreementParties';
import { useInsurancePolicyPartiesList } from '../hooks/useInsurancePolicyParties';
import {
  useAgreement, useAgreementReferences, useCascadeDeleteAgreement, useCreateAgreement,
  useDeleteAgreement, useRestoreAgreement, useUpdateAgreement,
  useAgreementCascadeFootprint, useCascadeStatusApply,
  useAgreementReactivateFootprint, useReactivateApply,
} from '../hooks/useAgreements';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { CascadeDeleteModal } from '../components/CascadeDeleteModal';
import { CascadeStatusModal } from '../components/CascadeStatusModal';
import { AddInsurancePolicyModal } from '../components/AddInsurancePolicyModal';
import { DetachPolicyModal } from '../components/DetachPolicyModal';
import { TerminatePolicyModal } from '../components/TerminatePolicyModal';
import type { CloseReason, UserCloseReason } from '../api/types';
import { AGREEMENT_INACTIVE_REASON_LABELS, CLOSE_REASON_LABELS, PARTY_ROLE_LABELS } from '../api/types';
import { type PendingRMOp } from '../components/NamedRMSection';
import { FeesSection } from '../components/FeesSection';
import { BillingIdentitySection } from '../components/BillingIdentitySection';
import { FeeHistorySection } from '../components/FeeHistorySection';
import { AddAgreementPartyModal } from '../components/AddAgreementPartyModal';
import { SuggestMifirModal } from '../components/SuggestMifirModal';
import { useAddPartyModal, useNewPartyPersonResume } from '../components/PartyAddSteps';
import { usePendingOps } from '../hooks/usePendingOps';
import { documentResultParts, useDocumentStaging } from '../hooks/useDocuments';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { StatusExplained } from '../components/StatusExplained';
import { usePermissions } from '../hooks/usePermissions';
import { useAgreementUsers, agreementUserKeys } from '../hooks/useAgreementUsers';
import { agreementUsersApi } from '../api/agreementUsers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { changeRequestsApi } from '../api/changeRequests';
import { EntityFriendlyLabel } from '../components/EntityFriendlyLabel';
import { PersonRef } from '../components/EntityRef';
import { usePendingCreates } from '../hooks/usePendingCreates';
import {
  AgreementPortfoliosSection, portfolioOpKey, type PendingPortfolioOp,
} from '../components/AgreementPortfoliosSection';
import { TradesList } from '../components/TradesList';
import type { ViewRequest } from './PortfolioViewTab';
import { AdminOverrideSwitch, useAdminOverride } from '../hooks/useAdminOverride';
import { agreementPortfoliosApi } from '../api/agreementPortfolios';
import { changedFieldLabels, fieldSuffix, labelizeField } from '../utils/toastFields';
import { diffUpdatePayload, toInt } from '../utils/diffPayload';
import { pepLabel, usPersonLabel } from '../utils/pepLabels';
import { overlapConfirmProps, pendingFieldOverlap } from '../utils/pendingOverlap';
import {
  footprintToPlan, isCascadeTriggering,
  type LifecycleStatus as CascadeLifecycleStatus,
} from '../api/agreements';
import { IncompleteBanner } from '../components/IncompleteBanner';
import { FIELD_TABS, TabMissingCount, useMissingFields } from '../components/MissingFields';
import { AgreementInteractionsTab } from '../components/AgreementInteractionsTab';
import { AgreementTasksTab } from '../components/AgreementTasksTab';
import {
  useAgreementDocumentsByAgreement,
  adKeys,
} from '../hooks/useAgreementDocuments';
import { agreementDocumentsApi } from '../api/agreementDocuments';
import { agreementsKeys } from '../hooks/useAgreements';

// Lazy: the "AuM & Return" tab pulls the Vega chart family (~840KB via
// components/charts.tsx); loading it lazily keeps Vega off the main agreement
// view — it arrives only when the tab is opened (C-260).
const AgreementAumReturnTab = React.lazy(() =>
  import('../components/AgreementAumReturnTab').then((m) => ({ default: m.AgreementAumReturnTab })),
);

const SERVICE_OPTS  = [{ value: 'CIIF', label: 'CIIF' }, { value: 'CEC', label: 'CEC' }];
const CLIENT_OPTS   = [{ value: 'Particular', label: 'Particular' }, { value: 'Empresa', label: 'Empresa' }];
// Team feedback P15 (Pedro 2026-10-09): how the client came to Baluarte.
const ORIGIN_OPTS = ['RM', 'Referred by', 'Website', 'Media', 'Other'].map((v) => ({ value: v, label: v }));
// #273 (2026-06-08): rename from legacy Activo/Inactivo to shared
// LifecycleStatus enum. Note: the cascade modal handles Active→Closed
// transitions; only the binary Active↔Inactive subset is exposed in
// this dropdown until the Wave A UX (#274) adds Closed affordances.
const STATUS_OPTS   = [{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }];
const NATURE_OPTS   = [{ value: '', label: '-' }, { value: 'NP', label: 'NP - Nao Profissional' }, { value: 'P', label: 'P - Profissional' }, { value: 'CE', label: 'CE - Contraparte Elegivel' }];
const RESIDENCE_OPTS= [{ value: '', label: '-' }, { value: 'R', label: 'Residente' }, { value: 'NR', label: 'Nao Residente' }];
// #52 (migration 0037): allocation solutions below SAA00 added (MM,
// MM+, STB). Labels match AqSection's ALLOC_LABELS (smoke 2026-06-13).
const SAA_OPTS      = [
  { value: '', label: '-' },
  { value: 'CASH', label: 'MM' }, { value: 'CASH_PLUS', label: 'MM+' }, { value: 'STB', label: 'STB' },
  ...['SAA00','SAA10','SAA20','SAA30','SAA40','SAA50','SAA60','SAA70','SAA80','SAA90','SAA100']
    .map(v => ({ value: v, label: v })),
];
const AML_OPTS      = [{ value: '', label: '-' }, { value: 'Normal', label: 'Normal' }, { value: 'Elevado', label: 'Elevado' }];
// #50 stage 2 (migration 0036): NOT NULL with default PT — no empty option.
const LANG_OPTS     = [{ value: 'PT', label: 'PT — Português' }, { value: 'EN', label: 'EN — English' }];
// #52: discrete suitability scores (-10, 0, 10 … 100). AQ-populated,
// adviser-editable. Legacy non-step values are appended at render time
// so the Select can still display them.
const SCORE_OPTS    = ['', '-10', '0', '10', '20', '30', '40', '50', '60', '70', '80', '90', '100'];
// #52: ESG axis picker (9 values + clear).
const ESG_OPTS      = [{ value: '', label: '-' },
  ...(['ESG_0','ESG_8_0_10','ESG_8_10_25','ESG_8_25_50','ESG_8_50_100',
       'ESG_9_0_10','ESG_9_10_25','ESG_9_25_50','ESG_9_50_100'] as const)
    .map(v => ({ value: v, label: ESG_LABELS[v] }))];

interface FormValues {
  service_type: ServiceType;
  client_number: number | string;
  client_mnemonic: string;
  client_type: ClientType;
  status: LifecycleStatus;
  // KYC tab — investor identity + agreement-level review umbrella
  mifid_investor_nature: string;
  mifid_residence: string;
  preferred_language: string;                    // #50: document language default (PT/EN)
  preferred_ref_currency: string;                // MR-C (0126): Portfolios tab preset + MR portfolio pages
  mifir_id: string;
  mifir_id_type: string;
  kyc_last_date: string;
  kyc_next_date: string;                         // user-editable; backend clamps
  // MiFID tab — suitability only
  mifid_suitability_score: string;
  mifid_suitability_last_date: string;
  mifid_suitability_next_date: string;          // user-editable; backend clamps if > rule ceiling
  strategic_allocation: string;
  esg_score: string;                             // #52: ESG axis (AQ-populated, adviser-editable)
  // AML tab
  aml_risk: string;
  aml_last_date: string;
  aml_next_date: string;                         // user-editable; backend clamps if > rule ceiling
  // Beta #3: KYC contact snapshot.
  kyc_contact_person_id: string;
  kyc_contact_email: string;
  kyc_contact_phone_country_code: string;
  kyc_contact_phone_num: string;
  kyc_contact_address: string;
  // Team feedback P15 (migration 0099): client origin.
  client_origin: string;
  client_origin_referrer_id: string;
  client_origin_other: string;
}

const EMPTY: FormValues = {
  service_type: 'CIIF', client_number: 1, client_mnemonic: '', client_type: 'Particular',
  status: 'Active',
  mifid_investor_nature: '', mifid_residence: '', preferred_language: 'PT', preferred_ref_currency: 'EUR',
  mifir_id: '', mifir_id_type: '',
  kyc_last_date: '', kyc_next_date: '',
  mifid_suitability_score: '', mifid_suitability_last_date: '', mifid_suitability_next_date: '',
  strategic_allocation: '', esg_score: '',
  aml_risk: '', aml_last_date: '', aml_next_date: '',
  kyc_contact_person_id: '',
  kyc_contact_email: '',
  kyc_contact_phone_country_code: '',
  kyc_contact_phone_num: '',
  kyc_contact_address: '',
  client_origin: '',
  client_origin_referrer_id: '',
  client_origin_other: '',
};

/**
 * #327 (Task 2 Phase 1, 2026-06-09): Parties pending-op queue.
 * Lifted to AgreementDetailPage so the Save handler can batch the
 * ops alongside any other Edit-mode changes. Type is defined inline
 * because both AgreementDetailPage and PartiesSection live in this file.
 *
 * Session A4 (2026-06-10): `create` kind added — "+ Add party" now opens
 * AddAgreementPartyModal (modal-that-stages) instead of navigating away,
 * closing the last confirmed C-107 violator (#7). personDisplayName rides
 * along so the pending row renders a friendly name pre-commit.
 */
type PendingPartyOp =
  | { kind: 'delete'; party_id: UUID }
  | {
      kind: 'create';
      tempKey: string;
      payload: AgreementPartyCreateInput;
      personDisplayName: string | null;
    };

// ── usePendingOps key functions (module-level so references are stable) ──
// Session A4 (2026-06-10): the four hand-rolled queue states + failure sets
// were replaced by the shared usePendingOps hook (assessment pushback 1).
// Domain staging rules stay in the page via q.transform; storage, unstage,
// clearing and post-Save settlement are the hook's job.
/**
 * Pedro's UX request (2026-06-10): the Save toast names the changed fields.
 * Capped at three names + "+N more" so a bulk edit can't produce an
 * enormous toast (creates are unaffected — they keep the "created" toast).
 * The audit log remains the full inventory.
 */
const FIELD_LABELS: Record<string, string> = {
  service_type: 'Service type',
  client_number: 'Client number',
  client_mnemonic: 'Mnemonic',
  client_type: 'Client type',
  status: 'Status',
  mifid_investor_nature: 'Investor nature',
  mifid_residence: 'Residence',
  preferred_language: 'Preferred language',
  preferred_ref_currency: 'Preferred Ref Ccy',
  mifir_id: 'MIFIR ID',
  mifir_id_type: 'MIFIR ID type',
  kyc_last_date: 'KYC last review',
  kyc_next_date: 'KYC next review',
  mifid_suitability_score: 'Suitability score',
  esg_score: 'ESG score',
  mifid_suitability_last_date: 'Suitability last review',
  mifid_suitability_next_date: 'Suitability next review',
  strategic_allocation: 'Strategic allocation',
  aml_risk: 'AML risk',
  aml_last_date: 'AML last review',
  aml_next_date: 'AML next review',
  kyc_contact_person_id: 'KYC contact',
  kyc_contact_email: 'KYC contact email',
  kyc_contact_phone_country_code: 'KYC contact phone country',
  kyc_contact_phone_num: 'KYC contact phone',
  kyc_contact_address: 'KYC contact address',
  client_origin: 'Client origin',
  client_origin_referrer_id: 'Referred by',
  client_origin_other: 'Client origin (other)',
};

const rmOpKey = (op: PendingRMOp): string =>
  op.kind === 'assign' ? op.tempKey : op.assignment_id;
const partyOpKey = (op: PendingPartyOp): string =>
  op.kind === 'create' ? op.tempKey : op.party_id;
const docOpKey = (op: PendingDocOp): string =>
  op.kind === 'create' ? op.tempKey : op.doc_id;
const accountOpKey = (op: PendingAccountOp): string => op.account_id;
const policyOpKey = (op: PendingPolicyOp): string => op.policy_id;

/**
 * #327 (Task 2 Phase 2, 2026-06-09): IAA document pending-op queue.
 * Three op kinds, all violators in C-107 (lines 1584/1599/1611 for
 * the three Add buttons; 1698 for the per-row inline Save; 1708 for
 * the per-row Delete). The Add buttons stage `create`; the per-row
 * label/signed_date inputs flow through `update` ops; the trash icon
 * on Amendments stages `delete`. Knob A semantics: staging `delete`
 * drops any existing `update` for the same doc_id (revoke
 * supersedes).
 */
type PendingDocOp =
  | {
      kind: 'create';
      tempKey: string;
      payload: {
        kind: AgreementDocumentKind;
        ordinal: number;
        label: string;
        signed_date: string | null;
      };
    }
  | {
      kind: 'update';
      doc_id: UUID;
      payload: { label?: string; signed_date?: string | null };
    }
  | { kind: 'delete'; doc_id: UUID };

/**
 * #377 / A1.d (2026-06-09): Account detach pending-op queue.
 *
 * Only `detach-from-agreement` lives here on AgreementDetailPage — the
 * mirror op `detach-from-policy` belongs to InsurancePolicyDetailPage's
 * queue if/when implemented there. Both endpoints transition the
 * Account to orphan state (Inactive + both FKs NULL). Lifted to
 * AgreementDetailPage so the Save handler batches the detach with any
 * other Edit-mode changes through the shared maker-checker pipeline.
 *
 * +Add Account stays Pattern B (navigate-away to /accounts/new) for
 * now — see Task #379 §3h sweep for the broader Add-affordance
 * rewire.
 */
type PendingAccountOp =
  | { kind: 'detach'; account_id: UUID }
  | { kind: 'attach'; account_id: UUID };

/**
 * #21 / #19-B (2026-06-10, Pedro's decision): ALL policy operations on the
 * Agreement Insurance tab stage on the Edit transaction — attach existing,
 * move-from-another-agreement, detach, terminate. Completes the §3g
 * doctrine: "Edit mode is a draft; Save commits it — always."
 *
 * One op per policy (same-key staging replaces — e.g. staging Terminate
 * after Detach on the same policy keeps only the Terminate). `move`
 * carries its stage-time footprint plan; the server re-validates it at
 * commit and rejects with a stale-plan 409 if the world changed between
 * staging and Save (same protection the staged Terminate has relied on
 * since A2). attach/detach/terminate are direct writes (Compliance/Admin
 * routing per Wave A); move routes through maker-checker.
 */
type PendingPolicyOp =
  | { kind: 'attach'; policy_id: UUID; label: string | null }
  | {
      kind: 'move';
      policy_id: UUID;
      plan: Record<string, unknown>;
      label: string | null;
      sourceLabel: string | null;
    }
  | { kind: 'detach'; policy_id: UUID; label: string | null }
  | { kind: 'terminate'; policy_id: UUID; reason: CloseReason; label: string | null };

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  // Pass include_deleted=true so the detail page can load and render
  // soft-deleted records (DELETED badge + Restore button). Without this,
  // GET /agreements/{id} returns 404 on soft-deleted rows (per get_alive)
  // and the page can only show stale React Query cache. Task #245
  // (2026-06-02). Backend matches the list-endpoint posture: any
  // authenticated user can opt in to see deleted rows.
  const { data, isLoading } = useAgreement(isNew ? undefined : id, true);
  const create = useCreateAgreement();
  const update = useUpdateAgreement(id ?? '');
  const del = useDeleteAgreement();
  const cascadeDelete = useCascadeDeleteAgreement();
  const refs = useAgreementReferences(id, false);
  const [cascadeOpen, setCascadeOpen] = React.useState(false);
  const restore = useRestoreAgreement();

  // #262 cascade-status: state for the cascade modal flow.
  // Modal opens when Save is attempted with a cascade-triggering status
  // transition. `cascadeToStatus` drives both the footprint fetch and
  // the apply-button copy. `cascadePendingValues` stashes the form
  // values so other-fields don't get lost between Save click and Confirm.
  const [cascadeToStatus, setCascadeToStatus] =
    React.useState<CascadeLifecycleStatus | null>(null);
  const [cascadePendingValues, setCascadePendingValues] =
    React.useState<FormValues | null>(null);
  const cascadeFootprint = useAgreementCascadeFootprint(id, cascadeToStatus);
  const cascadeApply = useCascadeStatusApply();
  // Phase 3: faithful-reactivate flow. Opens when Save is attempted on an
  // Inactive→Active transition (which is NOT cascade-triggering, so it bypasses
  // the cascade modal). The footprint lists the cascade-touched children that
  // will be restored; the apply reopens to the reason-driven target.
  const [reactivateOpen, setReactivateOpen] = React.useState(false);
  const [reactivatePendingValues, setReactivatePendingValues] =
    React.useState<FormValues | null>(null);
  const reactivateFootprint = useAgreementReactivateFootprint(id, reactivateOpen);
  const reactivateApply = useReactivateApply();
  const confirmDelete = useConfirmDelete();

  // D2-polish 2 follow-up: tabs are URL-driven so deep-links like
  // /agreements/X?tab=parties land on the right tab. Used by the [+ Add Party]
  // → Person create → Save flow which returns to ?tab=parties.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  // Beta v2 polish G follow-up (2026-05-26): Identity is now the first
  // visible tab on existing agreements too. Land there by default so
  // the tab order matches the user's expectation (left-to-right).
  const defaultTab = 'identity';
  const activeTab = urlTab ?? defaultTab;
  const setActiveTab = (v: string | null) => {
    if (!v) return;
    const next = new URLSearchParams(searchParams);
    if (v === defaultTab) next.delete('tab');
    else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  const initial: FormValues = isNew || !data ? EMPTY : {
    service_type: data.service_type,
    client_number: data.client_number,
    client_mnemonic: data.client_mnemonic,
    client_type: data.client_type,
    status: data.status,
    mifid_investor_nature: data.mifid_investor_nature ?? '',
    mifid_residence: data.mifid_residence ?? '',
    preferred_language: data.preferred_language ?? 'PT',
    preferred_ref_currency: data.preferred_ref_currency ?? 'EUR',
    mifir_id: data.mifir_id ?? '',
    mifir_id_type: data.mifir_id_type ?? '',
    kyc_last_date: data.kyc_last_date ?? '',
    kyc_next_date: data.kyc_next_date ?? '',
    // #52: normalise the Decimal wire form ("50.00") to the discrete-step
    // form ("50") so the Select matches; legacy non-step values keep their
    // numeric face and are appended to the options at render time.
    mifid_suitability_score: data.mifid_suitability_score != null && data.mifid_suitability_score !== ''
      ? Number(data.mifid_suitability_score).toString() : '',
    mifid_suitability_last_date: data.mifid_suitability_last_date ?? '',
    mifid_suitability_next_date: data.mifid_suitability_next_date ?? '',
    strategic_allocation: data.strategic_allocation ?? '',
    esg_score: data.esg_score ?? '',
    aml_risk: data.aml_risk ?? '',
    aml_last_date: data.aml_last_date ?? '',
    aml_next_date: data.aml_next_date ?? '',
    kyc_contact_person_id: data.kyc_contact_person_id ?? '',
    kyc_contact_email: data.kyc_contact_email ?? '',
    kyc_contact_phone_country_code:
      data.kyc_contact_phone_country_code != null
        ? String(data.kyc_contact_phone_country_code) : '',
    kyc_contact_phone_num: data.kyc_contact_phone_num ?? '',
    kyc_contact_address: data.kyc_contact_address ?? '',
    client_origin: data.client_origin ?? '',
    client_origin_referrer_id: data.client_origin_referrer_id ?? '',
    client_origin_other: data.client_origin_other ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      client_mnemonic: (v) => (/^[A-Z]{6}$/.test(v) ? null : '6 uppercase letters required'),
      client_number: (v, values) => {
        // The 1-999 client number is only meaningful for a numbered Active
        // client saved through the normal PATCH. Every OTHER path skips it,
        // because the number is not part of that save:
        //   - creating / editing a Prospect (no number until promotion)
        //   - inactivating a Prospect or Active agreement (cascade-status
        //     endpoint — number untouched)
        //   - reactivating an Inactive agreement (reactivate endpoint; the
        //     backend sets the reopen target — a lost-lead reopens to Prospect,
        //     which has no number)
        // So require the number ONLY when the record is, and stays, a numbered
        // Active client. This is also robust to a stale `data` closure: any
        // value of data.status other than 'Active' skips the check.
        // (C-170 / lifecycle redesign, fixed 2026-06-15 after Pedro's smoke
        // found Prospect→Inactive and Inactive→reactivate both wrongly blocked.)
        const stayingActiveClient =
          values.status === 'Active' && (isNew || data?.status === 'Active');
        if (!stayingActiveClient) return null;
        const n = Number(v);
        return Number.isInteger(n) && n >= 1 && n <= 999 ? null : 'Must be 1-999';
      },
    },
  });
  // #326 (2026-06-09): hoisted ahead of the pending-RM-op helpers
  // below so settleSaveOutcome's useCallback closure can reference
  // showAlert without "used before declaration" errors. (Original
  // declaration site was after the kycContactOptions memo, ~line 463;
  // moved up the same const into this position.)
  const { showAlert, confirm } = useAlertModal();
  // Pedro 2026-10-10: unfilled required fields (completeness rules) are outlined in
  // violet and counted on their tab; the banner's fields open their tab.
  const missing = useMissingFields('AGREEMENT', isNew ? undefined : data?.id);
  const hl = (f: keyof FormValues) => missing.input(f, form.values[f]);

  // #326 Pattern A (§3g, 2026-06-09): Named RM ops are STAGED, not
  // applied immediately. The queue lives here so:
  //   1. The dirty-form-guard treats pending RM ops as "unsaved changes"
  //      (line below) and triggers the modal on navigate-away.
  //   2. The Save Changes handler processes the queue alongside the
  //      field-level update, in a single user-initiated transaction.
  //   3. Cancel / Discard clears the queue alongside form.reset().
  // `failedRMKeys` is populated by the Save handler when an op fails;
  // those rows show a red "Failed" badge so the user can retry without
  // losing context. Keys: tempKey for assigns, assignment_id otherwise.
  // Session A4 (2026-06-10): queue storage + failure bookkeeping moved to
  // the shared usePendingOps hook; only the domain staging rules remain
  // here (via q.stage / q.transform). Behaviour is unchanged.
  const rmQ = usePendingOps<PendingRMOp>(rmOpKey);

  // Named-RM staging handlers removed 2026-07-06 (C3): RM management moved to the
  // Admin "RMs" card (/admin/rms). `rmQ` stays defined + wired into Save/cascade as
  // an INERT queue (never populated from this page now) — its Save-side removal is a
  // filed follow-up, kept out of this change so it doesn't touch the Save transaction.

  // #327 (Task 2 Phase 1, 2026-06-09): Parties also stage on click —
  // same Pattern A discipline as Named RM.
  // Session A4 (2026-06-10): `create` kind added — staged by
  // AddAgreementPartyModal via stagePartyCreate below.
  const partyQ = usePendingOps<PendingPartyOp>(partyOpKey);

  const stagePartyDelete = React.useCallback((party_id: UUID) => {
    // stage() replaces same-key ops, so a double-click is a no-op
    // (same dedupe semantics as the previous hand-rolled guard).
    partyQ.stage({ kind: 'delete', party_id });
  }, [partyQ.stage]);

  // Session A4: "+ Add party" staging path. tempKey-keyed so multiple
  // adds coexist; personDisplayName rides along for the pending row.
  const stagePartyCreate = React.useCallback(
    (payload: AgreementPartyCreateInput, personDisplayName: string | null) => {
      const tempKey = `party-create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      partyQ.stage({ kind: 'create', tempKey, payload, personDisplayName });
    },
    [partyQ.stage],
  );

  const unstagePartyOp = partyQ.unstage;

  // #327 Phase 2 (2026-06-09): IAA document pending-op queue. Three
  // op kinds — create / update / delete — flowing through one queue
  // so the Save handler can commit them in order.
  const docQ = usePendingOps<PendingDocOp>(docOpKey);

  // #377 / A1.d (2026-06-09): Account detach pending-op queue. Only
  // `detach` is supported on Agreement detail today (Direct → orphan).
  // Failed key = account_id. Mirrors the PartiesSection pattern.
  const accountQ = usePendingOps<PendingAccountOp>(accountOpKey);

  const stageAccountDetach = React.useCallback((account_id: UUID) => {
    accountQ.stage({ kind: 'detach', account_id });
  }, [accountQ.stage]);
  // gap-1 (2026-06-16, Phase 4b / C-167): stage attaching an existing orphan
  // Account to this Agreement (mirror of AccountDetail reattach-to-agreement).
  // Commits on Save via commitPendingAccountOps.
  const stageAccountAttach = React.useCallback((account_id: UUID) => {
    accountQ.stage({ kind: 'attach', account_id });
  }, [accountQ.stage]);

  const unstageAccountOp = accountQ.unstage;

  // #21 / #19-B (2026-06-10): policy-operation queue — attach / move /
  // detach / terminate all stage; Save commits. Same-key staging means
  // at most one pending op per policy.
  const policyQ = usePendingOps<PendingPolicyOp>(policyOpKey);
  const stagePolicyOp = policyQ.stage;
  const unstagePolicyOp = policyQ.unstage;

  // Banks arc step 4 (0115): portfolio link / unlink queue (Pattern A, as parties). Ops run
  // in staged order on Save, so an unlink lands before a relink of the same portfolio.
  const portfolioQ = usePendingOps<PendingPortfolioOp>(portfolioOpKey);
  const stagePortfolioLink = React.useCallback(
    (bank_id: UUID, portfolio_number: string, label: string) => {
      const tempKey = `pf-link-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      portfolioQ.stage({ kind: 'link', tempKey, bank_id, portfolio_number, label });
    }, [portfolioQ.stage]);
  const stagePortfolioUnlink = React.useCallback((link_id: UUID, label: string) => {
    portfolioQ.stage({ kind: 'unlink', link_id, label });
  }, [portfolioQ.stage]);
  // TRADE-FLOW (Pedro 2026-10-25): the Trades tab's steps commit at once from the trade's side
  // panel (§3g Pattern B), so nothing of it rides on this page's Save.
  const tradeOverride = useAdminOverride();   // N5: the Admin override (Beta)
  // N2 / N4: a Trades row opens its draft in trade mode on this page's Portfolios tab.
  const [portfolioOpen, setPortfolioOpen] = React.useState<ViewRequest>({ ref: '', n: 0 });

  // Commit hooks for the policy batch (instantiated here so their cache
  // invalidations ride along; mutateAsync references are stable).
  const attachPolicyMut = useAttachPolicy();
  const movePolicyMut = useMovePolicy();
  const detachPolicyMut = useDetachPolicy();
  const redeemPolicyMut = useRedeemPolicy();
  const lapsePolicyMut = useLapsePolicy();
  const maturePolicyMut = useMaturePolicy();

  const stageDocCreate = React.useCallback(
    (payload: {
      kind: AgreementDocumentKind;
      ordinal: number;
      label: string;
      signed_date: string | null;
    }) => {
      const tempKey = `doc-create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      docQ.stage({ kind: 'create', tempKey, payload });
    },
    [docQ.stage],
  );

  // updateDocField — bidirectional: writes the new value into the
  // pending op (creating or extending it), AND clears the field from
  // the payload if it's been reverted to the DB value. If the payload
  // ends up empty, the update op itself is dropped. For pending
  // creates, updates the create payload in place (no separate update
  // op needed since the create hasn't hit the DB yet).
  const updateDocField = React.useCallback(
    <K extends 'label' | 'signed_date'>(
      key: string,                   // doc_id for DB rows, tempKey for pending-create rows
      isPendingCreate: boolean,
      field: K,
      newValue: string | null,
      dbValue: string | null,
    ) => {
      docQ.transform((prev) => {
        if (isPendingCreate) {
          // Mutate the create payload in place
          return prev.map((op) =>
            op.kind === 'create' && op.tempKey === key
              ? { ...op, payload: { ...op.payload, [field]: newValue } }
              : op,
          );
        }
        // DB row: stage / extend / clear an update op
        const existingIdx = prev.findIndex(
          (op) => op.kind === 'update' && op.doc_id === key,
        );
        const matchesDb = (newValue ?? null) === (dbValue ?? null);
        if (matchesDb) {
          if (existingIdx === -1) return prev;
          const existing = prev[existingIdx] as Extract<PendingDocOp, { kind: 'update' }>;
          const remaining = { ...existing.payload };
          delete remaining[field];
          if (Object.keys(remaining).length === 0) {
            return prev.filter((_, i) => i !== existingIdx);
          }
          return prev.map((op, i) => (i === existingIdx ? { ...existing, payload: remaining } : op));
        }
        if (existingIdx === -1) {
          return [...prev, { kind: 'update', doc_id: key, payload: { [field]: newValue } }];
        }
        const existing = prev[existingIdx] as Extract<PendingDocOp, { kind: 'update' }>;
        return prev.map((op, i) =>
          i === existingIdx
            ? { ...existing, payload: { ...existing.payload, [field]: newValue } }
            : op,
        );
      });
    },
    [],
  );

  const stageDocDelete = React.useCallback((doc_id: UUID) => {
    docQ.transform((prev) => {
      // Delete supersedes any pending update for the same row.
      const filtered = prev.filter(
        (op) => !(op.kind === 'update' && op.doc_id === doc_id),
      );
      return [...filtered, { kind: 'delete', doc_id }];
    });
  }, [docQ.transform]);

  const unstageDocOp = docQ.unstage;

  // P23 (2026-10-10): files on this agreement (signed legal documents + others) —
  // staged on the Documents tab in Edit mode, sent on Save. Distinct from docQ above,
  // which edits the IAA-history rows (agreement_documents).
  const fileDocs = useDocumentStaging('agreements', isNew ? undefined : id);
  const fileDocsClear = fileDocs.queue.clear;

  // Session A4: one place to clear every staged queue — wired into
  // Cancel, back-arrow and the dirty-guard discard paths.
  // #21: policy queue included.
  const clearAllQueues = React.useCallback(() => {
    rmQ.clear();
    partyQ.clear();
    docQ.clear();
    accountQ.clear();
    policyQ.clear();
    portfolioQ.clear();
    fileDocsClear();
  }, [rmQ.clear, partyQ.clear, docQ.clear, accountQ.clear, policyQ.clear, portfolioQ.clear, fileDocsClear]);

  // #326 Step D (2026-06-09): the dirty signal aggregates form
  // dirtiness AND every pending-op queue, so navigate-away with only
  // staged changes still triggers the dirty-form-guard.
  // #327 Phase 1: pendingPartyOps added (Party Delete).
  // #327 Phase 2: pendingDocOps added (IAA create/update/delete).
  // #377 A1.d: pendingAccountOps added (Account Detach-from-Agreement).
  // #52: a staged AQ assessment + staged version deletes are part of the
  // Edit transaction too (polish round 2).
  const [aqDraft, setAqDraft] = React.useState<AqDraft | null>(null);
  const [aqDeletes, setAqDeletes] = React.useState<UUID[]>([]);
  const createAq = useCreateAqResponse(data?.id);
  // Conversion rows for the persistent under-field warning (server truth
  // lives in the completeness pseudo-field; this mirrors it on the page).
  // The exceeds computation itself lives below editMode's declaration.
  const conversionRowsQ = useSuitabilityConversion();
  const aqResponsesQ = useAqResponses(id && id !== 'new' ? id : undefined);
  // Pending (RM-filed, not-yet-approved) questionnaire CREATEs for this agreement
  // — used to flag that Score/ESG will change on approval (#52/#11).
  const pendingAqCreates = usePendingCreates('aq_responses', 'agreement_id', id && id !== 'new' ? id : undefined);

  const pageDirty = form.isDirty()
    || !rmQ.isEmpty
    || !partyQ.isEmpty
    || !docQ.isEmpty
    || !accountQ.isEmpty
    || !policyQ.isEmpty
    || !portfolioQ.isEmpty
    || !fileDocs.queue.isEmpty
    || aqDraft !== null
    || aqDeletes.length > 0;
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('agreement-form', pageDirty, !isNew);
  const confirmDiscard = useDirtyFormGuard(pageDirty, saveForGuard);

  // #326 Step E: batch helper for the Save handler. Processes the
  // staged RM ops sequentially through the maker-checker-aware API
  // and returns the aggregate outcome. The caller is responsible for
  // updating pendingRMOps + failedRMKeys based on the result and for
  // composing the user-facing toast.
  //
  // Sequential (not parallel) is deliberate: the partial unique index
  // on (agreement_id, user_id) WHERE deleted_at IS NULL means a revoke
  // of user X followed by a fresh assign of user X must hit the DB in
  // that order. Parallel firing could race and produce a 409 on the
  // assign.
  //
  // queries are invalidated once at the end, not per-op, to keep the
  // table from flickering through intermediate states.
  const queryClient = useQueryClient();
  const commitPendingRMOps = React.useCallback(
    async (ops: readonly PendingRMOp[]): Promise<{
      applied: number;
      filed: number;
      failedKeys: Set<string>;
      // #58 (2026-06-14, §3j): the detail of the FIRST failed op in the
      // batch, so the aggregate Save toast can state a concrete reason
      // instead of only a count. undefined when nothing failed.
      firstError?: string;
    }> => {
      if (ops.length === 0 || !id) {
        return { applied: 0, filed: 0, failedKeys: new Set() };
      }
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = op.kind === 'assign' ? op.tempKey : op.assignment_id;
        try {
          const result = op.kind === 'assign'
            ? await agreementUsersApi.create({
                agreement_id: id,
                user_id: op.user_id,
                is_primary: op.is_primary,
              })
            : op.kind === 'revoke'
              ? await agreementUsersApi.delete(op.assignment_id)
              : await agreementUsersApi.update(op.assignment_id, {
                  is_primary: op.new_is_primary,
                });
          if (result.pending) filed++;
          else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: agreementUserKeys.all });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient],
  );

  // #327 Phase 1 (2026-06-09): mirror of commitPendingRMOps for Parties.
  // Session A4 (2026-06-10): `create` kind committed alongside `delete`.
  // Ops run in STAGED order (same rationale as the RM queue above): a
  // delete of person P's role staged before a fresh add of the same
  // (person, role) must hit the DB in that order, or the partial-unique
  // index on alive rows 409s the create.
  const commitPendingPartyOps = React.useCallback(
    async (ops: readonly PendingPartyOp[]): Promise<{
      applied: number;
      filed: number;
      failedKeys: Set<string>;
      // #58 (2026-06-14, §3j): the detail of the FIRST failed op in the
      // batch, so the aggregate Save toast can state a concrete reason
      // instead of only a count. undefined when nothing failed.
      firstError?: string;
    }> => {
      if (ops.length === 0 || !id) {
        return { applied: 0, filed: 0, failedKeys: new Set() };
      }
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = partyOpKey(op);
        try {
          const result = op.kind === 'create'
            ? await agreementPartiesApi.create(op.payload)
            : await agreementPartiesApi.delete(op.party_id);
          if (result.pending) filed++;
          else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: apKeys.all });
      // Pedro's app-wide rule (2026-06-10, extends Task 4): filed-pending
      // creates stay VISIBLE in the list as phantom rows sourced from the
      // pending CRs — refresh that query so the row appears the moment
      // the Save toast fires.
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient],
  );

  // Banks arc step 4 (0115): the portfolio link batch, in staged order (an unlink before a
  // relink: a portfolio belongs to one agreement at a time).
  const commitPendingPortfolioOps = React.useCallback(
    async (ops: readonly PendingPortfolioOp[]): Promise<{
      applied: number; filed: number; failedKeys: Set<string>; firstError?: string;
    }> => {
      if (ops.length === 0 || !id) return { applied: 0, filed: 0, failedKeys: new Set() };
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        try {
          const result = op.kind === 'link'
            ? await agreementPortfoliosApi.link({
                agreement_id: id, bank_id: op.bank_id, portfolio_number: op.portfolio_number })
            : await agreementPortfoliosApi.unlink(op.link_id);
          if (result.pending) filed++;
          else applied++;
        } catch (e) {
          failedKeys.add(portfolioOpKey(op));
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['agreement-portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient],
  );

  // #327 Phase 2 (2026-06-09): IAA document batch helper. Three op
  // kinds processed in order. Key for failed-tracking: tempKey for
  // creates, doc_id for updates/deletes. The agreement_documents
  // hooks already return WriteResult<T> from the maker-checker layer.
  const commitPendingDocOps = React.useCallback(
    async (ops: readonly PendingDocOp[]): Promise<{
      applied: number;
      filed: number;
      failedKeys: Set<string>;
      // #58 (2026-06-14, §3j): the detail of the FIRST failed op in the
      // batch, so the aggregate Save toast can state a concrete reason
      // instead of only a count. undefined when nothing failed.
      firstError?: string;
    }> => {
      if (ops.length === 0 || !id) {
        return { applied: 0, filed: 0, failedKeys: new Set() };
      }
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        const key = op.kind === 'create' ? op.tempKey : op.doc_id;
        try {
          const result = op.kind === 'create'
            ? await agreementDocumentsApi.create({
                agreement_id: id,
                kind: op.payload.kind,
                ordinal: op.payload.ordinal,
                label: op.payload.label,
                signed_date: op.payload.signed_date,
              })
            : op.kind === 'update'
              ? await agreementDocumentsApi.update(op.doc_id, op.payload)
              : await agreementDocumentsApi.delete(op.doc_id);
          if (result.pending) filed++;
          else applied++;
        } catch (e) {
          failedKeys.add(key);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: adKeys.all });
      // Doc writes also alter agreement-level fields (iaa_initial_date,
      // close_date) surfaced on /agreements — mirror useAgreementDocuments'
      // invalidateBoth behaviour.
      queryClient.invalidateQueries({ queryKey: agreementsKeys.all, refetchType: 'all' });
      // #20 (2026-06-10): filed doc CREATEs render as phantom rows
      // sourced from pending CRs — refresh that query so the row
      // appears the moment the Save toast fires.
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient],
  );

  // #377 / A1.d (2026-06-09): mirror of commitPendingPartyOps for
  // Account detach. Sequential commit through accountsApi (which
  // routes through maker-checker on the backend). Each detach
  // transitions the Account to orphan (Inactive, both FKs NULL).
  // Failed key = account_id. Invalidates both the accounts cache
  // (the list this section reads) and the Compliance queue cache
  // (CRs may have been filed).
  const commitPendingAccountOps = React.useCallback(
    async (ops: readonly PendingAccountOp[]): Promise<{
      applied: number;
      filed: number;
      failedKeys: Set<string>;
      // #58 (2026-06-14, §3j): the detail of the FIRST failed op in the
      // batch, so the aggregate Save toast can state a concrete reason
      // instead of only a count. undefined when nothing failed.
      firstError?: string;
    }> => {
      if (ops.length === 0 || !id) {
        return { applied: 0, filed: 0, failedKeys: new Set() };
      }
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | undefined;
      for (const op of ops) {
        try {
          // gap-1 (2026-06-16): attach an orphan Account to this Agreement;
          // detach (existing) sends a Direct Account to orphan. Both route
          // through maker-checker server-side (applied or filed).
          const result = op.kind === 'attach'
            ? await accountsApi.attachToAgreement(op.account_id, id)
            : await accountsApi.detachFromAgreement(op.account_id);
          if (result.pending) filed++;
          else applied++;
        } catch (e) {
          failedKeys.add(op.account_id);
          if (!firstError && e instanceof ApiError) firstError = e.detail;
        }
      }
      queryClient.invalidateQueries({ queryKey: accountsKeys.all });
      // The Agreement Accounts tab is fed by useAccounts({agreement_id: X})
      // which is in accountsKeys.all. Also invalidate Compliance queue
      // (RM filings) and account-insurance (in case the policy join
      // reflects on the policy's Accounts tab too).
      queryClient.invalidateQueries({ queryKey: ['account-insurance'] });
      queryClient.invalidateQueries({ queryKey: ['compliance'] });
      // A detach clears that account's Fees-tab billing identity (apply-path,
      // C-214); refresh the billing list (the Fees panel is keepMounted, so it
      // won't refetch on its own under the 30s staleTime).
      queryClient.invalidateQueries({ queryKey: ['account-billing'] });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient],
  );

  // #21 / #19-B (2026-06-10): commit the staged policy operations.
  // Sequential, processed LAST in the Save batch (cascade-laden ops after
  // everything else — same rationale as the Policy page's Phase 1.5
  // ordering). attach/detach/terminate are direct writes (applied or
  // error); move routes through maker-checker (applied or filed). A
  // stale move plan is rejected server-side (409) — the op stays staged
  // with a Failed badge, and the first error detail is surfaced via a
  // modal alert so the user knows to unstage + re-pick.
  const commitPendingPolicyOps = React.useCallback(
    async (ops: readonly PendingPolicyOp[]): Promise<{
      applied: number;
      filed: number;
      failedKeys: Set<string>;
      firstError: string | null;
    }> => {
      if (ops.length === 0 || !id) {
        return { applied: 0, filed: 0, failedKeys: new Set(), firstError: null };
      }
      let applied = 0;
      let filed = 0;
      const failedKeys = new Set<string>();
      let firstError: string | null = null;
      for (const op of ops) {
        try {
          if (op.kind === 'attach') {
            // 2026-06-10: attach endpoint went maker-checker — RM filings
            // come back pending instead of 403ing.
            const r = await attachPolicyMut.mutateAsync({ id: op.policy_id, agreementId: id });
            if (r.pending) filed++;
            else applied++;
          } else if (op.kind === 'move') {
            const r = await movePolicyMut.mutateAsync({
              id: op.policy_id, body: { move_plan: op.plan },
            });
            if (r.pending) filed++;
            else applied++;
          } else if (op.kind === 'detach') {
            // #26 (2026-06-10): detach went maker-checker — RM filings
            // come back pending with a footprint plan CR.
            const r = await detachPolicyMut.mutateAsync(op.policy_id);
            if (r.pending) filed++;
            else applied++;
          } else {
            const hook = op.reason === 'REDEEMED' ? redeemPolicyMut
              : op.reason === 'LAPSED' ? lapsePolicyMut
              : maturePolicyMut;
            // #26 (2026-06-10): terminate went maker-checker too.
            const r = await hook.mutateAsync(op.policy_id);
            if (r.pending) filed++;
            else applied++;
          }
        } catch (err) {
          failedKeys.add(op.policy_id);
          if (!firstError) {
            firstError = err instanceof ApiError ? err.detail : 'Unexpected error.';
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ['account-insurance'] });
      queryClient.invalidateQueries({ queryKey: accountsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['compliance'] });
      // A policy detach cascades a billing-clear to every indirect account via
      // that policy (apply-path, C-214); refresh the Fees-tab billing list.
      queryClient.invalidateQueries({ queryKey: ['account-billing'] });
      return { applied, filed, failedKeys, firstError };
    },
    [id, queryClient, attachPolicyMut.mutateAsync, movePolicyMut.mutateAsync,
     detachPolicyMut.mutateAsync, redeemPolicyMut.mutateAsync,
     lapsePolicyMut.mutateAsync, maturePolicyMut.mutateAsync],
  );

  // #326 Step E (cont.): post-save settlement. Runs after the field
  // update commits (whether through update.mutate or cascadeApply).
  // Processes the RM op batch, updates pending/failed state, exits
  // edit mode iff everything settled cleanly, and shows the aggregate
  // toast (or modal for the pure field-pending case).
  const settleSaveOutcome = React.useCallback(
    async (fieldOutcome: {
      fieldChanged: boolean;
      pending: boolean;
      clamped: string[];
      /** Friendly names of the changed form fields — rendered into the
       *  toast capped at 3 + "+N more" (Pedro, 2026-06-10). */
      changedLabels?: string[];
    }): Promise<void> => {
      const opsRM = rmQ.ops;
      const opsParty = partyQ.ops;
      const opsDoc = docQ.ops;
      const opsAccount = accountQ.ops;
      const opsPolicy = policyQ.ops;
      const opsPortfolio = portfolioQ.ops;
      const rmResult = await commitPendingRMOps(opsRM);
      const partyResult = await commitPendingPartyOps(opsParty);
      const portfolioResult = await commitPendingPortfolioOps(opsPortfolio);
      const docResult = await commitPendingDocOps(opsDoc);
      const accountResult = await commitPendingAccountOps(opsAccount);
      // P23: staged document files (settled inside commit()).
      const hadFileDocOps = !fileDocs.queue.isEmpty;
      const fileDocResult = await fileDocs.commit();
      // #21: policy ops LAST — cascade-laden operations commit after
      // everything else has landed.
      const policyResult = await commitPendingPolicyOps(opsPolicy);

      // Keep failed ops in their queues (so user can retry); drop successes.
      // (usePendingOps.settle does the filter + failure-marker replacement.)
      rmQ.settle(rmResult.failedKeys);
      partyQ.settle(partyResult.failedKeys);
      docQ.settle(docResult.failedKeys);
      accountQ.settle(accountResult.failedKeys);
      policyQ.settle(policyResult.failedKeys);
      portfolioQ.settle(portfolioResult.failedKeys, portfolioResult.firstError);

      // Policy failures get a modal alert with the server's reason (the
      // most actionable case is a stale move plan — the user must
      // unstage and re-pick from a fresh preview).
      if (policyResult.firstError) {
        showAlert({
          color: 'red',
          title: 'Policy operation failed',
          message: `${policyResult.firstError} The staged item was kept — fix the cause (or unstage it) and Save again.`,
        });
      }

      const totalFailed =
        rmResult.failedKeys.size + partyResult.failedKeys.size + docResult.failedKeys.size
        + accountResult.failedKeys.size + policyResult.failedKeys.size
        + portfolioResult.failedKeys.size + fileDocResult.failedKeys.size;

      // §3g corollary 6: stay in edit mode if anything failed so the
      // user can address the cause and re-save without losing context.
      if (totalFailed === 0) {
        setEditMode(false);
      }

      // Pure field-pending (no relationship ops in any queue): preserve
      // existing modal alert — the "live record unchanged until
      // Compliance approves" message is important context that's
      // better in a modal than a toast.
      const hadOps = opsRM.length > 0 || opsParty.length > 0 || opsDoc.length > 0
        || opsAccount.length > 0 || opsPolicy.length > 0 || opsPortfolio.length > 0 || hadFileDocOps;
      if (fieldOutcome.fieldChanged && fieldOutcome.pending && !hadOps) {
        showAlert({
          color: 'orange',
          title: 'Filed for compliance approval',
          message: 'Your edits were filed. The live record is unchanged until Compliance approves.',
        });
        return;
      }

      // Compose the aggregate toast (field + RM + Party + IAA).
      const parts: string[] = [];
      if (fieldOutcome.fieldChanged) {
        if (fieldOutcome.pending) {
          parts.push(`Filed for approval${fieldSuffix(fieldOutcome.changedLabels)}`);
        } else if (fieldOutcome.clamped.length > 0) {
          // Clamp information outranks the field list — keep it prominent.
          parts.push(`Saved${fieldSuffix(fieldOutcome.changedLabels)} — ${fieldOutcome.clamped.join(' / ')} next-review clamped to policy`);
        } else {
          parts.push(`Saved${fieldSuffix(fieldOutcome.changedLabels)}`);
        }
      }
      if (rmResult.applied > 0) {
        parts.push(`${rmResult.applied} Named RM change${rmResult.applied === 1 ? '' : 's'} applied`);
      }
      if (rmResult.filed > 0) {
        parts.push(`${rmResult.filed} Named RM change${rmResult.filed === 1 ? '' : 's'} filed for approval`);
      }
      if (rmResult.failedKeys.size > 0) {
        parts.push(`${rmResult.failedKeys.size} Named RM change${rmResult.failedKeys.size === 1 ? '' : 's'} failed${rmResult.firstError ? ` (${rmResult.firstError})` : ''} — see Failed badges and retry`);
      }
      if (partyResult.applied > 0) {
        // Session A4: queue carries adds AND detaches — neutral wording.
        parts.push(`${partyResult.applied} party change${partyResult.applied === 1 ? '' : 's'} applied`);
      }
      if (partyResult.filed > 0) {
        parts.push(`${partyResult.filed} party change${partyResult.filed === 1 ? '' : 's'} filed for approval`);
      }
      if (partyResult.failedKeys.size > 0) {
        parts.push(`${partyResult.failedKeys.size} party change${partyResult.failedKeys.size === 1 ? '' : 's'} failed${partyResult.firstError ? ` (${partyResult.firstError})` : ''} — see Failed badges and retry`);
      }
      if (docResult.applied > 0) {
        parts.push(`${docResult.applied} IAA change${docResult.applied === 1 ? '' : 's'} applied`);
      }
      if (docResult.filed > 0) {
        parts.push(`${docResult.filed} IAA change${docResult.filed === 1 ? '' : 's'} filed for approval`);
      }
      if (docResult.failedKeys.size > 0) {
        parts.push(`${docResult.failedKeys.size} IAA change${docResult.failedKeys.size === 1 ? '' : 's'} failed${docResult.firstError ? ` (${docResult.firstError})` : ''} — see Failed badges and retry`);
      }
      if (accountResult.applied > 0) {
        parts.push(`${accountResult.applied} account ${accountResult.applied === 1 ? 'detached' : 'detachments applied'}`);
      }
      if (accountResult.filed > 0) {
        parts.push(`${accountResult.filed} account ${accountResult.filed === 1 ? 'detachment' : 'detachments'} filed for approval`);
      }
      if (accountResult.failedKeys.size > 0) {
        parts.push(`${accountResult.failedKeys.size} account ${accountResult.failedKeys.size === 1 ? 'detachment' : 'detachments'} failed${accountResult.firstError ? ` (${accountResult.firstError})` : ''} — see Failed badges and retry`);
      }
      if (policyResult.applied > 0) {
        parts.push(`${policyResult.applied} policy change${policyResult.applied === 1 ? '' : 's'} applied`);
      }
      if (policyResult.filed > 0) {
        parts.push(`${policyResult.filed} policy change${policyResult.filed === 1 ? '' : 's'} filed for approval`);
      }
      if (policyResult.failedKeys.size > 0) {
        parts.push(`${policyResult.failedKeys.size} policy change${policyResult.failedKeys.size === 1 ? '' : 's'} failed — see Failed badges and retry`);
      }
      if (portfolioResult.applied > 0) {
        parts.push(`${portfolioResult.applied} portfolio link change${portfolioResult.applied === 1 ? '' : 's'} applied`);
      }
      if (portfolioResult.filed > 0) {
        parts.push(`${portfolioResult.filed} portfolio link change${portfolioResult.filed === 1 ? '' : 's'} filed for approval`);
      }
      if (portfolioResult.failedKeys.size > 0) {
        parts.push(`${portfolioResult.failedKeys.size} portfolio link change${portfolioResult.failedKeys.size === 1 ? '' : 's'} failed${portfolioResult.firstError ? ` (${portfolioResult.firstError})` : ''} — see Failed badges and retry`);
      }
      parts.push(...documentResultParts(fileDocResult));
      if (parts.length === 0) return;  // pure no-op

      // Tier colour:
      //   yellow → any op failed (user attention required) or clamped
      //   orange → anything filed for approval (field or relationship)
      //   green  → everything applied cleanly
      let color: string;
      if (totalFailed > 0) color = 'yellow';
      else if (fieldOutcome.pending || rmResult.filed > 0 || partyResult.filed > 0
        || docResult.filed > 0 || accountResult.filed > 0 || policyResult.filed > 0
        || portfolioResult.filed > 0 || fileDocResult.filed > 0) color = 'orange';
      else if (fieldOutcome.clamped.length > 0) color = 'yellow';
      else color = 'green';

      notifications.show({
        color,
        message: parts.join(' · '),
        autoClose: 6000,
      });
    },
    [rmQ.ops, rmQ.settle, partyQ.ops, partyQ.settle, docQ.ops, docQ.settle,
     accountQ.ops, accountQ.settle, policyQ.ops, policyQ.settle, portfolioQ.ops, portfolioQ.settle,
     commitPendingRMOps, commitPendingPartyOps, commitPendingDocOps,
     commitPendingAccountOps, commitPendingPolicyOps, commitPendingPortfolioOps, showAlert,
     fileDocs.queue.isEmpty, fileDocs.commit],
  );

  // Beta #3 polish (2026-05-26): KYC-contact picker is scoped to the
  // current Agreement's parties — not the global persons list.
  //
  // Task #231 (2026-05-28): the backend now returns person_display_name
  // on each AgreementParty payload, so we no longer need the per-row
  // /api/persons/<id> cascade that the previous useQueries implementation
  // produced. On a real-volume agreement with N parties, that cascade fired
  // N parallel requests on every page mount and tripped the DLP rate gate
  // during the v4 incident. The current implementation reads
  // person_display_name straight from the parties list response — one
  // request total. Pattern documented in
  // _MEMORY/decisions/2026-05-28_deploy-day-incident.md §7.
  const kycPartiesQ = useAgreementParties({
    agreement_id: id ?? '',
    limit: 200,
  });
  const kycContactOptions = React.useMemo(() => {
    const items = kycPartiesQ.data?.items ?? [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const ap of items) {
      if (!ap.person_id || seen.has(ap.person_id)) continue;
      seen.add(ap.person_id);
      options.push({
        value: ap.person_id,
        // Defensive fallback to a UUID prefix in the rare case where the
        // referenced Person row is missing or hasn't been resolved
        // server-side. Should be unreachable under normal FK constraints
        // but keeps the picker functional rather than blank-labelled.
        label: ap.person_display_name ?? `(${ap.person_id.slice(0, 8)})`,
      });
    }
    return options;
  }, [kycPartiesQ.data]);

  const [mnemonicModalOpen, setMnemonicModalOpen] = React.useState(false);
  // P10 (Pedro 2026-10-11): suggest the MIFIR NCI / LEI from a party of the agreement.
  const [mifirModalOpen, setMifirModalOpen] = React.useState(false);
  // showAlert hoisted earlier in the component (just after form
  // initialization) so the #326 Pattern A helpers can reference it.

  // #276 (2026-06-08): Named RM management is Compliance/Admin only.
  // The backend enforces this (403 from the AdminUser dependency);
  // gating the UI here just hides the buttons for non-admin roles
  // so they don't see affordances they can't use.
  // #13 (2026-06-17): Named RM editing is Admin-only initially (tightened from
  // compliance+admin), pending the #14 permissions framework.
  const { can } = usePermissions();
  // #14 P2: gate on permissions, not roles. agreement.promote is admin/compliance/rm.
  const canPromote = can('agreement.promote');
  // Read-mode default for existing agreements; new agreements land in edit
  // mode so the user can fill the form. Toggling Edit / Cancel on the header
  // flips this; Save success calls setEditMode(false) below.
  const [editMode, setEditMode] = React.useState(isNew);
  // P34: the Email button (Admin / Compliance / RM; Read mode too).
  const { user: me } = useCurrentUser();
  const [emailOpen, setEmailOpen] = React.useState(false);
  // #51: forced "New client / Prospect" choice on the create form. null until
  // the user picks — Save stays disabled, so an untouched form can never burn
  // a client number. Only meaningful when isNew.
  const [createKind, setCreateKind] = React.useState<'client' | 'prospect' | null>(null);

  // #6 (2026-09-13): resume the "Create new person" round-trip. PersonDetailPage
  // ferries the newly-created person's id back as ?new_party_person=<id> on the
  // Parties tab. Re-enter Edit mode (the Add-party flow is Edit-gated; the fresh
  // mount lands in Read mode) and hand the id to PartiesSection so it reopens the
  // Add-party modal on the Attach step pre-selected; strip the param so it fires once.
  // P18 (2026-10-10): shared with the Policy and Account pages (PartyAddSteps).
  const [resumePartyPersonId, setResumePartyPersonId] = React.useState<string | null>(null);
  // Waits for `data` so the form is filled before Edit mode turns on.
  useNewPartyPersonResume(!!data, (pid) => {
    setResumePartyPersonId(pid);
    setEditMode(true);
  });

  // #52 round-3 polish: the under-field "Allocation exceeds Suitability
  // Score" warning tracks the LIVE form values in Edit mode (re-picking
  // an acceptable allocation clears it immediately); in read mode it
  // reflects the saved record.
  const savedAllocationExceeds = editMode
    ? allocationExceeds(
        conversionRowsQ.data?.items ?? [],
        form.values.mifid_suitability_score !== ''
          ? Number(form.values.mifid_suitability_score) : null,
        form.values.strategic_allocation || null,
      )
    : allocationExceeds(
        conversionRowsQ.data?.items ?? [],
        data?.mifid_suitability_score != null && data.mifid_suitability_score !== ''
          ? Number(data.mifid_suitability_score) : null,
        data?.strategic_allocation,
      );
  // Score/ESG auto-sync from the latest questionnaire (#52/#11): the backend
  // sets these on apply; a MANUAL override is allowed but surfaced with a small
  // footnote (mirrors the SAA exceed note). Compares the shown value (live form
  // in Edit, saved value in Read) to the most-recent questionnaire's computed value.
  const _latestAq = aqResponsesQ.data?.items?.[0] ?? null;
  const _curScore = editMode
    ? form.values.mifid_suitability_score
    : (data?.mifid_suitability_score != null && data.mifid_suitability_score !== ''
        ? String(Number(data.mifid_suitability_score)) : '');
  const scoreDeviatesFromAq = !!_latestAq && _latestAq.profile_score != null
    && _curScore !== '' && _curScore !== String(_latestAq.profile_score);
  const _curEsg = editMode ? form.values.esg_score : (data?.esg_score ?? '');
  const esgDeviatesFromAq = !!_latestAq && !!_latestAq.esg_derived
    && _curEsg !== '' && _curEsg !== _latestAq.esg_derived;
  const hasPendingAq = pendingAqCreates.length > 0;
  const lastAutoNumber = React.useRef<{ service: ServiceType; num: number } | null>(null);

  // Phase D2: agreement_documents drive the IAA history (initial signing,
  // amendments, close) and the summary-bar IAA-or-close date.
  const docsQ = useAgreementDocumentsByAgreement(isNew ? undefined : id);

  React.useEffect(() => {
    if (!isNew) return;
    const svc = form.values.service_type;
    let cancelled = false;
    agreementsApi.nextClientNumber(svc)
      .then(({ next }) => {
        if (cancelled) return;
        const current = Number(form.values.client_number);
        const last = lastAutoNumber.current;
        if ((current === 1 && !last) || (last && last.num === current)) {
          form.setFieldValue('client_number', next);
          lastAutoNumber.current = { service: svc, num: next };
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.service_type, isNew]);


  // #31 (2026-06-11): hydration effect replaces the old render-time
  // sentinel guard (`form.values.client_mnemonic === ''` → setValues),
  // which snapped the form back whenever the user CLEARED the mnemonic.
  // Hydrate when server data arrives or refetches, but never while an
  // edit is open. Pattern shared with the other detail pages.
  React.useEffect(() => {
    if (!isNew && data && !editMode) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;
  const isParticular = form.values.client_type === 'Particular';

  // Summary-bar IAA / Close date derivation.
  const docs = docsQ.data?.items ?? [];
  const iaaInitial = docs.find((d) => d.kind === 'IAA_INITIAL') ?? null;
  const closeRow = docs.find((d) => d.kind === 'CLOSE') ?? null;
  // #273 (2026-06-08): "isInactivo" → "isTerminated" — covers both Inactive
  // limbo and Closed (terminal). Both states render the Close date in the
  // summary bar rather than the IAA initial.
  const isTerminated =
    form.values.status === 'Inactive' || form.values.status === 'Closed';
  const summaryIaaDate = isTerminated
    ? (closeRow?.signed_date ?? null)
    : (iaaInitial?.signed_date ?? null);
  const summaryIaaLabel = isTerminated ? 'Close date' : 'IAA initial';

  // Beta #15 (2026-05-26): Aggregated PEP + US Person (FATCA) flags across
  // all alive parties on this agreement. Both surfaces are intentionally
  // worst-case rollups — if any party triggers, the agreement is flagged.
  //
  // PEP: 'PEP' > 'Related' (family/close) > 'Não' > '—' (no data yet).
  // US:  any us_person=true → 'Yes' (FATCA Form W-9 due); otherwise 'No' if
  //      all parties answered no; '—' if anyone is unanswered.
  //
  // Tooltips list the per-party breakdown so the user can drill in
  // without leaving the page.
  // Task #231 (2026-05-28): partyPersons used to come from N parallel
  // /api/persons/<id> fetches via the kycPartyPersonQueries cascade —
  // that fan-out was the production-incident DLP-trip on 2026-05-28.
  // The backend now returns person_display_name + person_pep_status +
  // person_us_person directly on each AgreementParty payload, so we
  // build the partyPersons array from a single response. Same shape the
  // downstream pepAggregate / usAggregate memos consumed before.
  const partyPersons = React.useMemo(() => {
    const items = kycPartiesQ.data?.items ?? [];
    const seen = new Set<string>();
    const out: { display_name: string; pep_status: string | null; us_person: boolean | null; legal: boolean }[] = [];
    for (const ap of items) {
      if (!ap.person_id || seen.has(ap.person_id)) continue;
      seen.add(ap.person_id);
      out.push({
        display_name: ap.person_display_name ?? `(${ap.person_id.slice(0, 8)})`,
        pep_status: ap.person_pep_status ?? null,
        us_person: ap.person_us_person ?? null,
        legal: ap.person_kind === 'LEGAL',
      });
    }
    return out;
  }, [kycPartiesQ.data]);

  // Beta v3 polish C (2026-05-27): strongest answered signal wins —
  // unanswered parties no longer dilute a real signal. Matches the
  // server-side rollup in app/services/agreement_aggregates.py so the
  // summary-bar badges agree with the list-page dots.
  const pepAggregate = React.useMemo(() => {
    if (partyPersons.length === 0) return null;
    // Pedro 2026-10-13: only individuals can be PEPs; companies are left out of the rollup
    // (as in agreement_aggregates.py).
    const naturals = partyPersons.filter((p) => !p.legal);
    if (naturals.length === 0) {
      return { label: 'N/A', color: 'gray',
               breakdown: 'No individual among the parties: PEP applies to individuals only.' };
    }
    const hasPep = naturals.some((p) => p.pep_status === 'PEP');
    const hasRelated = naturals.some(
      (p) => p.pep_status === 'Membro de Família de PEP'
          || p.pep_status === 'Pessoa Próxima de PEP',
    );
    const hasNao = naturals.some((p) => p.pep_status === 'Não');
    // P4 (2026-10-09): English labels via the shared pepLabels map.
    const label = hasPep ? 'PEP'
      : hasRelated ? 'Related to PEP'
      : hasNao ? 'No'
      : 'Incomplete';
    const color = hasPep ? 'orange'
      : hasRelated ? 'yellow'
      : hasNao ? 'green'
      : 'gray';
    const breakdown = naturals
      .map((p) => `${p.display_name}: ${pepLabel(p.pep_status)}`)
      .join('\n');
    return { label, color, breakdown };
  }, [partyPersons]);

  const usAggregate = React.useMemo(() => {
    if (partyPersons.length === 0) return null;
    const hasUs = partyPersons.some((p) => p.us_person === true);
    const hasNo = partyPersons.some((p) => p.us_person === false);
    const label = hasUs ? 'Yes' : hasNo ? 'No' : 'Incomplete';
    const color = hasUs ? 'orange' : hasNo ? 'green' : 'gray';
    const breakdown = partyPersons
      .map((p) => `${p.display_name}: ${usPersonLabel(p.us_person)}`)
      .join('\n');
    return { label, color, breakdown };
  }, [partyPersons]);

  async function handleSubmit(values: FormValues) {
    // Beta #3 polish (2026-05-26): the snapshot fields are independently
    // editable so Compliance can record a KYC contact whose
    // email/phone/address are NOT in the persons table (external counsel,
    // family-office relay, etc.). Don't clear them when person_id is
    // empty; the user controls those fields directly.
    // Derive mifir_id_type from client_type when mifir_id is set
    // (Particular = NCI, Empresa = LEI). Both empty -> drop both via addOptional.
    if (values.mifir_id) {
      values.mifir_id_type = values.client_type === 'Particular' ? 'NCI' : 'LEI';
    }
    if (isNew) {
      const isProspect = createKind === 'prospect';
      if (!isProspect) {
        // #51: creating a real client permanently consumes a number (never
        // recycled). Confirm before burning it — maker-side safety for an
        // irreversible act (C-160 ethos). A Prospect needs no confirm.
        const numCode = `${values.service_type}-${String(Number(values.client_number)).padStart(3, '0')}`;
        const ok = await confirm({
          title: 'Create new client?',
          message: `This creates client ${numCode} and permanently assigns that number — it can never be reused, even if the client is later closed. (If you meant a lead, choose Prospect instead.)`,
          confirmLabel: 'Create client',
          cancelLabel: 'Back',
        });
        if (!ok) return;
      }
      const payload: AgreementCreateInput = {
        service_type: values.service_type,
        client_mnemonic: values.client_mnemonic,
        client_type: values.client_type,
        status: isProspect ? 'Prospect' : values.status,
        // client_number omitted for a Prospect; for a client, addOptional adds
        // it from values (values.client_number is '' for a Prospect → skipped).
        ...(isProspect ? {} : { client_number: Number(values.client_number) }),
      };
      addOptional(payload, values);
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): after create-then-navigate
          // (replace:true), the component re-renders with isNew=false
          // but useState(isNew) doesn't re-initialize, so editMode stays
          // true and the user sees Cancel/Save/Delete instead of Edit.
          // Flip editMode explicitly. Harmless on the change-requests
          // navigate branch (component unmounts).
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: `Your new agreement was filed. The agreement is not yet visible — Compliance must approve it first.`,
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: `Agreement ${r.entity.code_1} created`, color: 'green' });
            navigate(`/agreements/${r.entity.id}`, { replace: true });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    } else {
      // #262 (2026-06-08): detect cascade-triggering status transitions
      // and route through the cascade modal instead of the regular
      // update endpoint. The modal preview + Confirm gate is mandatory
      // per the Entity-State Framework's Rule 1 (parent→child cascade).
      // #273 (2026-06-08): casts removed — Agreement.status is now the
      // shared LifecycleStatus type, so both sides line up.
      const currentBackendStatus = (data?.status ?? '') as CascadeLifecycleStatus;
      const nextBackendStatus = (values.status ?? '') as CascadeLifecycleStatus;
      if (
        data
        && currentBackendStatus
        && nextBackendStatus
        && isCascadeTriggering(currentBackendStatus, nextBackendStatus)
      ) {
        // Stash the values so other-field edits aren't lost across the modal.
        // For v1, the cascade endpoint ships ONLY the status change; if other
        // fields are also dirty, we surface a notice and ask the user to
        // save those separately.
        setCascadePendingValues(values);
        setCascadeToStatus(nextBackendStatus);
        return;  // The modal's onConfirm picks it up from here.
      }

      // Phase 3: Inactive→Active is NOT cascade-triggering (asymmetric reversal),
      // so it bypasses the cascade check above. Route it to the faithful-
      // reactivate door (the generic PATCH no longer accepts status). The
      // reactivate modal previews the children that will be restored.
      if (
        data
        && currentBackendStatus === 'Inactive'
        && nextBackendStatus === 'Active'
      ) {
        setReactivatePendingValues(values);
        setReactivateOpen(true);
        return;  // The reactivate modal's onConfirm picks it up from here.
      }

      // Pedro's UX request (2026-06-10): the Save toast names the changed
      // fields. Computed here, against the pre-save baseline.
      const changedLabels = changedFieldLabels(values, initial, FIELD_LABELS);

      // #36 (2026-06-11): UPDATE sends ONLY changed fields — accurate op
      // labels on the pending badge and clean CR diffs for the reviewer.
      // status rides along only when it actually changed (cascade
      // transitions were intercepted above). Clear semantics unchanged:
      // the three *_next_date fields stay the only null-on-clear keys
      // (matching the old addOptional behaviour); widening that set is a
      // Pedro-owned decision (see task #36).
      const payload = diffUpdatePayload(
        values as unknown as Record<string, unknown>,
        initial as unknown as Record<string, unknown>,
        {
          exclude: new Set(['service_type']),  // create-only field
          // #52 polish 7: "Clear questionnaire" blanks the two score
          // fields — the clears must PERSIST (C-120 class; the full
          // clear-on-blank sweep is #50-f).
          nullableOnClear: new Set([
            'aml_next_date', 'mifid_suitability_next_date', 'kyc_next_date',
            'mifid_suitability_score', 'esg_score',
            // P15: the origin and its companions can be cleared (C-120 class).
            'client_origin', 'client_origin_referrer_id', 'client_origin_other',
          ]),
          transform: {
            client_number: toInt,
            kyc_contact_phone_country_code: toInt,
          },
        },
      ) as AgreementUpdateInput;
      // mifir_id and its derived type travel as a pair: if the id is in
      // the payload, the type must be too (derivation ran at the top of
      // handleSubmit; diff would omit it when client_type was unchanged).
      if (payload.mifir_id && values.mifir_id_type) {
        // FormValues stores the derived type as a plain string; the
        // derivation at the top of handleSubmit only ever writes the
        // two legal values ('NCI' | 'LEI').
        payload.mifir_id_type = values.mifir_id_type as AgreementUpdateInput['mifir_id_type'];
      }
      // #52: a staged AQ assessment commits FIRST (its own maker-checker
      // CREATE — the computed snapshot rides the CR payload). On failure
      // everything stays staged (§3g corollary 6: the form does not become
      // un-dirty until everything succeeded).
      // #52 polish 2: staged version deletes commit first (sequential —
      // each is its own maker-checker DELETE; partial failure reports and
      // keeps the remainder staged, §3g corollary 6).
      let aqDeleted = 0;
      if (aqDeletes.length > 0 && data) {
        const remaining: UUID[] = [];
        for (const rid of aqDeletes) {
          try {
            await aqApi.delete(data.id, rid);
            aqDeleted += 1;
          } catch {
            remaining.push(rid);
          }
        }
        setAqDeletes(remaining);
        queryClient.invalidateQueries({ queryKey: ['aq-responses'] });
        if (remaining.length > 0) {
          showAlert({
            color: 'red',
            title: 'Some assessment deletions failed',
            message: `${aqDeleted} deleted; ${remaining.length} still staged — try saving again.`,
          });
          return;
        }
        if (aqDeleted > 0) {
          notifications.show({
            color: 'green',
            message: `${aqDeleted} assessment version${aqDeleted === 1 ? '' : 's'} deleted`,
          });
        }
      }

      let aqOutcome: 'applied' | 'pending' | null = null;
      if (aqDraft) {
        try {
          const resp = await createAq.mutateAsync({
            answers: aqDraft.answers,
            financials: aqDraft.financials,
            notes: aqDraft.notes || null,
            // Snapshot the allocation staged on the form (server computes
            // the exceeds flag against the conversion table).
            allocation_chosen: (values.strategic_allocation || null) as
              import('../api/types').StrategicAllocation | null,
          });
          aqOutcome = (resp.pending_change_requests?.length ?? 0) > 0 ? 'pending' : 'applied';
          setAqDraft(null);
          notifications.show({
            color: aqOutcome === 'pending' ? 'orange' : 'green',
            message: aqOutcome === 'pending'
              ? 'Adequacy assessment filed for approval'
              : 'Adequacy assessment recorded',
          });
        } catch (err) {
          const e = err as ApiError;
          const d = e.body && typeof e.body === 'object'
            ? (e.body as { detail?: unknown }).detail : null;
          if (
            e.status === 409 && d && typeof d === 'object'
            && (d as { code?: string }).code === 'aq_incomplete'
          ) {
            const miss = ((d as { missing?: string[] }).missing ?? []);
            showAlert({
              color: 'orange',
              title: 'Questionnaire incomplete',
              message: `Answer the remaining item${miss.length === 1 ? '' : 's'} before saving: `
                + miss.slice(0, 8).join(', ')
                + (miss.length > 8 ? ` +${miss.length - 8} more` : ''),
            });
          } else {
            showAlert({
              color: 'red',
              title: 'Could not record the assessment',
              message: e.detail || e.message,
            });
          }
          return;  // leave the form + assessment staged
        }
      }
      // No scalar field changes to PATCH (an empty UPDATE would file a
      // pointless CR). But staged relationship ops (RM / party / doc /
      // account / policy) and/or an AQ assessment+deletes may still need
      // to commit — they don't ride the field PATCH.
      if (Object.keys(payload).length === 0) {
        const hasRelationshipOps =
          rmQ.ops.length > 0 || partyQ.ops.length > 0 || docQ.ops.length > 0
          || accountQ.ops.length > 0 || policyQ.ops.length > 0 || portfolioQ.ops.length > 0
          || !fileDocs.queue.isEmpty;
        if (aqOutcome || aqDeleted > 0) form.resetDirty();
        if (hasRelationshipOps) {
          // Bug #24 (2026-06-14): this path previously returned early and
          // silently dropped staged relationship ops whenever no scalar
          // field had changed — no PATCH, no POST, no toast, the row stuck
          // on PENDING SAVE ("zombie save"). settleSaveOutcome commits the
          // batch, shows the aggregate toast, and exits edit mode iff
          // everything settled cleanly.
          await settleSaveOutcome({
            fieldChanged: false, pending: false, clamped: [], changedLabels: [],
          });
        } else if (aqOutcome || aqDeleted > 0) {
          setEditMode(false);
        }
        return;
      }

      // #37/old-#347 (2026-06-11, Pedro's decision: warn, don't block):
      // if a pending CR already changes any field in this payload, ask
      // before filing a competing change.
      const overlap = pendingFieldOverlap(
        payload as Record<string, unknown>,
        data?.pending_change_requests ?? null,
      );
      if (overlap) {
        const ok = await confirm(overlapConfirmProps(
          overlap, (f) => FIELD_LABELS[f] ?? labelizeField(f),
        ));
        if (!ok) return;
      }
      update.mutate(payload, {
        // #326 Step E (2026-06-09): handler is now async so it can
        // chain settleSaveOutcome() — which processes the pending RM
        // op batch via maker-checker, updates pending/failed state,
        // exits edit mode iff everything succeeded, and shows the
        // aggregate toast. The field-side logic (resetDirty + clamp
        // detection + form sync) stays here; the toast composition
        // moves to settleSaveOutcome.
        onSuccess: async (r) => {
          if (r.pending) {
            form.resetDirty(values);
            await settleSaveOutcome({
              fieldChanged: true, pending: true, clamped: [], changedLabels,
            });
          } else {
            // Phase D1 v2 / D2: detect server-side clamp by comparing the
            // submitted *_next_date with what came back. If the server
            // clamped to the rule ceiling, the aggregate toast surfaces it.
            const clamped: string[] = [];
            const serverAml = r.entity.aml_next_date ?? '';
            const serverMifid = r.entity.mifid_suitability_next_date ?? '';
            const serverKyc = r.entity.kyc_next_date ?? '';
            if (values.aml_next_date && values.aml_next_date !== serverAml) {
              clamped.push('AML');
            }
            if (values.mifid_suitability_next_date && values.mifid_suitability_next_date !== serverMifid) {
              clamped.push('MiFID suitability');
            }
            if (values.kyc_next_date && values.kyc_next_date !== serverKyc) {
              clamped.push('KYC');
            }
            // Sync the form's *_next_date fields to the server response so
            // the user sees the authoritative (possibly clamped or
            // rule-defaulted) value immediately, not the value they typed.
            const synced = {
              ...values,
              aml_next_date: serverAml,
              mifid_suitability_next_date: serverMifid,
              kyc_next_date: serverKyc,
            };
            form.setValues(synced);
            form.resetDirty(synced);
            await settleSaveOutcome({
              fieldChanged: true, pending: false, clamped, changedLabels,
            });
          }
        },
        onError: (err) => {
          applyApiErrorToForm(form, err);
          // Always surface save failures via the centered alert modal so
          // the user can never silently lose work. Edge case 7 in the
          // alert consistency convention.
          showAlert({
            color: 'red',
            title: 'Save failed',
            message: err.detail || `HTTP ${err.status}: ${err.message}`,
          });
          // Task #253 (2026-06-04): suppress the page's Tier-2 inline
          // banner for THIS error. Without reset, update.error stays set
          // and feeds the apiError computed at line ~257, which would
          // render the inline banner alongside the modal — same backend
          // error surfaced twice on the same page. Edge case 7's
          // suppression contract: when a deliberate Tier-3 modal handles
          // a mutation's error, clear the mutation so the standard
          // Tier-2 plumbing doesn't double-fire.
          update.reset();
        },
      });
    }
  }

  // Beta #11 cascade-delete (2026-05-26).
  const handleCascadeConfirm = (refIds: Record<string, string[]>) => {
    if (!data) return;
    cascadeDelete.mutate(
      { id: data.id, refIds: refIds as Record<string, string[]> },
      {
        onSuccess: () => { setCascadeOpen(false); navigate('/agreements'); },
        onError: async (err) => {
          if (err instanceof ApiError && err.code === 'cascade_refs_mismatch') {
            await refs.refetch();
            showAlert({
              color: 'orange',
              title: 'Attachments changed since you opened this dialog',
              message: err.detail + ' The list has been refreshed — please review and confirm again.',
            });
          } else {
            setCascadeOpen(false);
            showAlert({
              color: 'red',
              title: 'Cascade delete failed',
              message: err instanceof ApiError ? err.detail : 'Unexpected error.',
            });
          }
        },
      },
    );
  };

  // #262 cascade-status: handlers for the cascade modal.
  function handleCascadeStatusCancel() {
    // Revert the status field in the form to the current DB value so the
    // form isn't left in a stale state implying the cascade will happen.
    if (data && cascadePendingValues) {
      form.setFieldValue('status', data.status as FormValues['status']);
    }
    setCascadeToStatus(null);
    setCascadePendingValues(null);
  }

  function handleCascadeStatusConfirm() {
    if (!data || !cascadeToStatus || !cascadeFootprint.data || !cascadePendingValues) return;
    const plan = footprintToPlan(cascadeFootprint.data);
    cascadeApply.mutate(
      { id: data.id, to_status: cascadeToStatus, cascade_plan: plan },
      {
        // #326 Step E (2026-06-09): cascade also processes pendingRMOps
        // — the user's intent on Save is to commit ALL staged changes,
        // and the cascade modal interrupted (but didn't change) the
        // queue. We preserve the cascade-specific toast/alert (it
        // carries descendant-count info that's important context) and
        // surface a SEPARATE RM-batch toast if any ops were staged.
        // setEditMode(false) only fires if RM ops also settle cleanly.
        onSuccess: async (r) => {
          setCascadeToStatus(null);
          setCascadePendingValues(null);
          form.resetDirty();
          const total =
            r.affected_direct_account_count
            + r.affected_policy_count
            + r.affected_indirect_account_count;
          if (r.change_request_status === 'pending') {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: `Your status change (and its cascade to ${total} descendant records) was filed. The live records are unchanged until Compliance approves.`,
            });
          } else {
            notifications.show({
              color: 'green',
              message: `Agreement marked ${r.new_status}. ${total} descendant record${total === 1 ? '' : 's'} updated.`,
            });
          }
          // Process any staged relationship ops alongside the cascade.
          // #327 Phase 1+2 (2026-06-09): RM + Party + IAA queues.
          // (Account-detach ops are deliberately NOT processed here —
          // the status cascade already transitions child accounts
          // server-side, and racing a staged detach against the cascade
          // would produce a confusing double-transition. Pre-existing
          // behaviour preserved by the A4 refactor; see smoke notes.
          // #21: staged POLICY ops are excluded for the same reason —
          // the agreement-status cascade transitions policies itself.)
          const opsRM = rmQ.ops;
          const opsParty = partyQ.ops;
          const opsDoc = docQ.ops;
          // Banks arc step 4: portfolio links are not touched by the status cascade, so
          // they commit here with the parties.
          const opsPortfolio = portfolioQ.ops;
          if (opsRM.length > 0 || opsParty.length > 0 || opsDoc.length > 0 || opsPortfolio.length > 0
            || !fileDocs.queue.isEmpty) {
            const rmResult = await commitPendingRMOps(opsRM);
            const partyResult = await commitPendingPartyOps(opsParty);
            const portfolioResult = await commitPendingPortfolioOps(opsPortfolio);
            const docResult = await commitPendingDocOps(opsDoc);
            // P23: staged document files ride along (settled inside commit()).
            const fileDocResult = await fileDocs.commit();
            rmQ.settle(rmResult.failedKeys);
            partyQ.settle(partyResult.failedKeys);
            portfolioQ.settle(portfolioResult.failedKeys, portfolioResult.firstError);
            docQ.settle(docResult.failedKeys);
            const parts: string[] = [];
            if (rmResult.applied > 0) parts.push(`${rmResult.applied} Named RM change${rmResult.applied === 1 ? '' : 's'} applied`);
            if (rmResult.filed > 0) parts.push(`${rmResult.filed} Named RM change${rmResult.filed === 1 ? '' : 's'} filed for approval`);
            if (rmResult.failedKeys.size > 0) parts.push(`${rmResult.failedKeys.size} Named RM change${rmResult.failedKeys.size === 1 ? '' : 's'} failed${rmResult.firstError ? ` (${rmResult.firstError})` : ''} — see Failed badges and retry`);
            if (partyResult.applied > 0) parts.push(`${partyResult.applied} party change${partyResult.applied === 1 ? '' : 's'} applied`);
            if (partyResult.filed > 0) parts.push(`${partyResult.filed} party change${partyResult.filed === 1 ? '' : 's'} filed for approval`);
            if (partyResult.failedKeys.size > 0) parts.push(`${partyResult.failedKeys.size} party change${partyResult.failedKeys.size === 1 ? '' : 's'} failed${partyResult.firstError ? ` (${partyResult.firstError})` : ''} — see Failed badges and retry`);
            if (portfolioResult.applied > 0) parts.push(`${portfolioResult.applied} portfolio link change${portfolioResult.applied === 1 ? '' : 's'} applied`);
            if (portfolioResult.filed > 0) parts.push(`${portfolioResult.filed} portfolio link change${portfolioResult.filed === 1 ? '' : 's'} filed for approval`);
            if (portfolioResult.failedKeys.size > 0) parts.push(`${portfolioResult.failedKeys.size} portfolio link change${portfolioResult.failedKeys.size === 1 ? '' : 's'} failed${portfolioResult.firstError ? ` (${portfolioResult.firstError})` : ''} — see Failed badges and retry`);
            if (docResult.applied > 0) parts.push(`${docResult.applied} IAA change${docResult.applied === 1 ? '' : 's'} applied`);
            if (docResult.filed > 0) parts.push(`${docResult.filed} IAA change${docResult.filed === 1 ? '' : 's'} filed for approval`);
            if (docResult.failedKeys.size > 0) parts.push(`${docResult.failedKeys.size} IAA change${docResult.failedKeys.size === 1 ? '' : 's'} failed${docResult.firstError ? ` (${docResult.firstError})` : ''} — see Failed badges and retry`);
            parts.push(...documentResultParts(fileDocResult));
            const totalFailed = rmResult.failedKeys.size + partyResult.failedKeys.size + docResult.failedKeys.size
              + portfolioResult.failedKeys.size + fileDocResult.failedKeys.size;
            if (parts.length > 0) {
              notifications.show({
                color: totalFailed > 0 ? 'yellow' : (rmResult.filed > 0 || partyResult.filed > 0 || docResult.filed > 0 || portfolioResult.filed > 0 || fileDocResult.filed > 0) ? 'orange' : 'green',
                message: parts.join(' · '),
                autoClose: 6000,
              });
            }
            if (totalFailed === 0) setEditMode(false);
          } else {
            setEditMode(false);
          }
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'cascade_plan_mismatch') {
            // Footprint changed between modal open and Confirm. Refetch so
            // the user sees the new plan and can re-confirm.
            cascadeFootprint.refetch();
            showAlert({
              color: 'orange',
              title: 'Cascade plan changed',
              message: 'Descendants of this Agreement changed since you opened this dialog. The preview has been refreshed — please review and confirm again.',
            });
          } else {
            showAlert({
              color: 'red',
              title: 'Cascade failed',
              message: err instanceof ApiError ? err.detail : 'Unexpected error.',
            });
          }
        },
      },
    );
  }

  // Phase 3: handlers for the faithful-reactivate modal (mirror the cascade
  // handlers; the reactivate apply restores the cascade-touched children and
  // reopens to the reason-driven target).
  function handleReactivateCancel() {
    if (data && reactivatePendingValues) {
      form.setFieldValue('status', data.status as FormValues['status']);
    }
    setReactivateOpen(false);
    setReactivatePendingValues(null);
  }

  function handleReactivateConfirm() {
    if (!data || !reactivateFootprint.data || !reactivatePendingValues) return;
    const plan = footprintToPlan(reactivateFootprint.data);
    reactivateApply.mutate(
      { id: data.id, reactivate_plan: plan },
      {
        onSuccess: async (r) => {
          setReactivateOpen(false);
          setReactivatePendingValues(null);
          // Sync the form to the ACTUAL reopen target — may be Prospect for a
          // lost-lead, not the 'Active' the pill toggled to.
          form.setFieldValue('status', r.new_status as FormValues['status']);
          form.resetDirty();
          const total =
            r.affected_direct_account_count
            + r.affected_policy_count
            + r.affected_indirect_account_count;
          if (r.change_request_status === 'pending') {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: `Your reactivation (reopening to ${r.new_status}, restoring ${total} child record${total === 1 ? '' : 's'}) was filed. The live records are unchanged until Compliance approves.`,
            });
          } else {
            notifications.show({
              color: 'green',
              message: `Agreement reopened to ${r.new_status}. ${total} child record${total === 1 ? '' : 's'} restored.`,
            });
          }
          // Commit any relationship ops staged alongside the reactivation, so
          // they aren't silently dropped (§3g / C-159). Mirrors the cascade
          // confirm; account/policy ops are excluded (the reactivate already
          // transitions children server-side).
          const opsRM = rmQ.ops;
          const opsParty = partyQ.ops;
          const opsDoc = docQ.ops;
          // Banks arc step 4: portfolio links are not touched by the status cascade, so
          // they commit here with the parties.
          const opsPortfolio = portfolioQ.ops;
          if (opsRM.length > 0 || opsParty.length > 0 || opsDoc.length > 0 || opsPortfolio.length > 0
            || !fileDocs.queue.isEmpty) {
            const rmResult = await commitPendingRMOps(opsRM);
            const partyResult = await commitPendingPartyOps(opsParty);
            const portfolioResult = await commitPendingPortfolioOps(opsPortfolio);
            const docResult = await commitPendingDocOps(opsDoc);
            // P23: staged document files ride along (settled inside commit()).
            const fileDocResult = await fileDocs.commit();
            rmQ.settle(rmResult.failedKeys);
            partyQ.settle(partyResult.failedKeys);
            portfolioQ.settle(portfolioResult.failedKeys, portfolioResult.firstError);
            docQ.settle(docResult.failedKeys);
            const parts: string[] = [];
            if (rmResult.applied > 0) parts.push(`${rmResult.applied} Named RM change${rmResult.applied === 1 ? '' : 's'} applied`);
            if (rmResult.filed > 0) parts.push(`${rmResult.filed} Named RM change${rmResult.filed === 1 ? '' : 's'} filed for approval`);
            if (rmResult.failedKeys.size > 0) parts.push(`${rmResult.failedKeys.size} Named RM change${rmResult.failedKeys.size === 1 ? '' : 's'} failed${rmResult.firstError ? ` (${rmResult.firstError})` : ''} — see Failed badges and retry`);
            if (partyResult.applied > 0) parts.push(`${partyResult.applied} party change${partyResult.applied === 1 ? '' : 's'} applied`);
            if (partyResult.filed > 0) parts.push(`${partyResult.filed} party change${partyResult.filed === 1 ? '' : 's'} filed for approval`);
            if (partyResult.failedKeys.size > 0) parts.push(`${partyResult.failedKeys.size} party change${partyResult.failedKeys.size === 1 ? '' : 's'} failed${partyResult.firstError ? ` (${partyResult.firstError})` : ''} — see Failed badges and retry`);
            if (portfolioResult.applied > 0) parts.push(`${portfolioResult.applied} portfolio link change${portfolioResult.applied === 1 ? '' : 's'} applied`);
            if (portfolioResult.filed > 0) parts.push(`${portfolioResult.filed} portfolio link change${portfolioResult.filed === 1 ? '' : 's'} filed for approval`);
            if (portfolioResult.failedKeys.size > 0) parts.push(`${portfolioResult.failedKeys.size} portfolio link change${portfolioResult.failedKeys.size === 1 ? '' : 's'} failed${portfolioResult.firstError ? ` (${portfolioResult.firstError})` : ''} — see Failed badges and retry`);
            if (docResult.applied > 0) parts.push(`${docResult.applied} IAA change${docResult.applied === 1 ? '' : 's'} applied`);
            if (docResult.filed > 0) parts.push(`${docResult.filed} IAA change${docResult.filed === 1 ? '' : 's'} filed for approval`);
            if (docResult.failedKeys.size > 0) parts.push(`${docResult.failedKeys.size} IAA change${docResult.failedKeys.size === 1 ? '' : 's'} failed${docResult.firstError ? ` (${docResult.firstError})` : ''} — see Failed badges and retry`);
            parts.push(...documentResultParts(fileDocResult));
            const totalFailed = rmResult.failedKeys.size + partyResult.failedKeys.size + docResult.failedKeys.size
              + portfolioResult.failedKeys.size + fileDocResult.failedKeys.size;
            if (parts.length > 0) {
              notifications.show({
                color: totalFailed > 0 ? 'yellow' : (rmResult.filed > 0 || partyResult.filed > 0 || docResult.filed > 0 || portfolioResult.filed > 0 || fileDocResult.filed > 0) ? 'orange' : 'green',
                message: parts.join(' · '),
                autoClose: 6000,
              });
            }
            if (totalFailed === 0) setEditMode(false);
          } else {
            setEditMode(false);
          }
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'cascade_plan_mismatch') {
            reactivateFootprint.refetch();
            showAlert({
              color: 'orange',
              title: 'Reactivate preview changed',
              message: 'The records under this Agreement changed since you opened this dialog. The preview has been refreshed — please review and confirm again.',
            });
          } else {
            showAlert({
              color: 'red',
              title: 'Reactivate failed',
              message: err instanceof ApiError ? err.detail : 'Unexpected error.',
            });
          }
        },
      },
    );
  }

  // #51 / #26 (2026-06-15): promote a Prospect → Active. Extracted from the
  // header so the button renders in BOTH the Read-mode (greyed) and Edit-mode
  // (enabled) slots — the §26 consistency rule: consequential actions are
  // reachable only in Edit mode. IAA-gated (server enforces too). Its own
  // confirm modal IS the commit (§3g Pattern B): promotion allocates a
  // permanent, irreversible number, so it is never batched into Save.
  const handlePromote = async () => {
    if (!data) return;
    if (!data.iaa_initial_date) {
      showAlert({
        color: 'orange',
        title: 'Original IAA required',
        message: 'Register the Original IAA (on the KYC tab) before promoting this Prospect to Active.',
      });
      return;
    }
    let label = 'the next available number';
    try {
      const { next } = await agreementsApi.nextClientNumber(data.service_type);
      label = `${data.service_type}-${String(next).padStart(3, '0')}`;
    } catch { /* fall back to the generic wording */ }
    const ok = await confirm({
      title: 'Promote to Active?',
      message: `This assigns permanent number ${label}, activates the client, and applies full KYC / AML / MiFID / IAA requirements. The number can never be reused.`,
      confirmLabel: 'Promote',
      cancelLabel: 'Back',
    });
    if (!ok) return;
    try {
      const r = await agreementsApi.promote(data.id);
      if (r.pending) {
        showAlert({
          color: 'orange',
          title: 'Promotion filed for approval',
          message: 'Compliance must approve before the client is activated.',
        });
        navigate(`/change-requests/${r.changeRequestId}`);
      } else {
        notifications.show({ message: `Promoted to ${r.entity.code_1}`, color: 'green' });
        queryClient.invalidateQueries({ queryKey: agreementsKeys.all });
      }
    } catch (e) {
      showAlert({
        color: 'red',
        title: 'Could not promote',
        message: e instanceof ApiError ? e.detail : 'Unexpected error.',
      });
    }
  };

  return (
    <Container size="xl">
      <CascadeStatusModal
        opened={cascadeToStatus !== null}
        onClose={handleCascadeStatusCancel}
        footprint={cascadeFootprint.data ?? null}
        loading={cascadeFootprint.isLoading || cascadeApply.isPending}
        onConfirm={handleCascadeStatusConfirm}
        agreementLabel={data ? (data.client_mnemonic ?? `#${data.client_number}`) : undefined}
        pendingApproval={false /* default copy; backend determines actual route */}
      />
      {/* Phase 3: faithful-reactivate confirm (Inactive → reason-driven target). */}
      <CascadeStatusModal
        mode="reactivate"
        opened={reactivateOpen}
        onClose={handleReactivateCancel}
        footprint={reactivateFootprint.data ?? null}
        loading={reactivateFootprint.isLoading || reactivateApply.isPending}
        onConfirm={handleReactivateConfirm}
        agreementLabel={data ? (data.client_mnemonic ?? `#${data.client_number}`) : undefined}
        pendingApproval={false}
      />
      <CascadeDeleteModal
        opened={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        entityNoun="Agreement"
        entityLabel={data ? (data.client_mnemonic ?? `#${data.client_number}`) : undefined}
        report={refs.data}
        onConfirm={handleCascadeConfirm}
        loading={cascadeDelete.isPending}
      />
      <Stack>
        {!isNew && data && (
          <EmailDraftModal opened={emailOpen} onClose={() => setEmailOpen(false)}
            recordType="agreement" recordId={data.id} />
        )}
        <StickyDetailHeader
          editing={editMode}
          actions={!isNew && data && !isDeleted && canEmailDrafts(me?.role) ? (
            <Tooltip withArrow multiline w={280}
              label="Prepares a draft email to this agreement's parties, with files attached. It opens in Outlook: check it there and press Send.">
              <Button variant="light" leftSection={<IconMail size={16} />} onClick={() => setEmailOpen(true)}>
                Email
              </Button>
            </Tooltip>
          ) : null}
          title={isNew ? 'New agreement' : (
            data
              ? (data.client_mnemonic ? `${data.code_1} — ${data.client_mnemonic}` : data.code_1)
              : '...'
          )}
          backOnClick={() => confirmDiscard(() => {
            // Bug fix (2026-06-09 during A1.f Test 6): clear all dirty
            // state BEFORE navigating so the passive useBlocker (from
            // DirtyFormProvider, #258) doesn't see lingering dirtiness
            // on the navigate and re-trigger the discard modal. Mirrors
            // the Cancel button's clearing logic below.
            form.setValues(initial);
            form.resetDirty(initial);
            clearAllQueues();
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            // Task #245 (2026-06-02): hide Edit on deleted records. You can't
            // mutate a row that's been soft-deleted; the only legal action is
            // Restore (rendered in the `destructive` slot below). When isDeleted
            // is true, primary slot renders nothing.
            !editMode && !isDeleted ? (
              <Group gap="xs">
                {/* #51 / #26 (2026-06-15): promote a Prospect → Active. Now
                    Edit-gated for consistency (§3h / §26 rule): greyed with a
                    tooltip in Read mode, enabled in Edit mode (rendered in the
                    Edit-mode slot below). Server enforces the IAA gate too. */}
                {data?.status === 'Prospect' && activeTab === 'identity' && (
                  <DisabledTooltipButton
                    color="grape"
                    leftSection={<IconShieldCheck size={16} />}
                    disabledReason={canPromote ? "Enter Edit mode to promote this Prospect to Active" : "You do not have permission to promote agreements"}
                    onClick={handlePromote}
                  >
                    Promote to Active
                  </DisabledTooltipButton>
                )}
                {/* Lifecycle redesign (2026-06-15, Pedro): the dedicated
                    "Inactivate (lost lead)" button was removed. A Prospect is
                    now inactivated the SAME way as any agreement — Edit mode →
                    flip the status pill to Inactive → CascadeStatusModal gate →
                    apply_cascade stamps inactive_reason = LOST_LEAD server-side.
                    One unified, Edit-gated inactivation affordance for every
                    state (§3g Pattern A, §3h consistency). "Promote to Active"
                    stays a button: it's a distinct IAA-gated, number-allocating
                    flow, not a simple status flip. */}
                <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              </Group>
            ) : !editMode && isDeleted ? null : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      // #326 Step F (2026-06-09): Cancel/Discard MUST
                      // also clear pending op queues alongside the form
                      // reset — they're part of the same Edit-mode
                      // transaction. The confirmDiscard guard already
                      // shows the modal if anything is dirty (form OR
                      // any pending queue) thanks to the aggregated
                      // dirty signal.
                      // #327 Phase 1+2 + #377 A1.d: every staged queue
                      // clears alongside the form (clearAllQueues).
                      form.setValues(initial);
                      form.resetDirty(initial);
                      clearAllQueues();
                      setAqDraft(null);   // #52: staged assessment discards too
                      setAqDeletes([]);   // #52 polish 2: staged deletes too
                      // #51: Cancel on a NEW agreement has nothing to drop back
                      // to (there's no saved record), so leave the page rather
                      // than stranding the user on a read-only create form.
                      if (isNew) { navigate(-1); } else { setEditMode(false); }
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="agreement-form" type="submit"
                  loading={create.isPending || update.isPending}
                  // #326 Step F + #327 Phase 1+2 + #377 A1.d (2026-06-09):
                  // enable Save when any pending op queue has items, even
                  // if no field is dirty — Pattern A means staged changes
                  // commit via Save just like field edits.
                  disabled={(isNew && createKind === null) || (!form.isDirty() && !isNew
                    && rmQ.isEmpty
                    && partyQ.isEmpty
                    && docQ.isEmpty
                    && accountQ.isEmpty
                    && policyQ.isEmpty
                    && portfolioQ.isEmpty
                    && fileDocs.queue.isEmpty
                    && aqDraft === null
                    && aqDeletes.length === 0)}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
                {/* #26 (2026-06-15): Promote is reachable only in Edit mode
                    (consistency rule). It commits via its own confirm modal —
                    NOT the Save button — because it allocates a permanent,
                    irreversible number (§3g Pattern B). */}
                {!isNew && data?.status === 'Prospect' && activeTab === 'identity' && (
                  <DisabledTooltipButton
                    color="grape"
                    leftSection={<IconShieldCheck size={16} />}
                    disabledReason={canPromote ? null : "You do not have permission to promote agreements"}
                    onClick={handlePromote}
                  >
                    Promote to Active
                  </DisabledTooltipButton>
                )}
              </Group>
            )
          }
          destructive={
            // #7 (2026-06-16): entity Delete only on the Identity tab —
            // relationship tabs expose only per-row sever icons (§3h).
            activeTab !== 'identity' ? null
            : !isNew && data && !isDeleted ? (
              /* #26 (2026-06-15): Delete is Edit-gated for consistency — greyed
                 with a tooltip in Read mode, enabled in Edit mode. It commits
                 via its own confirm modal (§3g Pattern B), not the Save button,
                 because it removes the record. */
              <DisabledTooltipButton variant="light" color="red" leftSection={<IconTrash size={16} />}
                disabledReason={editMode ? null : 'Enter Edit mode to delete'}
                enabledTooltip="Deletes this agreement record"
                onClick={async () => {
                  const result = await refs.refetch();
                  const report = result.data;
                  if (!report || !report.has_active_references) {
                    confirmDelete('agreement', () => del.mutate(data.id, {
                      onSuccess: () => navigate('/agreements'),
                      onError: (err) => showAlert({
                        color: 'red', title: 'Delete failed',
                        message: err instanceof ApiError ? err.detail : 'Unexpected error.',
                      }),
                    }));
                  } else {
                    setCascadeOpen(true);
                  }
                }}
                loading={del.isPending || refs.isFetching}>Delete</DisabledTooltipButton>
            ) : !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        {!isNew && data && <IncompleteBanner entity_type="AGREEMENT" id={data.id} onOpenTab={setActiveTab} />}

        {/* Phase D2 summary bar - identity + KYC-relevant fields at a glance.
            Investor Nature, Residence, MIFIR are read-only here (editable on
            the KYC tab). The IAA / close date is derived from agreement_documents. */}
        {!isNew && data && (
          <Paper withBorder p="md" radius="sm">
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="lg" wrap="wrap">
                <SummaryItem label="Baluarte Code" value={data.code_1} mono bold />
                <SummaryItem label="Mnemonic" value={data.client_mnemonic} mono />
                <SummaryItem label="Type" value={data.client_type} />
                <SummaryItem label="Investor nature" value={mifidNatureLabel(data.mifid_investor_nature)} />
                <SummaryItem label="Residence" value={mifidResidenceLabel(data.mifid_residence)} />
                <SummaryItem
                  label={data.mifir_id_type ?? 'MIFIR'}
                  value={data.mifir_id ?? '—'}
                  mono
                />
                <SummaryItem label={summaryIaaLabel} value={summaryIaaDate ?? 'Pending IAA'} />
                {pepAggregate && (
                  <SummaryBadge label="PEP (parties)"
                    badge={pepAggregate.label} color={pepAggregate.color}
                    tooltip={pepAggregate.breakdown} />
                )}
                {usAggregate && (
                  <SummaryBadge label="US Person (parties)"
                    badge={usAggregate.label} color={usAggregate.color}
                    tooltip={usAggregate.breakdown} />
                )}
                {/* #276 polish (2026-06-08): Named RM chip in the summary
                    bar — shows Primary RM(s) at a glance without opening
                    the Named RMs tab. Falls back to em-dash if none. */}
                <NamedRMSummaryChip agreementId={data.id} />
              </Group>
              {/* B1/#35 (2026-06-11, old #363): pending-CR state in the
                  header — visible from any tab, next to the status pill. */}
              <Group gap="xs" wrap="nowrap">
              <PendingCRBadge crs={data.pending_change_requests} />
              {/* P47: the pill explains what its status means, plus how to change it
                  (one tooltip; this replaced the native title hint). */}
              <StatusExplained entity="agreement" status={isDeleted ? 'Deleted' : form.values.status}
                extra={
                  isDeleted ? undefined :
                  !editMode ? 'Enter Edit mode (button at top right) to change the status.' :
                  form.values.status === 'Prospect' ? 'Click to inactivate this Prospect as a lost lead (or use "Promote to Active" to convert it).' :
                  'Click to change the status.'
                }>
              <Badge size="lg" variant="filled"
                color={
                  isDeleted ? 'red'
                    : form.values.status === 'Active' ? 'green'
                    : form.values.status === 'Inactive' ? 'yellow'
                    : form.values.status === 'Prospect' ? 'grape'  // #51
                    : 'gray'  // Closed
                }
                style={{ cursor: (isDeleted || !editMode) ? 'default' : 'pointer' }}
                onClick={async () => {
                  if (isDeleted) return;
                  // §2 standing rule (CLAUDE.md, 2026-06-08): no mutations
                  // from a read-only view. The status pill is a quick-flip
                  // affordance, but it's still a mutation — gate it on
                  // Edit mode so the user has explicitly opted into
                  // "I am editing this record."
                  if (!editMode) return;
                  // §3g (2026-06-08): status toggle is a scalar field edit
                  // and follows Pattern A. Clicking just updates form state
                  // (marks dirty). Persistence and the cascade modal fire
                  // on Save Changes, same as any other field. The mnemonic
                  // pre-check stays at click time for fast feedback when
                  // attempting to reactivate.
                  // Lifecycle redesign (2026-06-15, Pedro): a Prospect's pill
                  // flips ONLY to Inactive (the "lost lead" path); Active is
                  // reserved for the IAA-gated "Promote to Active" button. The
                  // pill is now the single, Edit-gated inactivation affordance
                  // for every state — the dedicated lost-lead button is gone.
                  const next = form.values.status === 'Prospect'
                    ? 'Inactive'
                    : form.values.status === 'Active' ? 'Inactive' : 'Active';
                  if (next === 'Active' && form.values.client_mnemonic) {
                    try {
                      const r = await agreementsApi.checkMnemonic(form.values.client_mnemonic);
                      if (!r.available) {
                        showAlert({
                          color: 'red',
                          title: 'Cannot reactivate',
                          message: `Mnemonic ${form.values.client_mnemonic} is now in use by Active agreement ${r.conflicting_code_1 ?? '(unknown)'}. Set the other to Inactive or rename one before reactivating this client.`,
                        });
                        return;
                      }
                    } catch { /* */ }
                  }
                  form.setFieldValue('status', next);
                }}>
                {isDeleted ? 'DELETED' : form.values.status}
              </Badge>
              </StatusExplained>
              {/* Lifecycle redesign Phase 1 (§3j): name WHY an agreement is
                  Inactive — Suspended / Ex-client / Lost lead. */}
              {data?.status === 'Inactive' && data.inactive_reason && (
                <Badge size="lg" variant="outline" color="gray">
                  {AGREEMENT_INACTIVE_REASON_LABELS[data.inactive_reason]}
                </Badge>
              )}
              </Group>
            </Group>
          </Paper>
        )}

        {/* §3j (2026-06-10, Pedro's smoke — CIIF-005 "dead Save button"):
            Mantine runs validation BEFORE handleSubmit; when a field on
            ANOTHER tab fails (e.g. a 5-letter mnemonic on Identity while
            the user is on Insurance), the failure was silent and Save
            appeared dead. The second onSubmit argument surfaces it: name
            the fields, jump to the tab that holds the first one. */}
        {/* noValidate (#27 round 2): defensive — no required-marked inputs
            here today, but a future one would let browser-native validation
            silently swallow the submit (see AccountDetailPage). */}
        <form id="agreement-form" noValidate onSubmit={form.onSubmit(handleSubmit, (errors) => {
          const fields = Object.keys(errors);
          const labels = fields.map((f) => FIELD_LABELS[f] ?? f.replace(/_/g, ' '));
          // The one field → tab map (MissingFields.FIELD_TABS) holds the
          // validated fields too.
          const firstTab = FIELD_TABS.AGREEMENT[fields[0]] ?? 'identity';
          setActiveTab(firstTab);
          showAlert({
            color: 'red',
            title: 'Cannot save — fix the highlighted field(s) first',
            message: `${labels.join(', ')}: ${fields.map((f) => String(errors[f])).join('; ')}`,
          });
        })}>
          <Stack pos="relative">
            <LoadingOverlay visible={isLoading} />
            <Tabs value={activeTab} onChange={setActiveTab} keepMounted>
              <Tabs.List>
                {/* Beta v2 polish G (2026-05-26): Identity tab now visible
                    on existing agreements too, so the Type field can be corrected
                    after an operator error at onboarding. */}
                <Tabs.Tab value="identity" leftSection={<IconClipboardList size={14} />}>Identity</Tabs.Tab>
                {!isNew && <Tabs.Tab value="kyc" leftSection={<IconIdBadge2 size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.kyc} />}>KYC</Tabs.Tab>}
                <Tabs.Tab value="mifid" leftSection={<IconShieldCheck size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.mifid} />}>MiFID</Tabs.Tab>
                <Tabs.Tab value="aml" leftSection={<IconAlertTriangle size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.aml} />}>AML</Tabs.Tab>
                {!isNew && data && <Tabs.Tab value="fees" leftSection={<IconReceipt2 size={14} />}
                  rightSection={<TabMissingCount count={missing.tabCounts.fees} />}>Fees</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="parties" leftSection={<IconUsers size={14} />}>Parties</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="accounts" leftSection={<IconBuildingBank size={14} />}>Accounts</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="insurance" leftSection={<IconShieldCheck size={14} />}>UL Ins</Tabs.Tab>}
                {!isNew && data && can('portfolios.read') && (
                  <Tabs.Tab value="portfolios" leftSection={<IconBriefcase size={14} />}>Portfolios</Tabs.Tab>
                )}
                {/* TRADE-LIST (Pedro 2026-10-25): the trades of this agreement's portfolios. */}
                {!isNew && data && can('portfolios.read') && (
                  <Tabs.Tab value="trades" leftSection={<IconArrowsExchange size={14} />}>
                    Trades
                  </Tabs.Tab>
                )}
                {/* Feature 1 (#157, 2026-05-28): Interactions tab. */}
                {!isNew && data && <Tabs.Tab value="interactions" leftSection={<IconClipboardList size={14} />}>Notes</Tabs.Tab>}
                {/* Feature 4 (2026-05-28): Tasks tab. */}
                {!isNew && data && <Tabs.Tab value="tasks" leftSection={<IconChecklist size={14} />}>Tasks</Tabs.Tab>}
                {!isNew && data && <Tabs.Tab value="aum-return" leftSection={<IconChartHistogram size={14} />}>AuM &amp; R%</Tabs.Tab>}
                {/* #50 stage 2 (2026-06-12): Documents tab — one table,
                    On file + Export new; v1 live for the IAA row. */}
                {!isNew && data && <Tabs.Tab value="documents" leftSection={<IconFileText size={14} />}>Docs</Tabs.Tab>}
              </Tabs.List>

              <Tabs.Panel value="identity" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                {/* Identity card: present on both new and existing
                    agreements. service_type / client_number / client_mnemonic
                    feed code_1's GENERATED expression and the mnemonic-
                    unique-among-Activo constraint — disabled in edit on
                    existing rows. client_type + status remain editable. */}
                <Fieldset legend="Agreement identity">
                    {/* #51: forced New-client / Prospect choice on create. The
                        number field is gated on the choice, so a Prospect never
                        even shows a number (it gets CIIF-PRO until promotion),
                        and the user can't reach a real number without choosing
                        "New client". */}
                    {isNew && (
                      <Stack gap={4} mb="md">
                        <Text size="sm" fw={600}>What are you creating?</Text>
                        <SegmentedControl
                          data={[
                            { label: 'New client', value: 'client' },
                            { label: 'Prospect', value: 'prospect' },
                          ]}
                          value={createKind ?? ''}
                          onChange={(v) => {
                            const kind = v as 'client' | 'prospect';
                            setCreateKind(kind);
                            form.setFieldValue('status', kind === 'prospect' ? 'Prospect' : 'Active');
                            if (kind === 'prospect') form.setFieldValue('client_number', '');
                          }}
                          styles={{ root: { alignSelf: 'flex-start' } }}
                        />
                        {createKind === null && (
                          <Text size="xs" c="dimmed">
                            Pick one to continue. A Prospect has no client number
                            (provisional code CIIF-PRO) until it is promoted to Active.
                          </Text>
                        )}
                      </Stack>
                    )}
                    {(!isNew || createKind !== null) && (
                    <Grid>
                      <Grid.Col span={3}>
                        <Select label="Service" data={SERVICE_OPTS} {...form.getInputProps('service_type')}
                          allowDeselect={false} disabled={!isNew} />
                      </Grid.Col>
                      {(isNew && createKind === 'prospect') ? (
                        <Grid.Col span={3}>
                          <TextInput label="Client number"
                            value="— CIIF-PRO until promotion" disabled readOnly />
                        </Grid.Col>
                      ) : (!isNew && data != null && data.client_number == null) ? (
                        <Grid.Col span={3}>
                          {/* Lifecycle redesign (2026-06-15, Pedro's smoke): an
                              existing agreement with no number — a Prospect, or a
                              lost-lead reopened to Prospect — shows its provisional
                              code, NOT an editable NumberInput. The NumberInput
                              was surfacing a phantom "1" default for these (the
                              "split-mind" state) and inviting edits to a field
                              that has no number until promotion. */}
                          <TextInput label="Client number"
                            value={`— ${data.code_1 ?? 'no number'} until promotion`}
                            disabled readOnly />
                        </Grid.Col>
                      ) : (
                        <Grid.Col span={3}>
                          <NumberInput label="Client number" min={1} max={999}
                            disabled={!isNew}
                            {...form.getInputProps('client_number')} />
                        </Grid.Col>
                      )}
                      <Grid.Col span={3}>
                        <TextInput
                          label="Mnemonic (6 letters)"
                          disabled={!isNew}
                          {...form.getInputProps('client_mnemonic')}
                          maxLength={6}
                          style={{ textTransform: 'uppercase' }}
                          rightSection={
                            <ActionIcon variant="subtle" color="azul"
                              aria-label="Suggest mnemonic from holder name"
                              onClick={() => setMnemonicModalOpen(true)}>
                              <IconWand size={16} />
                            </ActionIcon>
                          }
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Select label="Client type" data={CLIENT_OPTS} {...form.getInputProps('client_type')}
                          allowDeselect={false} />
                      </Grid.Col>
                      {!(isNew && createKind === 'prospect') && (
                        <Grid.Col span={3}>
                          <Select label="Status" data={STATUS_OPTS} {...form.getInputProps('status')}
                            allowDeselect={false} />
                        </Grid.Col>
                      )}
                    </Grid>
                    )}
                  </Fieldset>
                  {/* Team feedback P15 (migration 0099): how the client came to us.
                      The referrer applies only to "Referred by", the free text only to
                      "Other"; changing the origin clears the other one here AND on the
                      server (model validator + DB CHECKs). Pattern A: saved with Save. */}
                  {(!isNew || createKind !== null) && (
                  <Fieldset legend="Client origin" mt="md">
                    <Grid>
                      <Grid.Col span={3}>
                        <Select label="How the client came to us" placeholder="Not recorded"
                          data={ORIGIN_OPTS} clearable
                          {...form.getInputProps('client_origin')}
                          onChange={(v) => form.setValues({
                            ...form.values,
                            client_origin: v ?? '',
                            client_origin_referrer_id:
                              v === 'Referred by' ? form.values.client_origin_referrer_id : '',
                            client_origin_other: v === 'Other' ? form.values.client_origin_other : '',
                          })} />
                      </Grid.Col>
                      {form.values.client_origin === 'Referred by' && (
                        <Grid.Col span={5}>
                          <PersonPicker label="Referred by" placeholder="Pick the referring person…"
                            disabled={!editMode}
                            value={form.values.client_origin_referrer_id || null}
                            onChange={(v) => form.setFieldValue('client_origin_referrer_id', v ?? '')} />
                        </Grid.Col>
                      )}
                      {form.values.client_origin === 'Other' && (
                        <Grid.Col span={5}>
                          <TextInput label="Specify" placeholder="e.g. Industry conference"
                            {...form.getInputProps('client_origin_other')} />
                        </Grid.Col>
                      )}
                    </Grid>
                  </Fieldset>
                  )}
                </fieldset>
              </Tabs.Panel>

              {/* Phase D2: KYC tab — investor identity (Investor nature,
                  Residence, MIFIR), agreement-level KYC review dates, and
                  the IAA-document history table. KYC is the umbrella event
                  that aggregates Persons + Accounts + Insurance + documents. */}
              <Tabs.Panel value="kyc" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                <Stack>
                  <Fieldset legend="Investor identity">
                    <Grid>
                      <Grid.Col span={3}>
                        <Select label="Investor nature" data={NATURE_OPTS}
                          {...form.getInputProps('mifid_investor_nature')} {...hl('mifid_investor_nature')} />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Select label="Residence" data={RESIDENCE_OPTS}
                          {...form.getInputProps('mifid_residence')} {...hl('mifid_residence')} />
                      </Grid.Col>
                      {isParticular ? (
                        <Grid.Col span={3}>
                          <TextInput label="MIFIR NCI"
                            placeholder="Natural-person ID"
                            {...form.getInputProps('mifir_id')} {...hl('mifir_id')}
                            rightSection={
                              <Tooltip label="Suggest from a party's NIF" withArrow>
                                <ActionIcon variant="subtle" color="azul"
                                  aria-label="Suggest the MIFIR NCI from a party's NIF"
                                  onClick={() => setMifirModalOpen(true)}>
                                  <IconWand size={16} />
                                </ActionIcon>
                              </Tooltip>
                            } />
                        </Grid.Col>
                      ) : (
                        <Grid.Col span={3}>
                          <TextInput label="MIFIR LEI"
                            placeholder="Legal Entity Identifier"
                            maxLength={20}
                            {...form.getInputProps('mifir_id')} {...hl('mifir_id')}
                            rightSection={
                              <Tooltip label="Suggest from a party's LEI" withArrow>
                                <ActionIcon variant="subtle" color="azul"
                                  aria-label="Suggest the MIFIR LEI from a party"
                                  onClick={() => setMifirModalOpen(true)}>
                                  <IconWand size={16} />
                                </ActionIcon>
                              </Tooltip>
                            } />
                        </Grid.Col>
                      )}
                      {/* #50 stage 2: defaults the Export-New modal's language picker. */}
                      <Grid.Col span={3}>
                        <Select label="Preferred language" data={LANG_OPTS}
                          allowDeselect={false}
                          {...form.getInputProps('preferred_language')} />
                      </Grid.Col>
                    </Grid>
                  </Fieldset>

                  {/* Beta #3 (2026-05-26): KYC contact snapshot. The fields
                      below are a frozen-in-time copy of the picked Person's
                      contact info; subsequent edits to that Person do not
                      auto-propagate. Click "Snapshot from picked Person" to
                      refresh. */}
                  <Fieldset legend="KYC contact (snapshot)">
                    <Stack gap="xs">
                      <Group align="end" gap="sm">
                        <div style={{ flex: 1 }}>
                          <Select
                            label="Designated KYC contact"
                            description="Restricted to Persons that are currently parties on this Agreement."
                            placeholder={kycContactOptions.length === 0
                              ? "No parties attached to this agreement yet"
                              : "Pick a party..."}
                            data={kycContactOptions}
                            value={form.values.kyc_contact_person_id || null}
                            onChange={(v) => form.setFieldValue('kyc_contact_person_id', v ?? '')}
                            clearable
                            // NOT searchable: options are scoped to this agreement's parties
                            // (a handful), so a search box adds nothing and creates a UX trap —
                            // typing filters an ephemeral search string that is NOT the stored
                            // value, so it never marks the form dirty (looked like "can't change /
                            // reverts / navigate-away unguarded"; 2026-07-05 smoke). Click-to-pick
                            // makes it unambiguously a picker; the name is the picked Person's
                            // identity (not free-text) — email/phone/address below are the editable
                            // snapshot fields.
                            disabled={!editMode || kycContactOptions.length === 0}
                          />
                        </div>
                        <Button variant="light" size="sm"
                          disabled={!form.values.kyc_contact_person_id || !editMode}
                          onClick={async () => {
                            const pid = form.values.kyc_contact_person_id;
                            if (!pid) return;
                            try {
                              const p = await personsApi.get(pid);
                              // Feedback P3 (2026-10-09): kind-aware, like the billing
                              // snapshot (account_billing._person_billing_fields). A Legal
                              // Person keeps its address in registered_office_* and often
                              // has only a landline, so the old fiscal/mobile-only copy
                              // came out empty for companies.
                              const legal = p.kind === 'LEGAL';
                              const address = legal
                                ? [p.registered_office_address, p.registered_office_postal_code,
                                   p.registered_office_city, p.registered_office_state,
                                   p.registered_office_country]
                                : [p.fiscal_address, p.fiscal_address_postal_code,
                                   p.fiscal_address_city, p.fiscal_address_state,
                                   p.fiscal_address_country];
                              const useMobile = !!p.mobile_num;
                              const phoneCc = useMobile ? p.mobile_country_code : p.fixed_phone_country_code;
                              form.setValues({
                                ...form.values,
                                kyc_contact_email: p.email ?? '',
                                kyc_contact_phone_country_code: phoneCc != null ? String(phoneCc) : '',
                                kyc_contact_phone_num: (useMobile ? p.mobile_num : p.fixed_phone_num) ?? '',
                                kyc_contact_address: address.filter(Boolean).join(', '),
                              });
                            } catch (e) {
                              showAlert({
                                color: 'red',
                                title: 'Could not load Person',
                                message: e instanceof ApiError
                                  ? `${e.detail} Pick a different Person or refresh the page.`
                                  : 'Pick a different Person or refresh the page.',
                              });
                            }
                          }}
                        >
                          Snapshot from picked Person
                        </Button>
                      </Group>
                      <Grid>
                        <Grid.Col span={6}>
                          <TextInput label="Email (snapshot)"
                            {...form.getInputProps('kyc_contact_email')} />
                        </Grid.Col>
                        <Grid.Col span={2}>
                          <TextInput label="Phone country" placeholder="351"
                            {...form.getInputProps('kyc_contact_phone_country_code')} />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <TextInput label="Phone number"
                            {...form.getInputProps('kyc_contact_phone_num')} />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <TextInput label="Address (snapshot)"
                            description="Single line; composed from the Person's fiscal address block at snapshot time."
                            {...form.getInputProps('kyc_contact_address')} />
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Fieldset>

                  <Fieldset legend="KYC review">
                    <Grid>
                      <Grid.Col span={3}>
                        <TextInput
                          label="Last KYC" type="date"
                          {...form.getInputProps('kyc_last_date')} {...hl('kyc_last_date')}
                          onChange={(e) => {
                            form.setFieldValue('kyc_last_date', e.currentTarget.value);
                            // When Last changes, clear Next so backend defaults
                            // it from new_last + rule_interval.
                            form.setFieldValue('kyc_next_date', '');
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <TextInput
                          label="Next KYC"
                          type="date"
                          {...form.getInputProps('kyc_next_date')} {...hl('kyc_next_date')}
                        />
                        {data?.kyc_review_is_manual_override
                          ? <Text size="xs" c="dimmed" mt={2}>manual override</Text>
                          : <Text size="xs" c="dimmed" mt={2}>defaults to last + rule; edit down to tighten</Text>}
                      </Grid.Col>
                    </Grid>
                  </Fieldset>

                  {!isNew && data && (
                    <AgreementDocumentsSection
                      agreementId={data.id}
                      editMode={editMode}
                      pendingOps={docQ.ops}
                      failedKeys={docQ.failedKeys}
                      onStageCreate={stageDocCreate}
                      onUpdateField={updateDocField}
                      onStageDelete={stageDocDelete}
                      onUnstageOp={unstageDocOp}
                    />
                  )}
                </Stack>
              </fieldset>
              </Tabs.Panel>

              <Tabs.Panel value="mifid" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                <Grid>
                  <Grid.Col span={2}>
                    {/* #52: discrete scores (-10…100); the AQ populates it,
                        the adviser can override. Legacy non-step values are
                        appended so they still display. */}
                    <Select label="Suitability score"
                      data={SCORE_OPTS.includes(form.values.mifid_suitability_score)
                        ? SCORE_OPTS
                        : [...SCORE_OPTS, form.values.mifid_suitability_score]}
                      {...form.getInputProps('mifid_suitability_score')} {...hl('mifid_suitability_score')} />
                    {scoreDeviatesFromAq && (
                      <Text size="xs" c="orange.7" mt={2}>
                        Differs from the latest questionnaire (which computed {_latestAq?.profile_score}).
                      </Text>
                    )}
                    {hasPendingAq && (
                      <Text size="xs" c="blue.7" mt={2}>
                        A pending questionnaire will update the Suitability Score and ESG on approval.
                      </Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={2}>
                    {/* #52: ESG axis between Score and Allocation. */}
                    <Select label="ESG score" data={ESG_OPTS}
                      {...form.getInputProps('esg_score')} />
                    {esgDeviatesFromAq && (
                      <Text size="xs" c="orange.7" mt={2}>
                        Differs from the latest questionnaire.
                      </Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Select label="Strategic allocation" data={SAA_OPTS}
                      {...form.getInputProps('strategic_allocation')} {...hl('strategic_allocation')} />
                    {/* #52 polish 2: persistent small-font warning when the
                        SAVED allocation exceeds the score's correspondence
                        (server truth = the Compliance pseudo-field). */}
                    {savedAllocationExceeds === true && (
                      <Text size="xs" c="orange.7" mt={2}>
                        Allocation exceeds Suitability Score
                      </Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Last suitability" type="date"
                      {...form.getInputProps('mifid_suitability_last_date')} {...hl('mifid_suitability_last_date')}
                      onChange={(e) => {
                        form.setFieldValue('mifid_suitability_last_date', e.currentTarget.value);
                        form.setFieldValue('mifid_suitability_next_date', '');
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Next suitability"
                      type="date"
                      {...form.getInputProps('mifid_suitability_next_date')} {...hl('mifid_suitability_next_date')}
                    />
                    {data?.mifid_suitability_review_is_manual_override
                      ? <Text size="xs" c="dimmed" mt={2}>manual override</Text>
                      : <Text size="xs" c="dimmed" mt={2}>defaults to last + rule; edit down to tighten</Text>}
                  </Grid.Col>
                </Grid>
                {/* #52: the adequacy questionnaire, inline (Pattern A — the
                    staged assessment commits on Save Changes). */}
                {!isNew && data && (
                  <AqSection
                    agreementId={data.id}
                    editMode={editMode}
                    draft={aqDraft}
                    onDraftChange={setAqDraft}
                    onClearScores={() => {
                      // Pedro polish 7: Clear questionnaire also clears the
                      // two score fields (persisted as nulls on Save via the
                      // nullableOnClear set below).
                      form.setFieldValue('mifid_suitability_score', '');
                      form.setFieldValue('esg_score', '');
                    }}
                    currentAllocation={form.values.strategic_allocation}
                    onPickAllocation={(a) => form.setFieldValue('strategic_allocation', a)}
                    pendingDeletes={aqDeletes}
                    onToggleDelete={(id) => setAqDeletes(prev =>
                      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                  />
                )}
                </fieldset>
              </Tabs.Panel>

              <Tabs.Panel value="aml" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                <Grid>
                  <Grid.Col span={3}><Select label="Risk classification" data={AML_OPTS} {...form.getInputProps('aml_risk')} {...hl('aml_risk')} /></Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Last AML" type="date"
                      {...form.getInputProps('aml_last_date')} {...hl('aml_last_date')}
                      onChange={(e) => {
                        form.setFieldValue('aml_last_date', e.currentTarget.value);
                        form.setFieldValue('aml_next_date', '');
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="Next AML"
                      type="date"
                      {...form.getInputProps('aml_next_date')} {...hl('aml_next_date')}
                    />
                    {data?.aml_review_is_manual_override
                      ? <Text size="xs" c="dimmed" mt={2}>manual override</Text>
                      : <Text size="xs" c="dimmed" mt={2}>defaults to last + rule; edit down to tighten</Text>}
                  </Grid.Col>
                </Grid>
                </fieldset>
              </Tabs.Panel>

              {/* #17 Inc B: Fees tab — its own Read/Edit transaction (not the
                  page-level fieldset). */}
              <Tabs.Panel value="fees" pt="md">
                {!isNew && data && <FeesSection agreementId={data.id} />}
                {!isNew && data && <BillingIdentitySection agreementId={data.id} readOnly />}
                {/* Inc 8: the Admin\Fees View-History visualization, scoped to this
                    agreement (Total card + period filter + month accordion + invoice
                    lifecycle, locked to this agreement). */}
                {!isNew && data && (
                  <>
                    <Group gap="xs" mt="xl"><IconHistory size={20} /><Title order={4}>Fee history</Title></Group>
                    <FeeHistorySection fixedAgreementId={data.id} />
                  </>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="parties" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                {!isNew && data && (
                  <PartiesSection
                    agreementId={data.id}
                    agreementLabel={data.client_mnemonic
                      ? `${data.code_1} — ${data.client_mnemonic}` : data.code_1}
                    clientType={data.client_type}
                    editMode={editMode}
                    pendingOps={partyQ.ops}
                    failedKeys={partyQ.failedKeys}
                    onStageCreate={stagePartyCreate}
                    onStageDelete={stagePartyDelete}
                    onUnstageOp={unstagePartyOp}
                    resumePersonId={resumePartyPersonId}
                    onResumeHandled={() => setResumePartyPersonId(null)}
                  />
                )}
                {!isNew && data && <PolicyPartiesUiJoinSection agreementId={data.id} />}
                </fieldset>
              </Tabs.Panel>

              <Tabs.Panel value="accounts" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                {!isNew && data && (
                  <Stack>
                    <AccountsSection
                      agreementId={data.id}
                      editMode={editMode}
                      pendingOps={accountQ.ops}
                      failedKeys={accountQ.failedKeys}
                      onStageDetach={stageAccountDetach}
                      onStageAttach={stageAccountAttach}
                      onUnstageOp={unstageAccountOp}
                    />
                    {/* Phase A3.g (2026-06-09): second section listing
                        Indirect Accounts that belong to this Agreement
                        via its Insurance Policies. Grouped by Policy.
                        Read-only — Detach for Indirect happens on the
                        Policy detail page or the Account detail page. */}
                    <IndirectAccountsViaPoliciesSection agreementId={data.id} />
                  </Stack>
                )}
                </fieldset>
              </Tabs.Panel>

              <Tabs.Panel value="insurance" pt="md">
              <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                {!isNew && data && (
                  <InsuranceSection
                    agreementId={data.id}
                    canAddPolicy={(data.status === 'Active' || data.status === 'Prospect') && !data.deleted_at}
                    editMode={editMode}
                    pendingOps={policyQ.ops}
                    failedKeys={policyQ.failedKeys}
                    onStageOp={stagePolicyOp}
                    onUnstageOp={unstagePolicyOp}
                  />
                )}
                </fieldset>
              </Tabs.Panel>

              {/* Banks arc step 4 (0115): no outer fieldset — Read mode is View portfolio itself
                  (its controls must stay usable); Edit mode stages links in the section.
                  Rendered only while the tab is open: the Tabs keep panels mounted, and reading
                  portfolios is a client-data read (DLP read log / rate gate) — opening an
                  agreement must not read its portfolios unasked. Staged ops live in portfolioQ. */}
              {!isNew && data && can('portfolios.read') && (
                <Tabs.Panel value="portfolios" pt="md">
                  {activeTab === 'portfolios' && <AgreementPortfoliosSection
                    agreementId={data.id}
                    editMode={editMode}
                    tradable={can('trades.draft')}
                    openRequest={portfolioOpen}
                    pendingOps={portfolioQ.ops}
                    failedKeys={portfolioQ.failedKeys}
                    onStageLink={stagePortfolioLink}
                    onStageUnlink={stagePortfolioUnlink}
                    onUnstage={portfolioQ.unstage}
                    preferredRef={data.preferred_ref_currency ?? 'EUR'}
                    preferredRefInput={form.getInputProps('preferred_ref_currency')}
                  />}
                </Tabs.Panel>
              )}

              {/* TRADE-LIST: ticks staged in Edit mode, committed with Save (§3g Pattern A). Read
                  only while the tab is open (a client-data read, as Portfolios). */}
              {!isNew && data && can('portfolios.read') && (
                <Tabs.Panel value="trades" pt="md">
                  {activeTab === 'trades' && (
                    <Stack gap="xs">
                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          The trades of the portfolios linked to this agreement. Click a trade to see its history, documents
                          and emails, and to take its next step: each step is saved at once, not with this page's Save.
                        </Text>
                        <AdminOverrideSwitch />
                      </Group>
                      <TradesList agreementId={data.id} canAct={can('trades.draft')} override={tradeOverride}
                        onOpen={(t) => {
                          // N2 / N4 (Pedro 2026-10-25): trade mode inside the agreement page, on its
                          // Portfolios tab (Read mode); unsaved changes must be saved or discarded first.
                          if (pageDirty) {
                            notifications.show({ color: 'orange', message: 'Save or discard your changes first, then open the trade.' });
                            return;
                          }
                          setEditMode(false);
                          setPortfolioOpen((r) => ({ ref: t.portfolio, n: r.n + 1, draft: t.id }));
                          setActiveTab('portfolios');
                        }}
                        onOpenRun={(run) => {
                          // TRADE-CONS 4b: a run opens on its consolidation, in trade mode (as a draft above).
                          if (pageDirty) {
                            notifications.show({ color: 'orange', message: 'Save or discard your changes first, then open the run.' });
                            return;
                          }
                          setEditMode(false);
                          setPortfolioOpen((r) => ({ ref: '', n: r.n + 1, draft: null,
                            run: { id: run.id, consolidationId: run.consolidation_id, refs: run.drafts.map((d) => d.portfolio) } }));
                          setActiveTab('portfolios');
                        }} />
                    </Stack>
                  )}
                </Tabs.Panel>
              )}

              <Tabs.Panel value="interactions" pt="md">
                {/* Interactions tab has its own permission/edit flow internally;
                    the outer editMode fieldset does not gate it. */}
                {!isNew && data && <AgreementInteractionsTab agreementId={data.id} />}
              </Tabs.Panel>

              <Tabs.Panel value="tasks" pt="md">
                {/* Tasks tab — author-only edit/delete; status flips by
                    creator or assignee. Owns its permission flow. */}
                {!isNew && data && <AgreementTasksTab agreementId={data.id} />}
              </Tabs.Panel>

              {/* AuM & Return — read-only per-agreement AuM / revenue / growth
                  views (Inc 2). Lazy-loaded (Vega ~840KB) so it never weighs on
                  the main agreement view (C-260). Read-only → no Edit lifecycle. */}
              <Tabs.Panel value="aum-return" pt="md">
                {!isNew && data && (
                  <React.Suspense fallback={<Text size="sm" c="dimmed">Loading…</Text>}>
                    <AgreementAumReturnTab agreementId={data.id} />
                  </React.Suspense>
                )}
              </Tabs.Panel>
              {/* #50 stage 2 (2026-06-12): Documents — On file + Export new (IAA
                  draft generator). Export is Pattern B (generation act, not an
                  entity write), so it works in Read mode too. P23 (2026-10-10):
                  adding / replacing / removing a signed copy or other file IS an
                  entity write — it stages into this page's Edit transaction
                  (fileDocs) and commits on Save. No disabled fieldset here, so
                  View / Download / Export keep working in Read mode. */}
              <Tabs.Panel value="documents" pt="md">
                {!isNew && data && <AgreementDocumentsTab agreement={data} editMode={editMode} staging={fileDocs} />}
              </Tabs.Panel>
            </Tabs>

            {!isNew && data && (
              <Text size="xs" c="dimmed" ta="right" mt="md">
                Last updated {new Date(data.updated_at).toLocaleString()}
              </Text>
            )}
          </Stack>
        </form>
      </Stack>
      <Modal opened={mnemonicModalOpen} onClose={() => setMnemonicModalOpen(false)}
        title="Suggest mnemonic from holder name" size="md">
        <SuggestMnemonicModalBody
          onApply={(m) => {
            form.setFieldValue('client_mnemonic', m);
            setMnemonicModalOpen(false);
          }}
          onCancel={() => setMnemonicModalOpen(false)}
        />
      </Modal>
      <SuggestMifirModal
        opened={mifirModalOpen}
        agreementId={!isNew && data ? data.id : null}
        idType={isParticular ? 'NCI' : 'LEI'}
        onApply={(v) => form.setFieldValue('mifir_id', v)}
        onClose={() => setMifirModalOpen(false)}
      />
    </Container>
  );
}


// Phase A3.a (2026-06-09): SummaryItem and SummaryBadge promoted to a shared
// module — see `components/SummaryBox.tsx`. Local definitions removed. Other
// detail pages will reuse the same components for consistent header summary
// rows.

// #276 polish (2026-06-08): summary-bar chip showing the Primary
// Named RM(s) for this Agreement. Read-only; management lives on
// the Named RMs tab (NamedRMSection). Falls back to em-dash if no
// primary assignments exist.
function NamedRMSummaryChip({ agreementId }: { agreementId: string }) {
  // Filter is_primary=true at the API to keep the chip narrowly
  // scoped to the Primary tier — the chip is meant to answer
  // "who's responsible at-a-glance" not "list everyone assigned".
  const assignmentsQ = useAgreementUsers({
    agreement_id: agreementId,
    is_primary: true,
    limit: 10,
  });

  const primaries = assignmentsQ.data?.items ?? [];
  // #56 (2026-06-13, C-134 class, §3j): use the server-resolved
  // user_display_name carried on each agreement-user row. Previously this
  // chip joined against an admin-gated GET /users list (useUsers), so a
  // staff/RM viewer got an empty map and the chip showed "—". The name now
  // arrives with the assignments query itself — no second admin-only query.
  const names = primaries
    .map((a) => a.user_display_name)
    .filter((n): n is string => Boolean(n));

  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase">Named RM</Text>
      {names.length === 0
        ? <Text size="sm" c="dimmed">—</Text>
        : <Tooltip label={names.length > 1 ? names.join(', ') : ''}
            disabled={names.length <= 1} withArrow>
            <Badge color="yellow" variant="light" size="md">
              {names.length === 1
                ? names[0]
                : `${names[0]} +${names.length - 1}`}
            </Badge>
          </Tooltip>}
    </div>
  );
}

function mifidNatureLabel(v: string | null | undefined): string {
  if (!v) return '—';
  const found = NATURE_OPTS.find((o) => o.value === v);
  return found?.label ?? v;
}
function mifidResidenceLabel(v: string | null | undefined): string {
  if (!v) return '—';
  const found = RESIDENCE_OPTS.find((o) => o.value === v);
  return found?.label ?? v;
}


// --- Phase D2 - Agreement documents (IAA history) ---
// Dynamic table that replaces the fixed signature_date / amendment_1-4_date
// / close_date columns. First row pinned to "Original IAA" (kind=IAA_INITIAL)
// so the user can backfill the original signing date even if it's missing.

const DOC_KIND_LABEL: Record<AgreementDocumentKind, string> = {
  IAA_INITIAL: 'Original IAA',
  AMENDMENT: 'Amendment',
  CLOSE: 'Close',
};

interface AgreementDocumentsSectionProps {
  agreementId: string;
  editMode: boolean;
  pendingOps: readonly PendingDocOp[];
  failedKeys: ReadonlySet<string>;
  onStageCreate: (payload: {
    kind: AgreementDocumentKind;
    ordinal: number;
    label: string;
    signed_date: string | null;
  }) => void;
  onUpdateField: (
    key: string,
    isPendingCreate: boolean,
    field: 'label' | 'signed_date',
    newValue: string | null,
    dbValue: string | null,
  ) => void;
  onStageDelete: (doc_id: UUID) => void;
  onUnstageOp: (key: string) => void;
}

function AgreementDocumentsSection({
  agreementId, editMode,
  pendingOps, failedKeys,
  onStageCreate, onUpdateField, onStageDelete, onUnstageOp,
}: AgreementDocumentsSectionProps) {
  const docsQ = useAgreementDocumentsByAgreement(agreementId);

  // #327 Phase 2 (2026-06-09): build the effective view (DB rows
  // overlaid with pending updates, minus pending deletes, plus pending
  // creates). Sort + validation must operate on the effective view so
  // a pending-create row with a signed_date participates in the
  // monotonic ordering check the same way a DB row would.
  type DbItem = {
    kind: 'db';
    doc: AgreementDocument;
    pending: Extract<PendingDocOp, { kind: 'update' | 'delete' }> | undefined;
    effLabel: string;
    effSignedDate: string | null;
  };
  type PendingCreateItem = {
    kind: 'pending-create';
    op: Extract<PendingDocOp, { kind: 'create' }>;
    effLabel: string;
    effSignedDate: string | null;
  };
  type EffectiveItem = DbItem | PendingCreateItem;

  const effectiveItems = React.useMemo<EffectiveItem[]>(() => {
    const list: EffectiveItem[] = [];
    const pendingByDocId = new Map<UUID, Extract<PendingDocOp, { kind: 'update' | 'delete' }>>();
    for (const op of pendingOps) {
      if (op.kind === 'update' || op.kind === 'delete') pendingByDocId.set(op.doc_id, op);
    }
    for (const doc of (docsQ.data?.items ?? [])) {
      const pending = pendingByDocId.get(doc.id);
      const effLabel = pending?.kind === 'update' && pending.payload.label !== undefined
        ? pending.payload.label : doc.label;
      const effSignedDate = pending?.kind === 'update' && pending.payload.signed_date !== undefined
        ? pending.payload.signed_date
        : doc.signed_date;
      list.push({ kind: 'db', doc, pending, effLabel, effSignedDate });
    }
    for (const op of pendingOps) {
      if (op.kind === 'create') {
        list.push({
          kind: 'pending-create',
          op,
          effLabel: op.payload.label,
          effSignedDate: op.payload.signed_date,
        });
      }
    }
    const sortKey = (it: EffectiveItem) => {
      const k = it.kind === 'db' ? it.doc.kind : it.op.payload.kind;
      const ord = it.kind === 'db' ? it.doc.ordinal : it.op.payload.ordinal;
      const sd = it.effSignedDate ?? '';
      const kindOrder = k === 'IAA_INITIAL' ? 0 : k === 'CLOSE' ? 2 : 1;
      return [kindOrder, ord, sd] as const;
    };
    list.sort((a, b) => {
      const [ak, ao, as] = sortKey(a);
      const [bk, bo, bs] = sortKey(b);
      if (ak !== bk) return ak - bk;
      if (ao !== bo) return ao - bo;
      return as.localeCompare(bs);
    });
    return list;
  }, [docsQ.data, pendingOps]);

  // hasInitial/hasClose decide whether the Add Original IAA / Add close
  // buttons are visible. Effective view: include pending-creates (you
  // can't add a second pending Original), exclude pending-deletes
  // (deleting the only IAA_INITIAL should allow staging a fresh one,
  // though in practice IAA_INITIAL has no delete affordance).
  const hasInitial = effectiveItems.some((it) => {
    if (it.kind === 'pending-create') return it.op.payload.kind === 'IAA_INITIAL';
    return it.doc.kind === 'IAA_INITIAL' && it.pending?.kind !== 'delete';
  });
  const hasClose = effectiveItems.some((it) => {
    if (it.kind === 'pending-create') return it.op.payload.kind === 'CLOSE';
    return it.doc.kind === 'CLOSE' && it.pending?.kind !== 'delete';
  });

  // #20 (2026-06-10): Pedro's app-wide rule — filed-awaiting-approval
  // CREATEs render as phantom rows (blue treatment), distinct from the
  // orange staged-not-yet-saved rows. Filed UPDATE/DELETE CRs on existing
  // doc rows are already covered by the row-level pending badge; only
  // CREATEs lack a row to decorate. See hooks/usePendingCreates.
  const filedCreates = usePendingCreates('agreement_documents', 'agreement_id', agreementId);

  return (
    <Fieldset legend={<Group gap="xs"><IconClipboardList size={16} /><span>IAA history</span></Group>}>
      <Stack>
        {docsQ.isLoading && <Text c="dimmed" size="sm">Loading…</Text>}

        {!docsQ.isLoading && effectiveItems.length === 0 && filedCreates.length === 0 && (
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">Pending IAA — no agreement documents recorded yet.</Text>
          </Alert>
        )}

        {(effectiveItems.length > 0 || filedCreates.length > 0) && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Kind</Table.Th>
                <Table.Th>Label</Table.Th>
                <Table.Th w={150}>Signed date</Table.Th>
                <Table.Th w={120}>Document</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {effectiveItems.map((it, i) => {
                // Polish 1 (Pattern A extension): monotonic ordering
                // now spans the effective view (DB + pending), so
                // pending-create / pending-update signed_dates
                // participate in the prior/next constraint computation.
                const priorMin = effectiveItems
                  .slice(0, i)
                  .map((p) => p.effSignedDate)
                  .filter((s): s is string => !!s)
                  .reduce<string | null>((m, s) => (m === null || s > m ? s : m), null);
                const nextMax = effectiveItems
                  .slice(i + 1)
                  .map((p) => p.effSignedDate)
                  .filter((s): s is string => !!s)
                  .reduce<string | null>((m, s) => (m === null || s < m ? s : m), null);
                if (it.kind === 'db') {
                  return (
                    <AgreementDocumentRow
                      key={it.doc.id}
                      doc={it.doc}
                      editMode={editMode}
                      pending={it.pending}
                      failed={failedKeys.has(it.doc.id)}
                      effLabel={it.effLabel}
                      effSignedDate={it.effSignedDate}
                      minSignedDate={priorMin}
                      maxSignedDate={nextMax}
                      onUpdateField={onUpdateField}
                      onStageDelete={onStageDelete}
                      onUnstageOp={onUnstageOp}
                    />
                  );
                }
                return (
                  <PendingDocCreateRow
                    key={it.op.tempKey}
                    op={it.op}
                    editMode={editMode}
                    failed={failedKeys.has(it.op.tempKey)}
                    minSignedDate={priorMin}
                    maxSignedDate={nextMax}
                    onUpdateField={onUpdateField}
                    onUnstageOp={onUnstageOp}
                  />
                );
              })}
              {/* #20: filed-pending CREATE phantom rows — blue treatment
                  ("filed, awaiting approval"), badge links to the change
                  request. These docs don't participate in the effective-
                  view ordering (they're not editable from here) — they
                  render at the bottom until Compliance decides. */}
              {filedCreates.map((cr) => {
                const p = cr.payload as {
                  kind?: string; label?: string; signed_date?: string | null;
                };
                return (
                  <Table.Tr key={cr.id}
                    style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {p.kind ? <Badge variant="light">{DOC_KIND_LABEL[p.kind as AgreementDocumentKind] ?? p.kind}</Badge> : '—'}
                    </Table.Td>
                    <Table.Td><Text size="sm">{p.label ?? '—'}</Text></Table.Td>
                    <Table.Td>{p.signed_date ?? '—'}</Table.Td>
                    <Table.Td>
                      <Tooltip
                        label="Filed for approval — Compliance must approve before the document becomes active. Click to open the change request."
                        withArrow>
                        <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                          <Badge color="blue" variant="light" size="xs"
                            styles={{ root: { cursor: 'pointer' } }}>
                            {/* #37/old-#360 (2026-06-11): canonical casing —
                                blue post-Save badges say "Pending Approval". */}
                            Pending Approval
                          </Badge>
                        </Anchor>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        {editMode && (
          <Group gap="xs">
            {!hasInitial && (
              <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                onClick={() => onStageCreate({
                  kind: 'IAA_INITIAL', ordinal: 0,
                  label: 'Original IAA', signed_date: null,
                })}>
                Add Original IAA
              </Button>
            )}
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
              onClick={() => {
                // next_ord considers BOTH DB amendments AND any pending
                // create amendments — so staging two amendments in a
                // single Edit session produces ordinals 1 and 2.
                const allAmendmentOrdinals = effectiveItems
                  .filter((it) => {
                    const k = it.kind === 'db' ? it.doc.kind : it.op.payload.kind;
                    return k === 'AMENDMENT' && !(it.kind === 'db' && it.pending?.kind === 'delete');
                  })
                  .map((it) => it.kind === 'db' ? it.doc.ordinal : it.op.payload.ordinal);
                const next_ord = (allAmendmentOrdinals.length === 0 ? 0 : Math.max(...allAmendmentOrdinals)) + 1;
                onStageCreate({
                  kind: 'AMENDMENT', ordinal: next_ord,
                  label: `Amendment ${next_ord}`, signed_date: null,
                });
              }}>
              Add amendment
            </Button>
            {!hasClose && (
              <Button size="xs" variant="light" color="gray" leftSection={<IconPlus size={14} />}
                onClick={() => onStageCreate({
                  kind: 'CLOSE', ordinal: 99,
                  label: 'Close', signed_date: null,
                })}>
                Add close
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Fieldset>
  );
}

interface AgreementDocumentRowProps {
  doc: AgreementDocument;
  editMode: boolean;
  pending: Extract<PendingDocOp, { kind: 'update' | 'delete' }> | undefined;
  failed: boolean;
  effLabel: string;
  effSignedDate: string | null;
  minSignedDate: string | null;
  maxSignedDate: string | null;
  onUpdateField: AgreementDocumentsSectionProps['onUpdateField'];
  onStageDelete: AgreementDocumentsSectionProps['onStageDelete'];
  onUnstageOp: AgreementDocumentsSectionProps['onUnstageOp'];
}

function AgreementDocumentRow({
  doc, editMode, pending, failed,
  effLabel, effSignedDate, minSignedDate, maxSignedDate,
  onUpdateField, onStageDelete, onUnstageOp,
}: AgreementDocumentRowProps) {
  // #38/old-#348b (2026-06-11): Original IAA / Close deletions get a
  // consequence-naming confirm before staging.
  const { confirm } = useAlertModal();
  const pendingDelete = pending?.kind === 'delete';
  const pendingUpdate = pending?.kind === 'update' ? pending : null;
  const signedDateStr = effSignedDate ?? '';

  // Polish 1: client-side validation of the monotonic ordering.
  // #38/old-#349 (2026-06-11): plus the today-cap — a document cannot be
  // signed in the future (Pydantic 422 + DB CHECK back this up).
  const todayStr = new Date().toISOString().slice(0, 10);
  const effMaxDate = maxSignedDate && maxSignedDate < todayStr ? maxSignedDate : todayStr;
  let dateError: string | null = null;
  if (signedDateStr) {
    if (signedDateStr > todayStr) {
      dateError = 'Signed date cannot be in the future.';
    } else if (minSignedDate && signedDateStr < minSignedDate) {
      dateError = `Must be on or after ${minSignedDate} (prior IAA event).`;
    } else if (maxSignedDate && signedDateStr > maxSignedDate) {
      dateError = `Must be on or before ${maxSignedDate} (later IAA event).`;
    }
  }

  return (
    <Table.Tr style={pendingDelete ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
      <Table.Td>
        <Badge variant="light"
          color={doc.kind === 'IAA_INITIAL' ? 'blue' : doc.kind === 'CLOSE' ? 'gray' : 'grape'}>
          {DOC_KIND_LABEL[doc.kind]}
        </Badge>
      </Table.Td>
      <Table.Td>
        {editMode ? (
          <TextInput
            size="xs"
            value={effLabel}
            disabled={pendingDelete}
            onChange={(e) => onUpdateField(doc.id, false, 'label', e.currentTarget.value, doc.label)}
          />
        ) : (
          <Text size="sm">{doc.label}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {editMode ? (
          <TextInput
            size="xs"
            type="date"
            value={signedDateStr}
            disabled={pendingDelete}
            min={minSignedDate ?? undefined}
            max={effMaxDate}
            error={dateError ?? undefined}
            onChange={(e) => onUpdateField(
              doc.id, false, 'signed_date',
              e.currentTarget.value || null,
              doc.signed_date,
            )}
          />
        ) : (
          <Text size="sm">{doc.signed_date ?? '—'}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">{doc.document_ref ?? 'no upload'}</Text>
        {pendingUpdate && <Badge color="orange" variant="light" size="xs" mt={4}>Pending Save Changes</Badge>}
        {pendingDelete && <Badge color="orange" variant="light" size="xs" mt={4}>Pending detach</Badge>}
        {failed && <Badge color="red" variant="light" size="xs" mt={4}>Failed</Badge>}
        {doc.pending_change_requests && doc.pending_change_requests.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <PendingCRBadge crs={doc.pending_change_requests} />
          </div>
        )}
      </Table.Td>
      <Table.Td>
        {editMode && (
          <Group gap={4} wrap="nowrap">
            {pending ? (
              <Tooltip label="Undo staged change" withArrow>
                <ActionIcon size="sm" variant="subtle" color="gray"
                  onClick={() => onUnstageOp(doc.id)}>
                  <IconArrowBackUp size={14} />
                </ActionIcon>
              </Tooltip>
            ) : (
              /* #38/old-#348 (2026-06-11, Pedro's decision 2b): Original
                 IAA + Close rows are now deletable too — same staged
                 Pattern A delete as Amendments, but with an extra
                 consequence-naming confirm. Rationale: Admin/Compliance
                 auto-apply on Save and skip the four-eyes net, so the
                 confirm is THEIR safety layer ("save Admins from
                 themselves"). Soft-delete: recoverable via Restore;
                 the alive-only uniqueness indexes allow re-adding. */
              <Tooltip
                label={
                  doc.kind === 'AMENDMENT' ? 'Delete amendment (on Save)'
                    : doc.kind === 'IAA_INITIAL' ? 'Delete Original IAA (on Save)'
                    : 'Delete close document (on Save)'
                }
                withArrow>
                <ActionIcon size="sm" variant="subtle" color="red"
                  onClick={async () => {
                    if (doc.kind === 'AMENDMENT') {
                      onStageDelete(doc.id);
                      return;
                    }
                    const ok = await confirm({
                      title: doc.kind === 'IAA_INITIAL'
                        ? 'Delete the Original IAA?'
                        : 'Delete the Close document?',
                      message: doc.kind === 'IAA_INITIAL'
                        ? 'The agreement will be flagged as incomplete (an '
                          + 'Original IAA is required) until a replacement is '
                          + 'added. The deletion commits on Save Changes and '
                          + 'is recoverable via Restore.'
                        : 'The Close date will disappear from the agreement '
                          + 'header. The deletion commits on Save Changes and '
                          + 'is recoverable via Restore.',
                      confirmLabel: 'Stage deletion',
                      cancelLabel: 'Cancel',
                      destructive: true,
                    });
                    if (ok) onStageDelete(doc.id);
                  }}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

interface PendingDocCreateRowProps {
  op: Extract<PendingDocOp, { kind: 'create' }>;
  editMode: boolean;
  failed: boolean;
  minSignedDate: string | null;
  maxSignedDate: string | null;
  onUpdateField: AgreementDocumentsSectionProps['onUpdateField'];
  onUnstageOp: AgreementDocumentsSectionProps['onUnstageOp'];
}

function PendingDocCreateRow({
  op, editMode, failed, minSignedDate, maxSignedDate,
  onUpdateField, onUnstageOp,
}: PendingDocCreateRowProps) {
  const signedDateStr = op.payload.signed_date ?? '';
  // #38/old-#349 (2026-06-11): today-cap mirrors AgreementDocumentRow.
  const todayStr = new Date().toISOString().slice(0, 10);
  const effMaxDate = maxSignedDate && maxSignedDate < todayStr ? maxSignedDate : todayStr;
  let dateError: string | null = null;
  if (signedDateStr) {
    if (signedDateStr > todayStr) {
      dateError = 'Signed date cannot be in the future.';
    } else if (minSignedDate && signedDateStr < minSignedDate) {
      dateError = `Must be on or after ${minSignedDate} (prior IAA event).`;
    } else if (maxSignedDate && signedDateStr > maxSignedDate) {
      dateError = `Must be on or before ${maxSignedDate} (later IAA event).`;
    }
  }
  return (
    <Table.Tr style={{ background: 'var(--mantine-color-orange-0)' }}>
      <Table.Td>
        <Badge variant="light"
          color={op.payload.kind === 'IAA_INITIAL' ? 'blue' : op.payload.kind === 'CLOSE' ? 'gray' : 'grape'}>
          {DOC_KIND_LABEL[op.payload.kind]}
        </Badge>
      </Table.Td>
      <Table.Td>
        {editMode ? (
          <TextInput
            size="xs"
            value={op.payload.label}
            onChange={(e) => onUpdateField(op.tempKey, true, 'label', e.currentTarget.value, null)}
          />
        ) : (
          <Text size="sm">{op.payload.label}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {editMode ? (
          <TextInput
            size="xs"
            type="date"
            value={signedDateStr}
            min={minSignedDate ?? undefined}
            max={effMaxDate}
            error={dateError ?? undefined}
            onChange={(e) => onUpdateField(
              op.tempKey, true, 'signed_date',
              e.currentTarget.value || null,
              null,
            )}
          />
        ) : (
          <Text size="sm">{op.payload.signed_date ?? '—'}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">(will create on Save)</Text>
        <Badge color="orange" variant="light" size="xs" mt={4}>Pending Save Changes</Badge>
        {failed && <Badge color="red" variant="light" size="xs" mt={4}>Failed</Badge>}
      </Table.Td>
      <Table.Td>
        {editMode && (
          <Tooltip label="Undo staged creation" withArrow>
            <ActionIcon size="sm" variant="subtle" color="gray"
              onClick={() => onUnstageOp(op.tempKey)}>
              <IconArrowBackUp size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Table.Td>
    </Table.Tr>
  );
}


interface SuggestProps { onApply: (m: string) => void; onCancel: () => void; }

function SuggestMnemonicModalBody({ onApply, onCancel }: SuggestProps) {
  const [pickedId, setPickedId] = React.useState<string>('');
  const [first, setFirst] = React.useState('');
  const [last, setLast] = React.useState('');
  const [mnemonic, setMnemonic] = React.useState('');
  const [base, setBase] = React.useState('');
  const [wasModified, setWasModified] = React.useState(false);
  // Pedro (2026-06-10): the generator-only modal forced gaming the
  // first/last fields just to type a known mnemonic (VINSA → VINSAA).
  // Keep the helpful generators, but allow direct free-text entry too.
  const [custom, setCustom] = React.useState('');
  const customValid = /^[A-Z]{6}$/.test(custom);
  const personQ = usePerson(pickedId.length >= 32 ? pickedId : undefined);

  React.useEffect(() => {
    const p = personQ.data;
    if (!p?.display_name) return;
    const tokens = p.display_name.trim().split(/\s+/);
    if (tokens.length === 0) return;
    setFirst(tokens[0]);
    setLast(tokens.length > 1 ? tokens[tokens.length - 1] : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personQ.data?.id]);

  React.useEffect(() => {
    if (!first && !last) {
      setMnemonic(''); setBase(''); setWasModified(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      agreementsApi.suggestMnemonic(first, last)
        .then((r) => {
          if (cancelled) return;
          setMnemonic(r.mnemonic); setBase(r.base); setWasModified(r.was_modified);
        })
        .catch(() => {});
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [first, last]);

  return (
    <Stack>
      <PersonPicker label="Pick a person (optional)"
        description="Pre-fills first / last name from the person's display name."
        value={pickedId} onChange={(v) => setPickedId(v ?? '')} />
      <Divider label="or enter manually" labelPosition="center" />
      <Grid>
        <Grid.Col span={6}>
          <TextInput label="First name" value={first}
            onChange={(e) => setFirst(e.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Last name" value={last}
            onChange={(e) => setLast(e.currentTarget.value)} />
        </Grid.Col>
      </Grid>
      <Text size="sm" c="dimmed">
        Preview: <Text component="span" ff="monospace" fw={600} c="azul.7">{mnemonic || '—'}</Text>
      </Text>
      {wasModified && (
        <Text size="xs" c="orange.7">
          {base} is already in use by an Activo agreement — suggesting {mnemonic} instead.
        </Text>
      )}
      <Divider label="or type it directly" labelPosition="center" />
      <TextInput
        label="Custom mnemonic"
        description="Exactly 6 uppercase letters (A–Z)."
        value={custom}
        maxLength={6}
        style={{ textTransform: 'uppercase' }}
        onChange={(e) => setCustom(e.currentTarget.value.toUpperCase().replace(/[^A-Z]/g, ''))}
        error={custom.length > 0 && !customValid ? '6 uppercase letters required' : undefined}
      />
      <Group justify="flex-end" gap="xs" mt="sm">
        <Button variant="subtle" onClick={onCancel}>Cancel</Button>
        {/* Custom entry wins when valid; otherwise the generated one. */}
        <Button
          onClick={() => onApply(customValid ? custom : mnemonic)}
          disabled={!customValid && mnemonic.length !== 6}>
          {customValid ? `Apply ${custom}` : 'Apply'}
        </Button>
      </Group>
    </Stack>
  );
}

const ROLE_COLOURS: Record<string, string> = {
  HOLDER: 'blue', REPRESENTATIVE: 'grape', BENEFICIARY: 'orange',
};

interface PartiesSectionProps {
  agreementId: string;
  /** Friendly label shown in the Add-party modal header. */
  agreementLabel: string;
  /** Drives the modal's UBO-switch visibility (Empresa only — the
   *  backend 422s is_ubo on Particular agreements). */
  clientType: 'Particular' | 'Empresa';
  /** Whether the parent Agreement page is in Edit mode. Trash icon
   *  only appears in Edit mode (§3g + §2); the Add button is greyed
   *  with a tooltip outside Edit mode (§3h / C-110). */
  editMode: boolean;
  pendingOps: readonly PendingPartyOp[];
  failedKeys: ReadonlySet<string>;
  onStageCreate: (
    payload: AgreementPartyCreateInput, personDisplayName: string | null,
  ) => void;
  onStageDelete: (party_id: UUID) => void;
  onUnstageOp: (key: string) => void;
  /** #6 (2026-09-13): a newly-created person's id ferried back from the "Create
   *  new person" round-trip — reopen the Add-party modal on the Attach step with
   *  it pre-selected. Cleared via onResumeHandled once consumed (one-shot). */
  resumePersonId?: string | null;
  onResumeHandled?: () => void;
}

function PartiesSection({
  agreementId, agreementLabel, clientType, editMode, pendingOps, failedKeys,
  onStageCreate, onStageDelete, onUnstageOp, resumePersonId, onResumeHandled,
}: PartiesSectionProps) {
  const partiesQ = useAgreementParties({ agreement_id: agreementId, limit: 200 });
  // D2-polish 2 follow-up: come back to the Parties tab after add/edit so the
  // user can immediately see the effect or add another row.
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=parties`);

  // Session A4 (2026-06-10): "+ Add party" now opens a modal-that-stages
  // (AddAgreementPartyModal) instead of navigating away — the last C-107
  // Pattern A violator (#7) closed. The section owns the modal state,
  // mirroring AccountDetailPage's PartiesSection (#413).
  // #6 (2026-09-13): a resumed create round-trip opens the modal with the new
  // person pre-selected (useAddPartyModal, shared with Policy/Account — P18).
  const addModal = useAddPartyModal(resumePersonId, onResumeHandled);

  // #327 Phase 1 (2026-06-09): O(1) lookup of "is this row pending
  // deletion?" Avoids walking pendingOps for every row.
  const pendingDeleteIds = React.useMemo(
    () => new Set(
      pendingOps.filter((op) => op.kind === 'delete').map((op) => op.party_id),
    ),
    [pendingOps],
  );

  // Session A4: staged-create rows, rendered beneath the committed rows
  // with the Pending Save treatment (orange row + Unstage button).
  const pendingCreates = React.useMemo(
    () => pendingOps.filter(
      (op): op is Extract<PendingPartyOp, { kind: 'create' }> => op.kind === 'create',
    ),
    [pendingOps],
  );

  // Pedro's app-wide rule (2026-06-10, A4 smoke Step 5 — extends Task 4's
  // Post-Save CR visibility): a FILED create must stay visible in the list
  // until Compliance decides. Task 4's PendingCRBadge only decorates rows
  // that exist; a pending CREATE has no row yet, so we render phantom rows
  // from the pending CRs themselves (blue treatment = "filed, awaiting
  // approval", distinct from the orange "staged, not yet saved").
  const filedCreates = usePendingCreates('agreement_parties', 'agreement_id', agreementId);

  const hasAnyRow = (partiesQ.data?.items.length ?? 0) > 0
    || pendingCreates.length > 0 || filedCreates.length > 0;

  return (
    <Fieldset legend={<Group gap="xs"><IconUsers size={16} /><span>Parties on this agreement</span></Group>}>
      <AddAgreementPartyModal
        opened={addModal.opened}
        onClose={addModal.close}
        agreementId={agreementId}
        agreementLabel={agreementLabel}
        clientType={clientType}
        onStage={onStageCreate}
        initialPersonId={addModal.initialPersonId}
      />
      <Stack>
        <Group justify="flex-end">
          {/* §3h / C-110 (Session A4): enabled in Edit mode; greyed with an
              explanatory tooltip in Read mode. The staged row commits on
              the page's Save Changes. */}
          <DisabledTooltipButton
            size="xs" leftSection={<IconPlus size={14} />} variant="light"
            disabledReason={editMode ? null : 'Enter Edit mode to add a party'}
            onClick={addModal.open}>
            Add party
          </DisabledTooltipButton>
        </Group>
        {partiesQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!partiesQ.isLoading && !hasAnyRow && (
          <Text c="dimmed" size="sm">No parties yet - click "Add party" to assign a holder, representative or beneficiary.</Text>
        )}
        {hasAnyRow && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Role</Table.Th>
                <Table.Th>Person</Table.Th>
                <Table.Th w={80}>UBO</Table.Th>
                <Table.Th w={160}>Status</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {partiesQ.data?.items.map((p) => {
                const pendingDelete = pendingDeleteIds.has(p.id);
                const failed = failedKeys.has(p.id);
                return (
                  <Table.Tr
                    key={p.id}
                    // #5 (2026-06-16): opacity dims the row; line-through is
                    // applied to the Role pill + Person cell only, so the
                    // status badges (incl. "Pending detach") stay legible.
                    style={pendingDelete ? { opacity: 0.6 } : undefined}
                  >
                    <Table.Td>
                      <Tooltip
                        label="Click to view / edit this role assignment"
                        withArrow position="top">
                        <Anchor component={Link} to={`/agreement-parties/${p.id}?return_to=${returnTo}`} size="sm" style={{ cursor: 'pointer' }}>
                          <Badge variant="light" color={ROLE_COLOURS[p.role]}
                            styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                            {PARTY_ROLE_LABELS[p.role] ?? p.role}
                          </Badge>
                        </Anchor>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                      {p.person_display_name ? (
                        <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                          {p.person_display_name}
                        </Anchor>
                      ) : (
                        <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                      )}
                    </Table.Td>
                    <Table.Td>{p.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                        {p.deleted_at
                          ? <Badge color="gray" variant="light">Deleted</Badge>
                          : <Badge color="green" variant="light">Active</Badge>}
                        </StatusExplained>
                        {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending detach</Badge>}
                        {failed && <Badge color="red" variant="light" size="xs">Failed</Badge>}
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                    </Table.Td>
                    {/* #327 Phase 1 (2026-06-09): trash icon stages a
                        pending detach instead of firing the mutation
                        immediately. Soft-deleted rows still hide both
                        affordances. Pending rows show only Undo. */}
                    <Table.Td>
                      <RowSeverIcon
                        pending={pendingDelete}
                        editMode={editMode}
                        deleted={!!p.deleted_at}
                        onStage={() => onStageDelete(p.id)}
                        onUnstage={() => onUnstageOp(p.id)}
                        stageLabel="Detach this party (on Save)"
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {/* Session A4: staged Add-party rows — Pending Save treatment
                  mirrors AccountDetailPage's PartiesSection (#413): orange
                  tint, Pending Save badge, Failed marker after an
                  unsuccessful Save batch, Unstage to drop the staged row. */}
              {pendingCreates.map((op) => {
                const failed = failedKeys.has(op.tempKey);
                return (
                  <Table.Tr key={op.tempKey}
                    style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                    <Table.Td>
                      <Badge variant="light" color={ROLE_COLOURS[op.payload.role]}>
                        {PARTY_ROLE_LABELS[op.payload.role] ?? op.payload.role}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {op.personDisplayName ? (
                        <Text size="sm">{op.personDisplayName}</Text>
                      ) : (
                        <Text size="xs" ff="monospace" c="dimmed">
                          {op.payload.person_id.slice(0, 8)}…
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {op.payload.is_ubo
                        ? <Badge color="red" variant="light">UBO</Badge> : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Badge color="orange" variant="light" size="xs">Pending Save</Badge>
                        {failed && (
                          <Tooltip label="This staged party failed on the last Save — fix the cause (e.g. duplicate role) or unstage it, then Save again." withArrow>
                            <Badge color="red" variant="light" size="xs">Failed</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {editMode && (
                        <Tooltip label="Remove this staged party" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="gray"
                            onClick={() => onUnstageOp(op.tempKey)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {/* Pedro's app-wide rule (2026-06-10, extends Task 4): FILED
                  creates render as phantom rows until Compliance decides —
                  blue treatment ("filed, awaiting approval"), badge links
                  to the change request. Distinct from orange staged rows
                  (not yet saved). Sourced from pending CREATE CRs whose
                  payload targets this agreement. */}
              {filedCreates.map((cr) => {
                const p = cr.payload as {
                  person_id?: string; role?: string; is_ubo?: boolean;
                };
                return (
                  <Table.Tr key={cr.id}
                    style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {p.role ? (
                        <Badge variant="light" color={ROLE_COLOURS[p.role] ?? 'gray'}>
                          {PARTY_ROLE_LABELS[p.role] ?? p.role}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>
                      <PersonRef id={p.person_id} linkPathPrefix="/persons" />
                    </Table.Td>
                    <Table.Td>
                      {p.is_ubo ? <Badge color="red" variant="light">UBO</Badge> : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        label="Filed for approval — Compliance must approve before the party becomes active. Click to open the change request."
                        withArrow>
                        <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                          <Badge color="blue" variant="light" size="xs"
                            styles={{ root: { cursor: 'pointer' } }}>
                            {/* #37/old-#360 (2026-06-11): canonical casing —
                                blue post-Save badges say "Pending Approval". */}
                            Pending Approval
                          </Badge>
                        </Anchor>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td />
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Fieldset>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PolicyPartiesUiJoinSection — Phase H1.5
//
// Read-only UI join across the agreement's insurance policies. Surfaces
// every insurance_policy_parties row (POLICY_HOLDER / LIFE_INSURED /
// BENEFICIARY) for any policy linked to one of this agreement's accounts,
// grouped by policy. No physical mirror — the underlying rows live in
// insurance_policy_parties on the policy, not on the agreement. Editing
// a row routes back to the policy's Parties tab so the source-of-truth
// stays unambiguous.
// ──────────────────────────────────────────────────────────────────────────
const POLICY_PARTY_ROLE_COLOURS: Record<InsurancePolicyPartyRole, string> = {
  POLICY_HOLDER: 'blue',
  LIFE_INSURED: 'teal',
  BENEFICIARY: 'orange',
};

function PolicyPartiesUiJoinSection({ agreementId }: { agreementId: string }) {
  const policiesQ = useAccountInsuranceList({ agreement_id: agreementId, limit: 50 });
  const policies = policiesQ.data?.items ?? [];
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=parties`);

  return (
    <Fieldset legend={<Group gap="xs"><IconShieldCheck size={16} /><span>Policy parties (read-only join)</span></Group>}
      mt="md">
      <Stack>
        <Text size="xs" c="dimmed">
          Policy-level role assignments live on each insurance policy. To add or
          edit, open the policy and use its Parties tab.
        </Text>
        {policiesQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!policiesQ.isLoading && policies.length === 0 && (
          <Text c="dimmed" size="sm">No insurance policies linked to this agreement.</Text>
        )}
        {policies.map((p) => (
          <PolicyPartiesUiJoinBlock key={p.id} policy={p} returnTo={returnTo} />
        ))}
      </Stack>
    </Fieldset>
  );
}

function PolicyPartiesUiJoinBlock(
  { policy, returnTo }: { policy: { id: string; insurer: string; policy_number: string }; returnTo: string },
) {
  // 200, like the other party tabs (the default 50 silently cut the list, C-374).
  const partiesQ = useInsurancePolicyPartiesList(policy.id, undefined, undefined, { limit: 200 });
  const items = partiesQ.data?.items ?? [];

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text size="sm" fw={500}>
          {policy.insurer} — <Text span ff="monospace">{policy.policy_number}</Text>
        </Text>
        <Anchor component={Link} to={`/account-insurance/${policy.id}?tab=parties`} size="xs">
          Open policy Parties tab →
        </Anchor>
      </Group>
      {partiesQ.isLoading && <Text c="dimmed" size="xs">Loading parties...</Text>}
      {!partiesQ.isLoading && items.length === 0 && (
        <Text c="dimmed" size="xs">No parties recorded yet on this policy.</Text>
      )}
      {items.length > 0 && (
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={140}>Role</Table.Th>
              <Table.Th>Person</Table.Th>
              <Table.Th w={120}>Designation</Table.Th>
              <Table.Th w={100}>Share %</Table.Th>
              <Table.Th w={140}>Designated</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>
                  <Anchor component={Link}
                    to={`/insurance-policy-parties/${p.id}?return_to=${returnTo}`} size="sm" style={{ cursor: 'pointer' }}>
                    <Badge variant="light" color={POLICY_PARTY_ROLE_COLOURS[p.role]}
                      styles={{ root: { cursor: 'pointer' } }}>
                      {PARTY_ROLE_LABELS[p.role] ?? p.role}
                    </Badge>
                  </Anchor>
                </Table.Td>
                {/* Task #231 (2026-05-28): server-resolved person_display_name
                    — same fix as the Parties tab above, eliminates the
                    per-row /persons/<id> cascade. */}
                <Table.Td>
                  {p.person_display_name ? (
                    <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                      {p.person_display_name}
                    </Anchor>
                  ) : (
                    <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {p.designation ? <Badge variant="light">{p.designation}</Badge> : <Text c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td>{p.percentage_share ?? '—'}</Table.Td>
                <Table.Td>{p.designation_date ?? '—'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function InsuranceSection({
  agreementId,
  canAddPolicy,
  editMode,
  pendingOps,
  failedKeys,
  onStageOp,
  onUnstageOp,
}: {
  agreementId: string;
  /** #274 (2026-06-08): only Active Agreements can host a new Policy (the
   *  schema CHECK constraint forbids attaching to Inactive/Closed
   *  Agreements). Session A4 (§3h / C-110): the button is now greyed with
   *  a reason instead of hidden. */
  canAddPolicy: boolean;
  /** C-110 gate direction: the trigger is enabled in Edit mode only. */
  editMode: boolean;
  /** #21 / #19-B (2026-06-10, Pedro's decision): ALL policy operations
   *  stage on the page's Edit transaction — attach/move (incoming) render
   *  as orange phantom rows; detach/terminate (on listed policies) tint
   *  the existing row orange with the staged action badged. Save commits
   *  the batch; the former onPatternBCommit exit-Edit workaround (C-111)
   *  is obsolete on this section. */
  pendingOps: readonly PendingPolicyOp[];
  failedKeys: ReadonlySet<string>;
  onStageOp: (op: PendingPolicyOp) => void;
  onUnstageOp: (policy_id: string) => void;
}) {
  const insuranceQ = useAccountInsuranceList({ agreement_id: agreementId, limit: 200 });
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=insurance`);
  const [addOpen, setAddOpen] = React.useState(false);

  // #21: O(1) staged-op lookup per listed policy (detach/terminate tint
  // the existing row) + the incoming ops (attach/move) that render as
  // orange phantom rows because the policy isn't in this list yet.
  const opByPolicyId = React.useMemo(() => {
    const m = new Map<string, PendingPolicyOp>();
    for (const op of pendingOps) m.set(op.policy_id, op);
    return m;
  }, [pendingOps]);
  const incomingOps = React.useMemo(
    () => pendingOps.filter((op) => op.kind === 'attach' || op.kind === 'move'),
    [pendingOps],
  );

  // Pedro's app-wide visibility rule, attach flavour (2026-06-10, smoke
  // test 16): an RM-FILED attach/move is an UPDATE CR on a limbo policy —
  // the policy has no row in this agreement's list yet, so without a
  // phantom the filing is invisible here. Fetch pending insurance_policies
  // UPDATEs whose payload targets this agreement and render blue rows.
  const filedPolicyAttachQ = useQuery({
    queryKey: ['change-requests', 'pending-policy-attaches', agreementId],
    queryFn: () => changeRequestsApi.list(
      { status: 'PENDING', entity_type: 'insurance_policies' }, 200,
    ),
  });
  const filedIncoming = React.useMemo(
    () => (filedPolicyAttachQ.data?.items ?? []).filter((cr) => {
      if (cr.kind !== 'UPDATE' || !cr.entity_id) return false;
      const p = cr.payload as { agreement_id?: string | null };
      return p.agreement_id === agreementId;
    }),
    [filedPolicyAttachQ.data, agreementId],
  );
  const stagedOpLabel = (op: PendingPolicyOp): string =>
    op.kind === 'attach' ? 'Attach'
      : op.kind === 'move' ? 'Move here'
      : op.kind === 'detach' ? 'Detach'
      : `Terminate (${op.reason === 'REDEEMED' ? 'Redeem' : op.reason === 'LAPSED' ? 'Lapse' : 'Mature'})`;
  // §3j.4 (2026-06-10): terse variant for the in-column badge — the full
  // label truncated into "PENDING SAVE · …" in the Status column. Short
  // badge, full wording in tooltips/toasts.
  const stagedOpShort = (op: PendingPolicyOp): string =>
    op.kind === 'attach' ? 'Attach'
      : op.kind === 'move' ? 'Move'
      : op.kind === 'detach' ? 'Detach'
      : 'Terminate';
  // #275 (2026-06-08): when set, render DetachPolicyModal for this Policy.
  // null = modal closed.
  const [detachTarget, setDetachTarget] = React.useState<AccountInsurance | null>(null);
  // #322 (2026-06-08): when set, render TerminatePolicyModal for this Policy
  // with the chosen reason. null = modal closed.
  const [terminateTarget, setTerminateTarget] = React.useState<
    { policy: AccountInsurance; reason: UserCloseReason } | null
  >(null);

  return (
    <Fieldset
      legend={
        <Group gap="xs">
          <IconShieldCheck size={16} />
          <span>Insurance policies on this agreement</span>
        </Group>
      }
    >
      <Stack>
        {/* Pedro (2026-06-10, smoke test 16 note): button right-justified
            ABOVE the table — matches every other tab's add-affordance
            placement (§3h). Was inline in the Fieldset legend.
            Session A4 (§3h / C-110): greyed-with-reason replaces hidden.
            Priority of reasons: non-Active Agreement first (no amount of
            Edit-mode entering helps), then Read mode. */}
        <Group justify="flex-end">
          <DisabledTooltipButton
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            disabledReason={
              !canAddPolicy
                ? 'Reactivate this Agreement to add Policies'
                : !editMode
                  ? 'Enter Edit mode to add an Insurance Policy'
                  : null
            }
            onClick={() => setAddOpen(true)}
          >
            Add Insurance Policy
          </DisabledTooltipButton>
        </Group>
        {insuranceQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!insuranceQ.isLoading
          && (insuranceQ.data?.items.length ?? 0) === 0
          && incomingOps.length === 0 && filedIncoming.length === 0 && (
          <Text c="dimmed" size="sm">
            No insurance policies linked to this agreement yet.{' '}
            {canAddPolicy
              ? 'Use the Add Insurance Policy button above to create a new one or attach an existing limbo Policy.'
              : 'Reactivate this Agreement to add Policies.'}
          </Text>
        )}
        <AddInsurancePolicyModal
          opened={addOpen}
          onClose={() => setAddOpen(false)}
          agreementId={agreementId}
          // #21: attach + move STAGE on the Edit transaction now.
          onStageAttach={(policy_id, label) =>
            onStageOp({ kind: 'attach', policy_id, label })}
          onStageMove={(policy_id, plan, label, sourceLabel) =>
            onStageOp({ kind: 'move', policy_id, plan, label, sourceLabel })}
        />
        {((insuranceQ.data?.items.length ?? 0) > 0 || incomingOps.length > 0
          || filedIncoming.length > 0) && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Insurer</Table.Th>
                <Table.Th>Policy #</Table.Th>
                <Table.Th>Accounts</Table.Th>
                <Table.Th>Fund #</Table.Th>
                {/* §3j.4 (2026-06-10, Pedro's smoke): 120px clipped the
                    "Pending · Move" / "Pending Save · …" badges into
                    noise — give status the room its content needs. */}
                <Table.Th w={190}>Status</Table.Th>
                {/* #275 (2026-06-08): per-row Detach affordance. Hidden
                    when the Agreement isn't Active (no point detaching
                    Policies from a frozen Agreement). */}
                {canAddPolicy && <Table.Th w={90}>Actions</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {insuranceQ.data?.items.map((p) => {
                // #21: detach/terminate staged on this listed policy tints
                // the row orange and swaps its actions for Unstage.
                const stagedOp = opByPolicyId.get(p.id);
                const stagedFailed = failedKeys.has(p.id);
                return (
                <Table.Tr key={p.id}
                  style={stagedOp
                    ? { backgroundColor: 'var(--mantine-color-orange-0)' }
                    : undefined}>
                  <Table.Td>
                    <Anchor component={Link} to={`/account-insurance/${p.id}?return_to=${returnTo}`} size="sm" fw={500}>
                      {p.insurer}
                    </Anchor>
                  </Table.Td>
                  <Table.Td><Text ff="monospace" size="sm">{p.policy_number}</Text></Table.Td>
                  <Table.Td>
                    {/* H1.6: Policy ↔ Account is now M:N. Render every linked
                        account so the operator sees all accounts the policy
                        wraps under this agreement. */}
                    {p.account_ids.length === 0
                      ? <Text c="dimmed" size="sm">—</Text>
                      : <Stack gap={2}>
                          {p.account_ids.map((aid) => (
                            <AccountRef key={aid} id={aid} linkPathPrefix="/accounts" />
                          ))}
                        </Stack>}
                  </Table.Td>
                  <Table.Td><Text ff="monospace" size="sm">{p.fund_number ?? '-'}</Text></Table.Td>
                  <Table.Td>
                    {/* Surface every lifecycle state the Policy can sit in.
                        Lifecycle redesign Phase 2 (links permanent, migration
                        0047): an Inactive or terminated-Closed policy now KEEPS
                        its agreement_id, so those rows DO appear in this list —
                        each is tagged Active / Inactive / Closed (+ reason) so a
                        non-live policy is never mistaken for an active one. */}
                    <StatusExplained entity="policy" status={p.deleted_at ? 'Deleted' : p.status}>
                    {p.deleted_at ? (
                      <Badge color="gray" variant="light">Deleted</Badge>
                    ) : p.status === 'Closed' ? (
                      <Group gap={4} wrap="nowrap">
                        <Badge color="red" variant="light">Closed</Badge>
                        {p.close_reason && (
                          <Badge color="red" variant="outline" size="sm">
                            {CLOSE_REASON_LABELS[p.close_reason]}
                          </Badge>
                        )}
                      </Group>
                    ) : p.status === 'Inactive' ? (
                      <Badge color="yellow" variant="light">Inactive</Badge>
                    ) : (
                      <Badge color="green" variant="light">Active</Badge>
                    )}
                    </StatusExplained>
                    {/* #26 (2026-06-10): filed-awaiting-approval CRs against
                        this policy (detach/terminate/move plans) — Task 4
                        badge, same as party rows. Own line so the op label
                        never truncates beside the status badge (§3j.4). */}
                    {(p.pending_change_requests?.length ?? 0) > 0 && (
                      <Group mt={4} wrap="nowrap">
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                    )}
                    {/* #21: staged-operation marker on the listed row. */}
                    {stagedOp && (
                      <Group gap={4} mt={4} wrap="nowrap">
                        {/* §3j.4: short badge, full wording in the tooltip. */}
                        <Tooltip label={`Staged: ${stagedOpLabel(stagedOp)} — commits when you click Save Changes`} withArrow>
                          <Badge color="orange" variant="light" size="xs">
                            Pending Save · {stagedOpShort(stagedOp)}
                          </Badge>
                        </Tooltip>
                        {stagedFailed && (
                          <Tooltip label="This staged operation failed on the last Save — see the alert for the reason; fix the cause or unstage, then Save again." withArrow>
                            <Badge color="red" variant="light" size="xs">Failed</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  {canAddPolicy && (
                    <Table.Td>
                      {/* #21: a staged op replaces the action buttons with
                          the row-undo affordance — one pending op per policy.
                          C-117 (2026-06-10): row-scoped staged actions are
                          icons + tooltip; the undo-arrow is THE row undo
                          shape app-wide. */}
                      {stagedOp ? (
                        <Tooltip label="Undo staged operation" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="gray"
                            onClick={() => onUnstageOp(p.id)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                      <>
                      {/* #275 (2026-06-08): Detach is only meaningful for
                          Active, non-deleted Policies (the only ones that
                          can appear in this list when filtered by
                          agreement_id — but defensive guard anyway).
                          #322 (2026-06-08): same gate for Terminate. */}
                      {p.status === 'Active' && !p.deleted_at && (
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label="Detach this policy (on Save)" withArrow>
                            <ActionIcon size="sm" variant="subtle" color="yellow"
                              onClick={() => setDetachTarget(p)}>
                              <IconUnlink size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Menu shadow="md" position="bottom-end">
                            <Menu.Target>
                              <Tooltip label="Terminate this policy (on Save)" withArrow>
                                <ActionIcon size="sm" variant="subtle" color="red">
                                  <IconCircleX size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Label>Terminal — cannot be reversed</Menu.Label>
                              <Menu.Item onClick={() => setTerminateTarget({ policy: p, reason: 'REDEEMED' })}>
                                Redeem
                                <Text size="xs" c="dimmed">surrendered for cash value</Text>
                              </Menu.Item>
                              <Menu.Item onClick={() => setTerminateTarget({ policy: p, reason: 'LAPSED' })}>
                                Lapse
                                <Text size="xs" c="dimmed">non-payment of premiums</Text>
                              </Menu.Item>
                              <Menu.Item onClick={() => setTerminateTarget({ policy: p, reason: 'MATURED' })}>
                                Mature
                                <Text size="xs" c="dimmed">reached end of term, paid out</Text>
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      )}
                      </>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
                );
              })}
              {/* #21: incoming staged ops (attach existing / move here) —
                  the policy isn't in this agreement's list yet, so it
                  renders as an orange phantom row until Save commits. */}
              {incomingOps.map((op) => (
                <Table.Tr key={op.policy_id}
                  style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                  <Table.Td colSpan={2}>
                    <Anchor component={Link}
                      to={`/account-insurance/${op.policy_id}?return_to=${returnTo}`}
                      size="sm" fw={500}>
                      {op.label ?? `${op.policy_id.slice(0, 8)}…`}
                    </Anchor>
                    {op.kind === 'move' && op.sourceLabel && (
                      <Text size="xs" c="dimmed">moving from {op.sourceLabel}</Text>
                    )}
                  </Table.Td>
                  <Table.Td><Text c="dimmed" size="sm">—</Text></Table.Td>
                  <Table.Td><Text c="dimmed" size="sm">—</Text></Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Badge color="orange" variant="light" size="xs">
                        Pending Save · {op.kind === 'attach' ? 'Attach' : 'Move'}
                      </Badge>
                      {failedKeys.has(op.policy_id) && (
                        <Tooltip label="This staged operation failed on the last Save — see the alert for the reason (a move plan may have gone stale); unstage and re-pick, then Save again." withArrow>
                          <Badge color="red" variant="light" size="xs">Failed</Badge>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  {canAddPolicy && (
                    <Table.Td>
                      <Tooltip label="Unstage" withArrow>
                        <ActionIcon size="sm" variant="subtle" color="gray"
                          onClick={() => onUnstageOp(op.policy_id)}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
              {/* Smoke test 16 (2026-06-10): FILED incoming attaches/moves —
                  blue phantom rows (filed ≠ staged: blue vs orange), badge
                  links to the change request. */}
              {filedIncoming.map((cr) => (
                <Table.Tr key={cr.id}
                  style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                  <Table.Td colSpan={2}>
                    <Anchor component={Link}
                      to={`/account-insurance/${cr.entity_id}?return_to=${returnTo}`}
                      size="sm" fw={500}>
                      <EntityFriendlyLabel
                        entityType="insurance_policies"
                        entityId={cr.entity_id!}
                      />
                    </Anchor>
                  </Table.Td>
                  <Table.Td><Text c="dimmed" size="sm">—</Text></Table.Td>
                  <Table.Td><Text c="dimmed" size="sm">—</Text></Table.Td>
                  <Table.Td>
                    <Tooltip
                      label="Filed for approval — Compliance must approve before the policy attaches to this agreement. Click to open the change request."
                      withArrow>
                      <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                        {/* Short form fits the column without truncation
                            (Pedro's smoke test 13); tooltip elaborates. */}
                        <Badge color="blue" variant="light" size="xs"
                          styles={{ root: { cursor: 'pointer' } }}>
                          Pending · Attach
                        </Badge>
                      </Anchor>
                    </Tooltip>
                  </Table.Td>
                  {canAddPolicy && <Table.Td />}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {detachTarget && (
          <DetachPolicyModal
            opened={!!detachTarget}
            onClose={() => setDetachTarget(null)}
            policy={detachTarget}
            // #21: stage instead of immediate-commit (modal's A2 dual mode).
            onStage={() => {
              onStageOp({
                kind: 'detach',
                policy_id: detachTarget.id,
                label: `${detachTarget.insurer} · ${detachTarget.policy_number}`,
              });
              setDetachTarget(null);
            }}
          />
        )}
        {terminateTarget && (
          <TerminatePolicyModal
            opened={!!terminateTarget}
            onClose={() => setTerminateTarget(null)}
            policy={terminateTarget.policy}
            reason={terminateTarget.reason}
            // #21: stage instead of immediate-commit (modal's A2 dual mode).
            onStage={(reason) => {
              onStageOp({
                kind: 'terminate',
                policy_id: terminateTarget.policy.id,
                reason,
                label: `${terminateTarget.policy.insurer} · ${terminateTarget.policy.policy_number}`,
              });
              setTerminateTarget(null);
            }}
          />
        )}
      </Stack>
    </Fieldset>
  );
}

interface AccountsSectionProps {
  agreementId: string;
  /** Whether the parent Agreement page is in Edit mode. All write
   *  affordances — Detach AND "+ Add account" — are enabled in Edit
   *  mode and greyed-with-reason in Read mode (§3g + §3h / C-110,
   *  Session A4). */
  editMode: boolean;
  pendingOps: readonly PendingAccountOp[];
  failedKeys: ReadonlySet<string>;
  onStageDetach: (account_id: UUID) => void;
  onStageAttach: (account_id: UUID) => void;
  onUnstageOp: (account_id: UUID) => void;
}

function AccountsSection({
  agreementId, editMode, pendingOps, failedKeys,
  onStageDetach, onStageAttach, onUnstageOp,
}: AccountsSectionProps) {
  const accountsQ = useAccounts({ agreement_id: agreementId, limit: 200 });
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=accounts`);

  // #377 A1.d (2026-06-09): O(1) lookup of "is this row pending detach?"
  // gap-1 (2026-06-16): split detach vs attach — detach marks an EXISTING
  // row; attach renders a NEW pending row (the orphan isn't in this
  // agreement's account list yet).
  const pendingDetachIds = React.useMemo(
    () => new Set(pendingOps.filter((op) => op.kind === 'detach').map((op) => op.account_id)),
    [pendingOps],
  );
  const pendingAttachOps = React.useMemo(
    () => pendingOps.filter((op) => op.kind === 'attach'),
    [pendingOps],
  );
  // gap-1 (2026-06-16, Phase 4b / C-167): single "+ Add account" entry point
  // (mirrors the Insurance tab's "+ Add Insurance Policy") opening a chooser →
  // Create new (navigate) OR Attach existing orphan (Pattern A, stages on
  // Save). Replaces the earlier two-button shape per Pedro's §3h consistency
  // catch — the two "+ Add X" flows are now identical.
  const [addOpen, setAddOpen] = React.useState(false);

  return (
    <Fieldset legend={<Group gap="xs"><IconBuildingBank size={16} /><span>Accounts on this agreement</span></Group>}>
      <Stack>
        <Group justify="flex-end">
          {/* §3h / C-110: enabled in Edit, greyed-with-reason in Read. The
              chooser modal owns Create-new (navigate) vs Attach-existing-orphan
              (staged) — identical UX to AddInsurancePolicyModal. */}
          <DisabledTooltipButton
            size="xs" leftSection={<IconPlus size={14} />} variant="light"
            disabledReason={editMode ? null : 'Enter Edit mode to add an Account'}
            onClick={() => setAddOpen(true)}>
            Add account
          </DisabledTooltipButton>
        </Group>
        <AddAccountModal
          opened={addOpen}
          onClose={() => setAddOpen(false)}
          agreementId={agreementId}
          onStageAttach={onStageAttach}
        />
        {accountsQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!accountsQ.isLoading && (accountsQ.data?.items.length ?? 0) === 0 && pendingAttachOps.length === 0 && (
          <Text c="dimmed" size="sm">No accounts yet - click "Add account" to create one or attach an existing orphan account.</Text>
        )}
        {((accountsQ.data?.items.length ?? 0) > 0 || pendingAttachOps.length > 0) && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Bank</Table.Th>
                <Table.Th>Internal #</Table.Th>
                <Table.Th>IBAN</Table.Th>
                <Table.Th w={100}>RTO</Table.Th>
                <Table.Th w={180}>Status</Table.Th>
                <Table.Th w={130}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {accountsQ.data?.items.map((a) => {
                const pendingDetach = pendingDetachIds.has(a.id);
                const failed = failedKeys.has(a.id);
                // Detach is offered for Direct Accounts only (this
                // section's list is filtered by agreement_id, so all
                // rows are Direct by definition). Status-gated like
                // the Insurance Policy detach reference: Active-only.
                // Deleted, Closed, and Inactive rows don't get a
                // Detach button (use Restore / re-attach flows
                // instead — outside A1 scope).
                let disabledReason: string | null = null;
                if (!editMode) disabledReason = 'Enter Edit mode to detach';
                else if (a.deleted_at) disabledReason = 'Deleted accounts cannot be detached';
                else if (a.status === 'Closed') disabledReason = 'Closed accounts cannot be detached';
                else if (a.status === 'Inactive') disabledReason = 'Inactive accounts cannot be detached';

                return (
                  <Table.Tr
                    key={a.id}
                    // #5 (2026-06-16): opacity dims the row; line-through on the
                    // account identity cells only, status badges stay legible.
                    style={pendingDetach ? { opacity: 0.6 } : undefined}
                  >
                    <Table.Td style={pendingDetach ? { textDecoration: 'line-through' } : undefined}>
                      <Anchor component={Link} to={`/accounts/${a.id}?return_to=${returnTo}`} size="sm" fw={500}>
                        {a.bank_name}
                      </Anchor>
                    </Table.Td>
                    <Table.Td style={pendingDetach ? { textDecoration: 'line-through' } : undefined}><Text ff="monospace" size="sm">{a.internal_num}</Text></Table.Td>
                    <Table.Td style={pendingDetach ? { textDecoration: 'line-through' } : undefined}><Text ff="monospace" size="sm">{a.iban ?? '-'}</Text></Table.Td>
                    <Table.Td>
                      {a.rto_approved
                        ? <Badge color="blue" variant="light">RTO</Badge>
                        : <Text c="dimmed">-</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="account" status={a.deleted_at ? 'Deleted' : a.status}>
                        {a.deleted_at ? (
                          <Badge color="gray" variant="light">Deleted</Badge>
                        ) : a.status === 'Closed' ? (
                          <Badge color="red" variant="light">Closed</Badge>
                        ) : a.status === 'Inactive' ? (
                          <Badge color="yellow" variant="light">Inactive</Badge>
                        ) : (
                          <Badge color="green" variant="light">Active</Badge>
                        )}
                        </StatusExplained>
                        {pendingDetach && <Badge color="orange" variant="light" size="xs">Pending detach</Badge>}
                        {failed && <Badge color="red" variant="light" size="xs">Failed</Badge>}
                        <PendingCRBadge crs={a.pending_change_requests} />
                      </Group>
                    </Table.Td>
                    {/* #377 A1.d (2026-06-09): per-row Detach with
                        Pattern A staging. Pending rows show only an
                        Undo affordance. Mirrors the Insurance Policy
                        detach button styling (Button + IconUnlink +
                        yellow + xs label) and the PartiesSection
                        staging behaviour (stage→Save→commit). The
                        DisabledTooltipButton handles the §3h
                        greyed-with-tooltip pattern for ineligible
                        states (read mode, Closed, Inactive, Deleted). */}
                    <Table.Td>
                      {/* #8 (2026-06-16): cut-a-tie = broken-link ActionIcon
                          (matches the Parties rows), undo-arrow when staged.
                          Ineligible states (read mode / Closed / Inactive /
                          Deleted) show a disabled icon with the reason. */}
                      {pendingDetach ? (
                        <Tooltip label="Undo staged detach" withArrow position="left">
                          <ActionIcon size="sm" variant="subtle" color="gray"
                            onClick={() => onUnstageOp(a.id)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : disabledReason ? (
                        <Tooltip label={disabledReason} withArrow position="left">
                          <span style={{ display: 'inline-block' }}>
                            <ActionIcon size="sm" variant="subtle" color="red" disabled>
                              <IconUnlink size={14} />
                            </ActionIcon>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Detach this account (on Save)" withArrow position="left">
                          <ActionIcon size="sm" variant="subtle" color="red"
                            onClick={() => onStageDetach(a.id)}>
                            <IconUnlink size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {/* gap-1 (2026-06-16): staged orphan-account attaches render as
                  NEW pending rows (orange) — the orphan isn't in this
                  agreement's list until Save commits. */}
              {pendingAttachOps.map((op) => (
                <StagedAttachAccountRow
                  key={op.account_id}
                  accountId={op.account_id}
                  onUnstage={() => onUnstageOp(op.account_id)}
                />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Fieldset>
  );
}

// gap-1 (2026-06-16, Phase 4b): a staged orphan-account attach renders as a
// pending row before Save. The orphan isn't in this agreement's account list
// yet, so fetch its display fields by id (mirrors the per-row useAccount
// pattern used by the Indirect-Accounts section below).
function StagedAttachAccountRow({
  accountId, onUnstage,
}: { accountId: UUID; onUnstage: () => void }) {
  const q = useAccount(accountId);
  const a = q.data;
  return (
    <Table.Tr style={{ background: 'var(--mantine-color-orange-0)' }}>
      <Table.Td><Text size="sm" fw={500}>{a ? a.bank_name : '…'}</Text></Table.Td>
      <Table.Td><Text ff="monospace" size="sm">{a?.internal_num ?? ''}</Text></Table.Td>
      <Table.Td><Text ff="monospace" size="sm">{a?.iban ?? '-'}</Text></Table.Td>
      <Table.Td><Text c="dimmed">-</Text></Table.Td>
      <Table.Td><Badge color="orange" variant="light" size="xs">Pending attach</Badge></Table.Td>
      <Table.Td>
        <Tooltip label="Undo staged attach" withArrow position="left">
          <Button
            size="xs" variant="subtle" color="gray"
            leftSection={<IconArrowBackUp size={14} />}
            onClick={onUnstage}
          >
            Undo
          </Button>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}

function addOptional<T extends object>(payload: T, values: FormValues): void {
  const optional: (keyof FormValues)[] = [
    // Beta #4 (2026-05-26): client_type was previously omitted from the
    // PATCH payload - editable on screen but silently dropped on save.
    'client_type',
    // Bug fix (2026-06-10, Pedro's #26 smoke — same Beta-#4 class):
    // mnemonic (edited via the suggest modal) and client number were
    // ALSO silently dropped on save; backend AgreementUpdate now accepts
    // both.
    'client_mnemonic', 'client_number',
    'mifid_investor_nature', 'mifid_residence', 'preferred_language', 'preferred_ref_currency',
    'mifir_id', 'mifir_id_type',
    'kyc_last_date', 'kyc_next_date',
    'mifid_suitability_score', 'mifid_suitability_last_date', 'mifid_suitability_next_date',
    'strategic_allocation', 'esg_score',
    'aml_risk', 'aml_last_date', 'aml_next_date',
    // Beta #3: KYC contact snapshot fields.
    'kyc_contact_person_id', 'kyc_contact_email',
    'kyc_contact_phone_country_code', 'kyc_contact_phone_num',
    'kyc_contact_address',
    // P15 (migration 0099): client origin.
    'client_origin', 'client_origin_referrer_id', 'client_origin_other',
  ];
  const NULLABLE_ON_CLEAR = new Set<keyof FormValues>([
    'aml_next_date', 'mifid_suitability_next_date', 'kyc_next_date',
  ]);
  for (const k of optional) {
    const v = values[k];
    if (v !== '' && v !== undefined && v !== null) {
      // Beta #3: phone_country_code is an int on the server; the form
      // stores it as a string. Coerce here.
      if (k === 'kyc_contact_phone_country_code') {
        const n = parseInt(v as string, 10);
        if (!Number.isNaN(n)) (payload as Record<string, unknown>)[k] = n;
        continue;
      }
      // 2026-06-10: client_number is an int on the server too.
      if (k === 'client_number') {
        const n = parseInt(String(v), 10);
        if (!Number.isNaN(n)) (payload as Record<string, unknown>)[k] = n;
        continue;
      }
      (payload as Record<string, unknown>)[k] = v;
    } else if ((v === '' || v === null) && NULLABLE_ON_CLEAR.has(k)) {
      (payload as Record<string, unknown>)[k] = null;
    }
  }
}

/**
 * Phase A3.g (2026-06-09): "Indirect Accounts via Insurance Policies" section
 * on the Agreement detail's Accounts tab. Surfaces the second half of the
 * dual-link picture: Accounts that belong to this Agreement only THROUGH a
 * Policy (account.opened_by_policy_id set; account.agreement_id null).
 *
 * Grouped by Policy. Each group is headed by a clickable Policy reference
 * (Insurer — Policy#) and lists the Indirect Accounts under that Policy.
 * Policies with zero Indirect accounts are skipped. If no policies on this
 * agreement carry any Indirect accounts, the entire section shows an empty
 * dimmed message instead of an empty Fieldset.
 *
 * Read-only. Detach for Indirect accounts happens on the Policy detail page
 * or the Account detail page — not here, because those are the entities
 * that own the relationship.
 */
function IndirectAccountsViaPoliciesSection({ agreementId }: { agreementId: string }) {
  // All Active policies attached to this Agreement. Closed/Inactive policies
  // are excluded because their account_ids are by definition empty (the
  // termination cascade detaches them). include_deleted stays false so
  // tombstoned policies don't leak into the read-only view.
  const policiesQ = useAccountInsuranceList({
    agreement_id: agreementId,
    status: ['Active'],
    limit: 200,
  });

  const policies = policiesQ.data?.items ?? [];
  const policiesWithAccounts = policies.filter((p) => p.account_ids.length > 0);
  const returnTo = encodeURIComponent(`/agreements/${agreementId}?tab=accounts`);

  return (
    <Fieldset legend={
      <Group gap="xs">
        <IconShieldCheck size={16} />
        <span>Indirect Accounts via Insurance Policies</span>
      </Group>
    }>
      <Stack>
        {policiesQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!policiesQ.isLoading && policiesWithAccounts.length === 0 && (
          <Text c="dimmed" size="sm">
            No indirect accounts. (Indirect accounts appear here when an
            Insurance Policy attached to this Agreement holds one or more
            Accounts.)
          </Text>
        )}
        {policiesWithAccounts.map((policy) => (
          <Fieldset
            key={policy.id}
            legend={
              <Anchor component={Link} to={`/account-insurance/${policy.id}`} size="sm" fw={500}>
                {policy.insurer} — {policy.policy_number}
              </Anchor>
            }
          >
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Bank</Table.Th>
                  <Table.Th>Internal #</Table.Th>
                  <Table.Th>IBAN</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {policy.account_ids.map((aid) => (
                  <IndirectAccountRow key={aid} accountId={aid} returnTo={returnTo} />
                ))}
              </Table.Tbody>
            </Table>
          </Fieldset>
        ))}
      </Stack>
    </Fieldset>
  );
}

/**
 * Phase A3.g (2026-06-09): one row of the Indirect Accounts table inside
 * IndirectAccountsViaPoliciesSection. Each row fetches the Account on its
 * own — react-query dedupes and caches within the page lifetime, so even
 * if the same account is referenced by multiple policies the network cost
 * is one fetch per Account. The bank-name link navigates to the Account
 * detail with a return path back to this tab.
 */
function IndirectAccountRow({ accountId, returnTo }: { accountId: string; returnTo: string }) {
  const accountQ = useAccount(accountId);
  if (accountQ.isLoading || !accountQ.data) {
    return (
      <Table.Tr>
        <Table.Td colSpan={3}><Text c="dimmed" size="xs">Loading...</Text></Table.Td>
      </Table.Tr>
    );
  }
  const a = accountQ.data;
  return (
    <Table.Tr>
      <Table.Td>
        <Anchor component={Link} to={`/accounts/${a.id}?return_to=${returnTo}`} size="sm" fw={500}>
          {a.bank_name}
        </Anchor>
      </Table.Td>
      <Table.Td><Text ff="monospace" size="sm">{a.internal_num ?? '—'}</Text></Table.Td>
      <Table.Td><Text ff="monospace" size="sm">{a.iban ?? '—'}</Text></Table.Td>
    </Table.Tr>
  );
}
